<template>
  <div class="contents">
  <Transition name="fade">
    <Dialog
      v-if="priceBreakdownModal"
      :customTitle="true"
      :title="`Order #${dialogOrderNumberDisplay} — Price Breakdown`"
      :showCloseButton="true"
      :showFooter="can('update-price-breakdown')"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :lg="true"
      :md="false"
      :sm="false"
      customWidthClass="!w-[min(112rem,99.5vw)] !max-w-[99.5vw]"
      :loading="loading"
      @closeEvent="closeEvent"
    >
      <template #title>
        <div class="flex min-w-0 flex-col gap-0.5 pr-2">
          <h4 class="text-lg font-semibold leading-tight text-black">
            <span>Order #{{ dialogOrderNumberDisplay }}</span>
            <span class="font-semibold"> · Price Breakdown</span>
          </h4>
          <p
            v-if="billingMerchantDisplayName"
            class="text-sm font-medium leading-snug text-gray-800"
          >
            {{ billingMerchantDisplayName }}
          </p>
        </div>
      </template>
      <template #header-right>
        <div class="flex items-center gap-2 ml-auto mr-5">
          <!-- Hidden for now: Stripe invoice status badge, invoice number link, edit/save invoice no -->
          <div v-if="showHeaderInvoiceUi" class="flex items-center gap-2">
            <template v-if="statusObjects.length > 0">
              <span class="rounded-full p-2" :class="statusObjects[0].color">
                <component
                  :is="statusObjects[0].icon"
                  class="w-6 h-6"
                  :stroke-width="3"
                />
              </span>
              <span class="flex flex-col mr-12">
                <a

                  v-if="headerLatestOrderItemInvoice && headerLatestOrderItemInvoice.status !== 'manual'"
                  :href="`${stripeDashboardUrl}/invoices/${headerLatestOrderItemInvoice.stripe_invoice_id || ''}`"

                  target="_blank"
                  class="capitalize mr-8 font-bold"
                >
                  {{ statusObjects[0].status ?? 'N/A' }}
                </a>
                <span v-else class="capitalize mr-8 font-bold">
                  {{ statusObjects[0].status ?? 'N/A' }}
                </span>
                <span class="font-light">{{ statusObjects[0].description }}</span>
              </span>
            </template>
           <template v-if="headerLatestOrderItemInvoice && headerLatestOrderItemInvoice.stripe_invoice_number">
              <span>Invoice No:</span>
              <span v-if="!editingInvoiceNo" class="inline-flex flex-wrap items-center gap-2">
                <a
                  v-if="primaryInvoicePdfOrHostedUrl"
                  :href="primaryInvoicePdfOrHostedUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-semibold text-[#219f84] underline decoration-[#219f84]/40 underline-offset-2 hover:text-[#1a8068]"
                  title="Open invoice PDF"
                >
                  {{ headerLatestOrderItemInvoice.stripe_invoice_number }}
                </a>
                <span v-else class="font-semibold">{{ headerLatestOrderItemInvoice.stripe_invoice_number }}</span>
                <button
                  class="ml-0 px-4 py-2 text-normal border border-[#219f84] rounded text-[#219f84] hover:bg-[#219f84] hover:text-white"
                  @click="() => startEditingInvoiceNo()"
                >
                  Edit Invoice Number
                </button>
              </span>
              <span v-else class="flex items-center gap-2">
                <input
                  v-model="invoiceNoInput"
                  type="text"
                  placeholder="Edit Invoice No"
                  class="px-2 py-2 border border-gray-300 rounded-md w-40"
                />
                <button
                  @click="saveInvoiceNumber"
                  :disabled="savingInvoice || !invoiceNoInput.trim()"
                  class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white relative"
                >

                  Save
                </button>
                <button
                  @click="editingInvoiceNo = false"
                  class="flex px-3 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-200"
                >
                  Cancel
                </button>
              </span>
            </template>
            <template v-else>
              <span>Invoice Status:</span>
              <span>Not invoiced</span>
              <span class="flex items-center gap-2">
                <input
                  v-model="invoiceNoInput"
                  type="text"
                  placeholder="Enter Invoice No"
                  class="px-2 py-2 border border-gray-300 rounded-md w-100"
                />
                <button
                  v-if="invoiceNoInput && invoiceNoInput.toString().trim().length > 0"
                  @click="saveInvoiceNumber"
                  :disabled="savingInvoice"
                  class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white relative"
                >
                  <span v-if="savingInvoice" class="inline-block w-4 h-4 border-2 border-white rounded-full border-r-transparent animate-spin mr-2"></span>
                  Save Invoice No
                </button>
              </span>
            </template>
          </div>
          <!--
            Split button: primary action is Create Invoice (unchanged behavior). Chevron toggles a
            dropdown that exposes the secondary action Create Credit Note (US 5253 / Task 16169).
            Picker + wizard navigation are handled by SelectStripeInvoiceForCreditNoteModal +
            CreateStripeCreditNoteModal — wizard UI itself ships in Task 16178.
          -->
          <div
            v-if="can('create-stripe-invoice')"
            ref="splitButtonContainerRef"
            class="relative flex flex-wrap items-center gap-2"
          >
            <div class="relative inline-flex items-stretch">
              <button
                type="button"
                class="relative flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-l-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white"
                @click="onCreateInvoiceClick"
                :disabled="stripeLoading || !hasBillableRemainingForStripePrimaryItem || hasPendingStripeSyncForPrimaryItem"
                :title="hasPendingStripeSyncForPrimaryItem
                  ? appendManualReviewReason(
                      pendingStripeSyncRequiresManualReview
                        ? 'Flagged for manual review — do not re-send until resolved.'
                        : 'A sync is already in progress for this item — verify before re-sending.',
                      pendingStripeSyncRowForPrimaryItem,
                    )
                  : undefined"
              >
                <div
                  v-if="stripeLoading"
                  class="absolute inset-0 bg-white/50 flex items-center justify-center z-[100]"
                >
                  <span
                    class="border-[4px] border-primary rounded-full w-10 h-10 animate-spin border-r-transparent"
                  ></span>
                </div>
                Create Invoice
              </button>
              <button
                type="button"
                class="-ml-px flex items-center px-2 py-2 text-[#219f84] border border-[#219f84] rounded-r-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#219f84] disabled:hover:bg-white"
                :aria-haspopup="true"
                :aria-expanded="splitButtonOpen"
                aria-label="More invoice actions"
                :disabled="stripeLoading"
                @click="splitButtonOpen = !splitButtonOpen"
              >
                <ChevronDownIcon class="h-4 w-4" />
              </button>
              <div
                v-if="splitButtonOpen"
                class="absolute right-0 top-full z-30 mt-1 min-w-[14rem] overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
                role="menu"
              >
                <button
                  type="button"
                  class="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-white"
                  :disabled="stripeLoading || !hasBillableRemainingForStripePrimaryItem || hasPendingStripeSyncForPrimaryItem"
                  :title="hasPendingStripeSyncForPrimaryItem
                    ? appendManualReviewReason(
                        pendingStripeSyncRequiresManualReview
                          ? 'Flagged for manual review — do not re-send until resolved.'
                          : 'A sync is already in progress for this item — verify before re-sending.',
                        pendingStripeSyncRowForPrimaryItem,
                      )
                    : undefined"
                  @click="onCreateInvoiceFromMenu"
                >
                  Create Invoice
                </button>
                <button
                  type="button"
                  class="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-white"
                  :disabled="creditFlowLoading || creditableInvoicesList.length === 0"
                  :title="
                    creditableInvoicesList.length === 0
                      ? 'No Stripe invoices on this order to credit'
                      : undefined
                  "
                  @click="onCreateCreditNoteClick"
                >
                  <span class="inline-flex items-center gap-2">
                    Create Credit Note
                    <span
                      v-if="creditFlowLoading"
                      class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#219f84] border-r-transparent"
                    />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #modal-content>
        <!-- Billing status + invoice links -->
        <div
          v-if="invoiceBillingStripVisible"
          class="mb-5 rounded-lg border border-gray-200 bg-slate-50/90 px-4 py-3 text-sm text-gray-800"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div class="inline-flex items-center gap-2">
                <span class="font-semibold text-gray-600">Invoice Status</span>
                <span
                  :class="
                    invoiceCompositeStatusPillClass(
                      primaryOrderItem?.invoice_composite_status,
                      { emptyAsNotInvoiced: true },
                    )
                  "
                  :title="topInvoiceStatusPillTitle"
                >
                  {{
                    primaryOrderItem?.invoice_composite_status
                      ? String(primaryOrderItem.invoice_composite_status).replace(/_/g, ' ')
                      : 'Not Invoiced'
                  }}
                </span>
                <span
                  v-if="manualInvoiceNumberForStatusStrip"
                  class="text-sm font-medium text-gray-500"
                >· {{ manualInvoiceNumberForStatusStrip }}</span>
                <!-- Bug 20222: sync in flight / awaiting reconciliation — block re-send until resolved. -->
                <span
                  v-if="hasPendingStripeSyncForPrimaryItem"
                  class="inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium leading-tight"
                  :class="pendingStripeSyncRequiresManualReview
                    ? 'bg-red-50 text-red-800 border-red-200'
                    : 'bg-amber-50 text-amber-900 border-amber-200'"
                  :title="appendManualReviewReason(
                    pendingStripeSyncRequiresManualReview
                      ? 'The invoice may have been sent but could not be confirmed. Do NOT re-send — flagged for manual review.'
                      : 'A previous sync is still being processed or awaiting reconciliation. Verify in Stripe before re-sending.',
                    pendingStripeSyncRowForPrimaryItem,
                  )"
                >
                  {{ pendingStripeSyncRequiresManualReview ? 'Under review' : 'Invoice sync in progress — verify before re-sending' }}
                </span>
              </div>
            </div>
            <span v-if="invoiceDataDebugBadge" class="text-xs font-medium text-amber-800">
              {{ invoiceDataDebugBadge }}
            </span>
          </div>
          <div v-if="partialInvoicesList.length" class="mt-3 flex flex-wrap gap-2 border-t border-gray-200 pt-3">
            <span class="w-full text-[11px] font-semibold uppercase text-gray-500">Invoices</span>
            <span
              v-for="inv in partialInvoicesList"
              :key="'inv-' + String(inv.id ?? inv.stripe_invoice_id ?? '')"
              class="inline-flex items-center gap-1"
            >
              <!-- Invoice pill: number + an inline refresh icon (always visible) so it's
                   obvious which invoice the refresh acts on. Bug 18510 -->
              <span
                class="inline-flex max-w-[18rem] items-center gap-1 rounded-md border bg-white px-2 py-1 text-xs"
                :class="invoicePdfOrHostedUrl(inv) ? 'border-[#219f84]/30' : 'border-gray-200'"
                :title="manualReviewReasonFor(inv) || undefined"
              >
                <button
                  v-if="can('refresh-invoice')"
                  type="button"
                  :disabled="refreshingInvoiceId === inv.id"
                  @click="refreshInvoiceLink(inv)"
                  title="Refresh this invoice's Stripe link (fetches a fresh, non-expired URL)"
                  class="shrink-0 rounded p-0.5 text-[#219f84] hover:bg-[#219f84]/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span
                    v-if="refreshingInvoiceId === inv.id"
                    class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#219f84] border-r-transparent"
                  ></span>
                  <RefreshCw v-else class="h-3 w-3" />
                </button>
                <a
                  v-if="invoicePdfOrHostedUrl(inv)"
                  :href="invoicePdfOrHostedUrl(inv)"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open invoice PDF"
                  class="truncate text-[#219f84] underline decoration-[#219f84]/30 underline-offset-2"
                >
                  {{ invoiceListDisplayLabel(inv)
                  }}<span v-if="inv.status && !isPendingStripeSyncStatus(inv.status)" class="text-gray-500"> ({{ inv.status }})</span>
                </a>
                <span v-else class="truncate text-gray-600">
                  {{ invoiceListDisplayLabel(inv)
                  }}<span v-if="inv.status && !isPendingStripeSyncStatus(inv.status)" class="text-gray-500"> ({{ inv.status }})</span>
                </span>
              </span>
              <button
                v-if="canOfferRemoveInvoiceFor(inv)"
                type="button"
                title="Remove this invoice from the order"
                :disabled="loading"
                class="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-300 bg-white px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                @click="onRemoveInvoice(inv)"
              >
                Remove Invoice
              </button>
              <!-- AB#20717 round 2 — credit-note pills: one per inv.credit_notes[] entry, next to
                   the invoice pill it applies to. Purely additive; the invoice pill above is
                   unchanged. -->
              <button
                v-for="cn in creditNotesForInvoice(inv)"
                :key="'cn-' + String(cn.id ?? cn.stripe_credit_note_id ?? '')"
                type="button"
                class="inline-flex shrink-0 items-center rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
                title="Open credit note PDF"
                @click="openCreditNotePdf(cn)"
              >
                {{ creditNotePillLabel(cn) }}
              </button>
            </span>
          </div>
          <!-- MINOR 5 (AB#20223 follow-up): failed attempts are excluded from the pill list above by
               design; when that leaves no invoices at all, keep the failure reason discoverable
               without distracting from a real invoice (hidden whenever any pill renders). -->
          <div
            v-else-if="mostRecentFailedInvoiceReason"
            class="mt-3 flex flex-wrap gap-2 border-t border-gray-200 pt-3"
          >
            <span
              class="text-xs italic text-gray-400"
              :title="mostRecentFailedInvoiceReason"
            >Last attempt failed — hover for reason</span>
          </div>
        </div>
        <!-- AB#20717 — billing documents ledger: additive, collapsed-by-default section below the
             invoice chips above. Reuses the parent's refresh/remove handlers via emits so there is
             exactly one code path for both actions. -->
        <BillingDocumentsLedger
          v-if="partialInvoicesList.length"
          ref="billingLedgerRef"
          class="mb-5"
          :order-id="Number(order_ref.id ?? 0)"
          :invoices="partialInvoicesList"
          :order-items="orderItemsForView"
          :resolve-url="invoicePdfOrHostedUrl"
          :can-remove-invoice="canOfferRemoveInvoiceFor"
          :refreshing-invoice-id="refreshingInvoiceId"
          @refresh="refreshInvoiceLink"
          @remove="onRemoveInvoice"
        />
        <template
          v-for="(orderItemRef, orderItemRefIndex) in orderItemsForView"
          :key="'order-item-wrap-' + orderItemRefIndex"
        >
          <template
          v-for="(factory_product, factoryProductIndex) in orderItemRef.factory_products"
          :key="`order-item-${orderItemRefIndex}factory_product_${factoryProductIndex}`"
        >
          <div class="price-breakdown-meta-row mb-5">
            <div class="price-breakdown-meta-row__meta mt-2 flex h-max min-w-0 items-center gap-2">
              <img
                v-if="
                  getFilePreview(factory_product) &&
                  !factorySkuThumbLoadFailed[
                    factorySkuThumbKey(orderItemRefIndex, factoryProductIndex)
                  ]
                "
                :src="getFilePreview(factory_product)!"
                alt="Sku preview"
                class="img-thumbnail img-fluid w-20 border-0"
                @error="
                  factorySkuThumbLoadFailed[
                    factorySkuThumbKey(orderItemRefIndex, factoryProductIndex)
                  ] = true
                "
              />
              <div
                v-else
                class="h-20 w-20 shrink-0"
                aria-hidden="true"
              />
              <div class="flex min-w-0 flex-col">
                <input
                  v-if="factory_product.is_custom_product"
                  v-model="factory_product.new_product_name"
                  :disabled="!can('update-price-breakdown')"
                  class="px-4 py-1 mr-2 border border-gray-300 rounded-md"
                />
                <input
                  v-else
                  v-model="factory_product.product_name"
                  :disabled="!can('update-price-breakdown')"
                  class="px-4 py-1 mr-2 border border-gray-300 rounded-md"
                />
                <span class="text-black-50 text-sm">
                  SKU: {{ factory_product.sku_number
                  }}<template v-if="factory_product.sku?.sku_id != null"
                    >({{ factory_product.sku.sku_id }})</template
                  >
                </span>
                <span class="text-black-50 mt-0.5 text-sm">
                  Design: {{ factoryProductDesignLabel(factory_product) }}
                </span>
                <button
                  v-if="!factory_product.id"
                  class="mt-0 flex w-max cursor-pointer items-center gap-x-1 rounded-md border border-red-500 p-1 text-red-500 transition-all hover:bg-red-500 hover:text-white"
                  @click="removeFactoryProduct(orderItemRefIndex, factoryProductIndex)"
                >
                  <trash-icon class="size-5" />
                  Delete
                </button>
              </div>
            </div>
            <div class="price-breakdown-meta-row__tables flex min-w-0 w-full flex-col gap-0">
              <div
                class="factory-pricing-invoice-pair flex w-full min-w-0 flex-col gap-3 min-[1280px]:flex-row min-[1280px]:items-start min-[1280px]:gap-4"
              >
                <div
                  class="price-breakdown-roster-table-wrap min-w-0 min-h-0 flex-1 overflow-x-auto rounded-md border border-gray-300 bg-white min-[1280px]:max-w-[56%]"
                >
                  <div class="h-full min-h-full overflow-x-auto">
                    <table
                      class="w-full min-w-[260px] table-fixed border-collapse products-table text-sm"
                    >
                      <thead v-if="factoryProductIndex === 0">
                        <tr>
                          <th
                            class="px-4 py-2 text-left align-middle text-sm font-semibold text-gray-800"
                            scope="col"
                          >
                            Size
                          </th>
                          <th
                            class="px-4 py-2 text-center align-middle text-sm font-semibold tabular-nums text-gray-800"
                            scope="col"
                          >
                            Qty
                          </th>
                          <th
                            class="price-breakdown-col-price px-4 py-2 text-right align-middle text-sm font-semibold tabular-nums text-gray-800"
                            scope="col"
                          >
                            Price
                          </th>
                          <th
                            class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm font-semibold tabular-nums text-gray-800"
                            scope="col"
                          >
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(size, size_idx) in factory_product.prices.sizes"
                          :key="size_idx"
                          class="text-center"
                        >
                          <td class="w-[25%] px-4 py-2 text-left align-middle text-sm text-gray-800">
                            {{ size.size }}
                          </td>
                          <td class="w-[25%] px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                            {{ formatIntegerQty(size.quantity) }}
                          </td>
                          <td class="w-[25%] price-breakdown-col-price px-4 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                            <span class="inline-flex items-center justify-end"
                                ><input
                                :disabled="!can('update-price-breakdown')"
                                class="price-breakdown-price-input mr-1 w-[4.5rem] min-w-[2.5rem] max-w-[4.5rem] rounded-md border border-gray-300 p-1.5 text-xs tabular-nums outline-none"
                                :value="formatMoney(size.final_net_price ?? 0)"
                                inputmode="decimal"
                                type="text"
                                @focus="stashSizePriceBeforeEdit(size)"
                                @input="onPriceInputSanitize"
                                @change="onFactorySizeNetPriceInput($event, size)"
                              /><span class="text-[10px] font-normal text-gray-500">{{
                                orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                              }}</span></span
                            >
                          </td>
                          <td class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                            {{ formatMoney(size.final_net_price_with_quantity) }}
                            <span class="text-[10px] font-normal text-gray-500">{{
                              orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                            }}</span>
                          </td>
                        </tr>
                        <tr
                          v-if="!factory_product.prices.total_addons"
                          class="border-t-2 border-gray-300 bg-gray-50"
                        >
                          <td class="w-[25%] px-4 py-2"></td>
                          <td
                            class="w-[25%] px-4 py-2 text-center align-middle text-sm font-semibold tabular-nums text-gray-900"
                          >
                            {{ formatIntegerQty(factory_product.prices?.total_quantity) }}
                          </td>
                          <td class="w-[25%] px-4 py-2"></td>
                          <td
                            class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm font-semibold text-gray-900"
                          >
                            {{ formatMoney(factory_product.prices.factory_product_total_price) }}
                            <span class="text-[10px] font-normal text-gray-500">{{
                              orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                            }}</span>
                          </td>
                        </tr>
                        <template v-if="factory_product.prices.total_addons > 0">
                          <tr class="bg-gray-50">
                            <td
                              colspan="4"
                              class="border-b border-t-2 border-gray-300 border-gray-200 bg-gray-50 px-4 py-2 text-center align-middle text-sm font-semibold text-gray-800"
                            >
                              Addons
                            </td>
                          </tr>
                          <template v-for="(addon, addon_idx) in factory_product.prices.addons" :key="'merged-add-' + addon_idx">
                            <tr
                              class="text-center"
                              :class="!addon_idx && Number(factoryProductIndex) > 0 ? 'border-t-2 border-gray-300' : ''"
                            >
                              <td class="w-[25%] px-4 py-2 text-left align-middle text-sm text-gray-800">
                                {{ addon.addon_name }}
                              </td>
                              <td class="w-[25%] px-4 py-2 align-middle"></td>
                              <td class="w-[25%] price-breakdown-col-price px-4 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                <input
                                  :disabled="!can('update-price-breakdown') || invoiceAddonsLocked"
                                  type="text"
                                  inputmode="decimal"
                                  class="price-breakdown-price-input mr-1 w-[4.5rem] min-w-[2.5rem] max-w-[4.5rem] rounded-md border border-gray-300 p-1.5 text-xs tabular-nums outline-none"
                                  :value="formatMoney(addon.final_net_price ?? 0)"
                                  @focus="stashAddonPriceBeforeEdit(addon)"
                                  @input="onPriceInputSanitize"
                                  @change="onCatalogAddonNetPriceChange($event, addon, factory_product)"
                                /><span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </td>
                              <td class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                {{ formatMoney(addon.final_net_price_with_quantity) }}
                                <span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </td>
                            </tr>
                          </template>
                          <template
                            v-for="(ungrouped_addon, ungrouped_addon_idx) in factory_product.prices
                              .ungrouped_addons"
                            :key="'merged-ungrouped-add-' + ungrouped_addon_idx"
                          >
                            <tr class="text-center">
                              <td class="w-[25%] px-4 py-2 text-left align-middle text-sm text-gray-800">
                                {{ ungrouped_addon.addon_name }}
                              </td>
                              <td class="w-[25%] px-4 py-2 align-middle"></td>
                              <td class="w-[25%] price-breakdown-col-price px-4 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                <input
                                  :disabled="!can('update-price-breakdown') || invoiceAddonsLocked"
                                  type="text"
                                  inputmode="decimal"
                                  class="price-breakdown-price-input mr-1 w-[4.5rem] min-w-[2.5rem] max-w-[4.5rem] rounded-md border border-gray-300 p-1.5 text-xs tabular-nums outline-none"
                                  :value="formatMoney(ungrouped_addon.final_net_price ?? 0)"
                                  @focus="stashAddonPriceBeforeEdit(ungrouped_addon)"
                                  @input="onPriceInputSanitize"
                                  @change="onCatalogAddonNetPriceChange($event, ungrouped_addon, factory_product)"
                                /><span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </td>
                              <td class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                {{ formatMoney(ungrouped_addon.final_net_price_with_quantity) }}
                                <span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </td>
                            </tr>
                          </template>
                          <template
                            v-for="(grouped_addon, group_name) in factory_product.prices.grouped_addons"
                            :key="'merged-grouped-add-' + String(group_name)"
                          >
                            <tr class="text-center">
                              <td class="w-[25%] px-4 py-2 text-left align-middle text-sm text-gray-800">
                                {{ group_name }}:
                                {{ grouped_addon.addon_name }}
                              </td>
                              <td class="w-[25%] px-4 py-2 align-middle"></td>
                              <td class="w-[25%] price-breakdown-col-price px-4 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                <input
                                  :disabled="!can('update-price-breakdown') || invoiceAddonsLocked"
                                  type="text"
                                  inputmode="decimal"
                                  class="price-breakdown-price-input mr-1 w-[4.5rem] min-w-[2.5rem] max-w-[4.5rem] rounded-md border border-gray-300 p-1.5 text-xs tabular-nums outline-none"
                                  :value="formatMoney(grouped_addon.final_net_price ?? 0)"
                                  @focus="stashAddonPriceBeforeEdit(grouped_addon)"
                                  @input="onPriceInputSanitize"
                                  @change="onCatalogAddonNetPriceChange($event, grouped_addon, factory_product)"
                                /><span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </td>
                              <td class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                {{ formatMoney(grouped_addon.final_net_price_with_quantity) }}
                                <span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </td>
                            </tr>
                          </template>
                          <template
                            v-for="(logo_technology, logo_technologies_index) in factory_product.prices
                              .logo_technologies"
                            :key="'merged-logo-add-' + logo_technologies_index"
                          >
                            <tr class="text-center">
                              <td class="w-[25%] px-4 py-2 text-left align-middle text-sm text-gray-800">
                                {{ logo_technology.addon_name }}
                              </td>
                              <td class="w-[25%] px-4 py-2 align-middle"></td>
                              <td class="w-[25%] price-breakdown-col-price px-4 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                <input
                                  :disabled="!can('update-price-breakdown') || invoiceAddonsLocked"
                                  type="text"
                                  inputmode="decimal"
                                  class="price-breakdown-price-input mr-1 w-[4.5rem] min-w-[2.5rem] max-w-[4.5rem] rounded-md border border-gray-300 p-1.5 text-xs tabular-nums outline-none"
                                  :value="formatMoney(logo_technology.final_net_price ?? 0)"
                                  @focus="stashAddonPriceBeforeEdit(logo_technology)"
                                  @input="onPriceInputSanitize"
                                  @change="onCatalogAddonNetPriceChange($event, logo_technology, factory_product)"
                                /><span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </td>
                              <td class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                {{ formatMoney(logo_technology.final_net_price_with_quantity) }}
                                <span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </td>
                            </tr>
                          </template>
                          <tr class="border-t-2 border-gray-300 bg-gray-50">
                            <td class="w-[25%] px-4 py-2 align-middle"></td>
                            <td
                              class="w-[25%] px-4 py-2 text-center align-middle text-sm font-semibold tabular-nums text-gray-900"
                            >
                              {{ formatIntegerQty(factory_product.prices?.total_quantity) }}
                            </td>
                            <td class="w-[25%] px-4 py-2 align-middle"></td>
                            <td
                              class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm font-semibold text-gray-900"
                            >
                              {{ formatMoney(factory_product.prices.factory_product_total_price) }}
                              <span class="text-[10px] font-normal text-gray-500">{{
                                orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                              }}</span>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div
                  v-if="showInvoiceProgressColumns && orderItemRefIndex === 0"
                  class="min-w-0 w-full min-[1280px]:flex-1 min-[1280px]:basis-0 min-[1280px]:max-w-[44%] rounded-md border border-gray-300 bg-white"
                >
                  <div class="h-full min-h-full overflow-x-auto">
                    <table
                      class="invoice-beside-legacy invoice-beside-summary-table products-table w-full min-w-[340px] table-fixed border-collapse text-sm"
                      aria-label="Partial quantity and amount for sizes, garment-line totals align with add-on label rows; add-on rows are not per-addon allocations"
                    >
                      <colgroup>
                        <col class="w-[22%]" />
                        <col class="w-[26%]" />
                        <col class="w-[26%]" />
                        <col class="w-[26%]" />
                      </colgroup>
                      <thead v-if="factoryProductIndex === 0">
                        <tr>
                          <th
                            class="px-4 py-2 text-center align-middle text-sm font-semibold tabular-nums text-gray-800"
                            scope="col"
                          >
                            Invoiced
                          </th>
                          <th
                            class="px-4 py-2 text-center align-middle text-sm font-semibold whitespace-nowrap tabular-nums text-gray-800"
                            scope="col"
                          >
                            Remaining qty
                          </th>
                          <th
                            class="px-4 py-2 text-center align-middle text-sm font-semibold whitespace-nowrap tabular-nums text-gray-800"
                            scope="col"
                          >
                            Qty to invoice
                          </th>
                          <th
                            class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm font-semibold tabular-nums text-gray-800"
                            scope="col"
                          >
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(size, size_idx) in factory_product.prices.sizes"
                          :key="'inv-beside-' + size_idx"
                          class="text-center"
                        >
                          <td class="px-4 py-2 text-center align-middle text-sm tabular-nums text-gray-800">
                            {{
                              invoicedQtyForFactorySizeRow(
                                orderItemRef,
                                factory_product,
                                Number(factoryProductIndex),
                                Number(size_idx),
                              )
                            }}
                          </td>
                          <td class="px-4 py-2 text-center align-middle text-sm tabular-nums text-gray-800">
                            {{
                              formatIntegerQty(
                                remainingQtyForFactorySizeRow(
                                  orderItemRef,
                                  factory_product,
                                  factoryProductIndex,
                                  size_idx,
                                  size.quantity,
                                ),
                              )
                            }}
                          </td>
                          <td class="px-4 py-2 align-middle text-sm">
                            <input
                              :min="
                                factorySizePartialInvoiceBounds(
                                  orderItemRef,
                                  factory_product,
                                  factoryProductIndex,
                                  size_idx,
                                  size,
                                ).min
                              "
                              :max="
                                factorySizePartialInvoiceBounds(
                                  orderItemRef,
                                  factory_product,
                                  factoryProductIndex,
                                  size_idx,
                                  size,
                                ).max
                              "
                              :value="
                                partialQtyToInvoiceByLineKey[
                                  buildFactorySizeLineKey(
                                    orderItemRef.id,
                                    factoryProductKeyForInvoice(factory_product, factoryProductIndex, orderItemRef),
                                    size_idx,
                                  )
                                ] ?? 0
                              "
                              class="mx-auto w-full min-w-12 max-w-[5rem] rounded border border-gray-300 p-1.5 text-center text-xs outline-none"
                              inputmode="numeric"
                              type="text"
                              @input="
                                onFactorySizePartialInvoiceInput(
                                  $event,
                                  orderItemRef,
                                  factory_product,
                                  factoryProductIndex,
                                  size_idx,
                                  size,
                                )
                              "
                            />
                          </td>
                          <td class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                            {{
                              formatMoney(
                                partialToInvoiceDollarForFactorySize(
                                  orderItemRef,
                                  factory_product,
                                  factoryProductIndex,
                                  size,
                                  size_idx,
                                ),
                              )
                            }}
                            <span class="text-[10px] text-gray-500">
                              {{ orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency?.code : '' }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          v-if="(factory_product.prices?.sizes?.length ?? 0) > 0"
                          class="border-t-2 border-gray-300 bg-gray-50"
                        >
                          <td
                            class="px-4 py-2 text-center align-middle tabular-nums text-sm font-semibold text-gray-900"
                          >
                            {{
                              sumInvoicedForFactorySizeLines(
                                orderItemRef,
                                factory_product,
                                factoryProductIndex,
                              )
                            }}
                          </td>
                          <td
                            class="px-4 py-2 text-center align-middle tabular-nums text-sm font-semibold text-gray-900"
                          >
                            {{ sumRemainingForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                          </td>
                          <td
                            class="px-4 py-2 text-center align-middle tabular-nums text-sm font-semibold text-gray-900"
                          >
                            {{
                              sumPartialQtyForFactorySizeLines(
                                orderItemRef,
                                factory_product,
                                factoryProductIndex,
                              )
                            }}
                          </td>
                          <td class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm font-semibold text-gray-900">
                            {{
                              formatMoney(
                                sumPartialAmountForFactorySizeLines(
                                  orderItemRef,
                                  factory_product,
                                  factoryProductIndex,
                                ),
                              )
                            }}
                            <span class="text-[10px] font-normal text-gray-500">{{
                              orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                            }}</span>
                          </td>
                        </tr>
                        <template v-if="(factory_product.prices?.total_addons || 0) > 0">
                          <template
                            v-for="(invAdd, inv_ad_idx) in factory_product.prices.addons || []"
                            :key="'inv-ad-addon-' + inv_ad_idx"
                          >
                            <tr class="text-center text-slate-800">
                              <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                                {{ sumInvoicedForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                              </td>
                              <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                                {{ sumRemainingForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                              </td>
                              <td class="px-4 py-2 align-middle text-sm">
                                <input
                                  type="text"
                                  :value="
                                    String(
                                      sumPartialQtyForFactorySizeLines(
                                        orderItemRef,
                                        factory_product,
                                        factoryProductIndex,
                                      ) ?? 0,
                                    )
                                  "
                                  class="mx-auto w-full min-w-12 max-w-[5rem] cursor-not-allowed rounded border border-gray-300 bg-gray-50 p-1.5 text-center text-xs text-gray-800 outline-none"
                                  inputmode="numeric"
                                  disabled
                                  aria-label="Product size Qty to invoice (read only on add-on row)"
                                />
                              </td>
                              <td class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                {{
                                  formatMoney(
                                    addonDollarByPartialQtyToInvoice(
                                      invAdd,
                                      orderItemRef,
                                      factory_product,
                                      factoryProductIndex,
                                    ),
                                  )
                                }}
                                <span class="text-[10px] text-gray-500">{{
                                  orderItemNetPriceCurrency?.code
                                    ? ' ' + orderItemNetPriceCurrency.code
                                    : ''
                                }}</span>
                              </td>
                            </tr>
                          </template>
                          <template
                            v-for="(invUng, inv_ung_idx) in factory_product.prices.ungrouped_addons || []"
                            :key="'inv-ad-ung-' + inv_ung_idx"
                          >
                            <tr class="text-center text-slate-800">
                              <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                                {{ sumInvoicedForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                              </td>
                              <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                                {{ sumRemainingForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                              </td>
                              <td class="px-4 py-2 align-middle text-sm">
                                <input
                                  type="text"
                                  :value="
                                    String(
                                      sumPartialQtyForFactorySizeLines(
                                        orderItemRef,
                                        factory_product,
                                        factoryProductIndex,
                                      ) ?? 0,
                                    )
                                  "
                                  class="mx-auto w-full min-w-12 max-w-[5rem] cursor-not-allowed rounded border border-gray-300 bg-gray-50 p-1.5 text-center text-xs text-gray-800 outline-none"
                                  inputmode="numeric"
                                  disabled
                                  aria-label="Product size Qty to invoice (read only on add-on row)"
                                />
                              </td>
                              <td class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                {{
                                  formatMoney(
                                    addonDollarByPartialQtyToInvoice(
                                      invUng,
                                      orderItemRef,
                                      factory_product,
                                      factoryProductIndex,
                                    ),
                                  )
                                }}
                                <span class="text-[10px] text-gray-500">{{
                                  orderItemNetPriceCurrency?.code
                                    ? ' ' + orderItemNetPriceCurrency.code
                                    : ''
                                }}</span>
                              </td>
                            </tr>
                          </template>
                          <template
                            v-for="(invG, inv_grp) in factory_product.prices.grouped_addons || {}"
                            :key="'inv-ad-grp-' + String(inv_grp)"
                          >
                            <tr class="text-center text-slate-800">
                              <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                                {{ sumInvoicedForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                              </td>
                              <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                                {{ sumRemainingForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                              </td>
                              <td class="px-4 py-2 align-middle text-sm">
                                <input
                                  type="text"
                                  :value="
                                    String(
                                      sumPartialQtyForFactorySizeLines(
                                        orderItemRef,
                                        factory_product,
                                        factoryProductIndex,
                                      ) ?? 0,
                                    )
                                  "
                                  class="mx-auto w-full min-w-12 max-w-[5rem] cursor-not-allowed rounded border border-gray-300 bg-gray-50 p-1.5 text-center text-xs text-gray-800 outline-none"
                                  inputmode="numeric"
                                  disabled
                                  aria-label="Product size Qty to invoice (read only on add-on row)"
                                />
                              </td>
                              <td class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                {{
                                  formatMoney(
                                    addonDollarByPartialQtyToInvoice(
                                      invG,
                                      orderItemRef,
                                      factory_product,
                                      factoryProductIndex,
                                    ),
                                  )
                                }}
                                <span class="text-[10px] text-gray-500">{{
                                  orderItemNetPriceCurrency?.code
                                    ? ' ' + orderItemNetPriceCurrency.code
                                    : ''
                                }}</span>
                              </td>
                            </tr>
                          </template>
                          <template
                            v-for="(invLt, inv_lt_idx) in factory_product.prices.logo_technologies || []"
                            :key="'inv-ad-logo-' + inv_lt_idx"
                          >
                            <tr class="text-center text-slate-800">
                              <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                                {{ sumInvoicedForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                              </td>
                              <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                                {{ sumRemainingForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                              </td>
                              <td class="px-4 py-2 align-middle text-sm">
                                <input
                                  type="text"
                                  :value="
                                    String(
                                      sumPartialQtyForFactorySizeLines(
                                        orderItemRef,
                                        factory_product,
                                        factoryProductIndex,
                                      ) ?? 0,
                                    )
                                  "
                                  class="mx-auto w-full min-w-12 max-w-[5rem] cursor-not-allowed rounded border border-gray-300 bg-gray-50 p-1.5 text-center text-xs text-gray-800 outline-none"
                                  inputmode="numeric"
                                  disabled
                                  aria-label="Product size Qty to invoice (read only on add-on row)"
                                />
                              </td>
                              <td class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                                {{
                                  formatMoney(
                                    addonDollarByPartialQtyToInvoice(
                                      invLt,
                                      orderItemRef,
                                      factory_product,
                                      factoryProductIndex,
                                    ),
                                  )
                                }}
                                <span class="text-[10px] text-gray-500">{{
                                  orderItemNetPriceCurrency?.code
                                    ? ' ' + orderItemNetPriceCurrency.code
                                    : ''
                                }}</span>
                              </td>
                            </tr>
                          </template>
                          <tr
                            v-if="
                              (factory_product.prices?.sizes?.length ?? 0) > 0 ||
                              (factory_product.prices?.total_addons || 0) > 0
                            "
                            class="border-t-2 border-gray-300 bg-gray-50 text-sm font-semibold text-gray-900"
                          >
                            <td class="px-4 py-2 text-center align-middle tabular-nums">
                              {{ sumInvoicedForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                            </td>
                            <td class="px-4 py-2 text-center align-middle tabular-nums">
                              {{ sumRemainingForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) }}
                            </td>
                            <td class="px-4 py-2 text-center align-middle tabular-nums">
                              {{
                                sumPartialQtyForFactorySizeLines(
                                  orderItemRef,
                                  factory_product,
                                  factoryProductIndex,
                                )
                              }}
                            </td>
                            <td class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm font-semibold text-gray-900">
                              {{
                                formatMoney(
                                  factoryProductPartialPlusAddonListDollars(
                                    orderItemRef,
                                    factory_product,
                                    factoryProductIndex,
                                  ),
                                )
                              }}
                              <span class="text-[10px] font-normal text-gray-500">{{
                                orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                              }}</span>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Custom order lines after factory products (primary line item only). -->
        <template v-if="orderItemRefIndex === 0">
        <template v-for="(orderLineRow, orderLineIndex) in standaloneOrderLines" :key="'order-line-' + orderLineIndex">
          <div class="price-breakdown-meta-row mb-5">
            <div class="price-breakdown-meta-row__meta mt-2 flex h-max min-w-0 items-center gap-2">
              <div class="h-20 w-20 shrink-0" aria-hidden="true" />
              <div class="flex min-w-0 flex-col">
                <input
                  :disabled="!can('update-price-breakdown')"
                  class="max-w-full rounded border border-gray-300 px-4 py-1 text-sm"
                  v-model="orderLineRow.name"
                  type="text"
                  placeholder="Line name"
                  @change="calculateTotalPrice"
                />
                <div
                  v-if="can('update-price-breakdown')"
                  class="mt-1 flex flex-nowrap items-center gap-x-2"
                >
                  <div
                    class="inline-flex shrink-0 rounded-md border border-gray-200 bg-gray-100/90 p-0.5 shadow-inner"
                    role="group"
                    aria-label="Fixed or percent pricing"
                  >
                    <button
                      type="button"
                      class="min-w-[3.25rem] rounded px-1.5 py-0.5 text-[10px] font-semibold leading-tight transition-all"
                      :class="
                        orderLineUsesFixedPricing(orderLineRow)
                          ? 'bg-white text-[#1a7a66] shadow-sm ring-1 ring-gray-200/90'
                          : 'text-gray-500 hover:text-gray-800'
                      "
                      @click="setOrderLinePricingMode(orderLineIndex, 'fixed')"
                    >
                      Fixed
                    </button>
                    <button
                      type="button"
                      class="min-w-[3.75rem] rounded px-1.5 py-0.5 text-[10px] font-semibold leading-tight transition-all"
                      :class="
                        !orderLineUsesFixedPricing(orderLineRow)
                          ? 'bg-white text-amber-900 shadow-sm ring-1 ring-gray-200/90'
                          : 'text-gray-500 hover:text-gray-800'
                      "
                      @click="setOrderLinePricingMode(orderLineIndex, 'percent')"
                    >
                      Percent
                    </button>
                  </div>
                  <button
                    type="button"
                    @click="removeOrderLineAt(orderLineIndex)"
                    class="ml-auto flex shrink-0 cursor-pointer items-center gap-x-0.5 rounded-md border border-red-500 px-1.5 py-0.5 text-[11px] font-medium text-red-500 transition-all hover:bg-red-500 hover:text-white"
                  >
                    <trash-icon class="size-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
            <div class="price-breakdown-meta-row__tables flex min-w-0 w-full flex-col gap-0">
              <div
                class="factory-pricing-invoice-pair flex w-full min-w-0 flex-col gap-3 min-[1280px]:flex-row min-[1280px]:items-start min-[1280px]:gap-4"
              >
                <div
                  class="price-breakdown-roster-table-wrap min-w-0 min-h-0 flex-1 overflow-x-auto rounded-md border border-gray-300 bg-white min-[1280px]:max-w-[56%]"
                >
                  <div class="h-full min-h-full overflow-x-auto">
                    <table
                      class="w-full min-w-[260px] table-fixed border-collapse products-table text-sm"
                    >
                      <tbody>
                        <tr class="text-center">
                          <td class="w-[25%] px-4 py-2 align-middle"></td>
                          <td class="w-[25%] px-4 py-2 align-middle text-sm text-gray-800">
                            <input
                              :disabled="!can('update-price-breakdown')"
                              type="text"
                              inputmode="numeric"
                              min="1"
                              title="Quantity"
                              class="w-16 rounded border border-gray-300 p-1.5 text-center text-xs outline-none"
                              :value="String(orderLineRow.quantity ?? 1)"
                              @input="onStandaloneOrderLineQtyInput($event, orderLineIndex)"
                            />
                          </td>
                          <td class="w-[25%] price-breakdown-col-price px-4 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                            <template v-if="orderLineUsesFixedPricing(orderLineRow)">
                              <span class="inline-flex items-center justify-end"
                                >                                <input
                                  :disabled="!can('update-price-breakdown')"
                                  type="text"
                                  inputmode="decimal"
                                  class="price-breakdown-price-input mr-1 w-[4.5rem] min-w-[2.5rem] max-w-[4.5rem] rounded-md border border-gray-300 p-1.5 text-xs tabular-nums outline-none"
                                  :value="formatMoney(orderLineRow.price ?? 0)"
                                  @focus="stashStandaloneOrderLinePrice(orderLineRow)"
                                  @input="onPriceInputSanitize"
                                  @change="onStandaloneAddOrderLinePriceChange($event, orderLineIndex)"
                                /><span class="text-[10px] font-normal text-gray-500">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span></span
                              >
                            </template>
                            <template v-else>
                              <span class="inline-flex items-center justify-end">
                                <input
                                  :disabled="!can('update-price-breakdown')"
                                  type="text"
                                  inputmode="decimal"
                                  title="Percentage"
                                  class="price-breakdown-price-input mr-1 w-[4.5rem] min-w-[2.5rem] max-w-[4.5rem] rounded-md border border-gray-300 p-1.5 text-xs tabular-nums outline-none"
                                  :value="formatMoney(orderLineRow.percentage ?? 0)"
                                  @focus="stashStandaloneOrderLinePercentage(orderLineRow)"
                                  @input="onPriceInputSanitize"
                                  @change="onStandaloneAddOrderLinePercentageChange($event, orderLineIndex)"
                                /><span class="text-[10px] font-normal text-gray-500">%</span>
                              </span>
                            </template>
                          </td>
                          <td class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                            {{ formatMoney(standaloneOrderLineAmountAt(orderLineIndex)) }}
                            <span class="text-[10px] font-normal text-gray-500">{{
                              orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                            }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div
                  v-if="showInvoiceProgressColumns"
                  class="min-w-0 w-full min-[1280px]:flex-1 min-[1280px]:basis-0 min-[1280px]:max-w-[44%] rounded-md border border-gray-300 bg-white"
                >
                  <div class="h-full min-h-full overflow-x-auto">
                    <table
                      class="invoice-beside-legacy invoice-beside-summary-table products-table w-full min-w-[340px] table-fixed border-collapse text-sm"
                      :aria-label="'Partial invoice for ' + (orderLineRow.name || 'order line')"
                    >
                      <colgroup>
                        <col class="w-[22%]" />
                        <col class="w-[26%]" />
                        <col class="w-[26%]" />
                        <col class="w-[26%]" />
                      </colgroup>
                      <tbody>
                        <tr class="text-center">
                          <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                            {{
                              orderLineRow?.id
                                ? invoicedQtyForLineKey(
                                    buildOrderLineLineKey(primaryOrderItem?.id, String(orderLineRow.id)),
                                  )
                                : '—'
                            }}
                          </td>
                          <td class="px-4 py-2 align-middle text-sm tabular-nums text-gray-800">
                            {{ formatRemainingStandaloneQty(orderLineRow) }}
                          </td>
                          <td class="px-4 py-2 align-middle text-sm">
                            <input
                              v-if="orderLineRow?.id && primaryOrderItem?.id"
                              type="text"
                              inputmode="numeric"
                              :min="standaloneOrderLinePartialBounds(orderLineRow).min"
                              :max="standaloneOrderLinePartialBounds(orderLineRow).max"
                              class="mx-auto w-full min-w-12 max-w-[5rem] rounded border border-gray-300 p-1.5 text-center text-xs outline-none"
                              :value="
                                partialQtyToInvoiceByLineKey[
                                  buildOrderLineLineKey(primaryOrderItem?.id, String(orderLineRow.id))
                                ] ?? 0
                              "
                              @input="onOrderLinePartialInvoiceInput($event, orderLineRow)"
                            />
                            <span v-else class="text-amber-700">Save order for line id</span>
                          </td>
                          <td class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm tabular-nums text-gray-800">
                            {{
                              formatMoney(
                                partialToInvoiceDollarForOrderLine(orderLineRow, orderLineIndex),
                              )
                            }}
                            <span class="text-[10px] text-gray-500">
                              {{ orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency?.code : '' }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        </template>

        <!-- Footer totals: same row layout as SKU / order lines — tables align under Size|Qty|Price|Total, not under the thumb column. -->
        <div
          v-if="
            orderItemRefIndex === 0 &&
            (
              (orderItemRef.factory_products?.length ?? 0) > 0 ||
              standaloneOrderLines.length > 0
            )
          "
          class="price-breakdown-meta-row mb-5"
        >
          <!-- Desktop: reserves column 1 so totals tables align with rows above (same grid track as meta). -->
          <div class="hidden min-[720px]:block" aria-hidden="true" />
          <div class="price-breakdown-meta-row__tables flex min-w-0 w-full flex-col gap-0">
            <div
              class="grand-totals-summary-pair factory-pricing-invoice-pair flex w-full min-w-0 flex-col gap-3 min-[1280px]:flex-row min-[1280px]:items-start min-[1280px]:gap-4"
            >
              <div
                class="price-breakdown-roster-table-wrap min-w-0 min-h-0 flex-1 overflow-x-auto rounded-md border border-gray-300 bg-white"
                :class="showInvoiceProgressColumns ? 'min-[1280px]:max-w-[56%]' : 'w-full min-[1280px]:max-w-full'"
              >
                <div class="h-full min-h-full overflow-x-auto">
                  <table
                    class="w-full min-w-[260px] table-fixed border-collapse products-table text-sm"
                    aria-label="Order item quantity and list total"
                  >
                    <tbody>
                      <tr class="border-t-2 border-gray-300 bg-gray-50 text-gray-900">
                        <td
                          class="w-[25%] px-4 py-2 text-left align-middle text-sm font-semibold text-gray-900"
                        >
                          Total
                        </td>
                        <td
                          class="w-[25%] px-4 py-2 text-center align-middle text-sm font-semibold tabular-nums text-gray-900"
                        >
                          {{
                            orderItemRef.price_info?.total_quantity != null
                              ? formatIntegerQty(orderItemRef.price_info.total_quantity)
                              : '—'
                          }}
                        </td>
                        <td class="w-[25%] px-4 py-2 align-middle"></td>
                        <td
                          class="w-[25%] whitespace-nowrap px-2 py-2 text-right align-middle text-sm font-semibold tabular-nums text-gray-900"
                        >
                          <!--
                            Task 16180: strikethrough gross above emerald net. On hover, the
                            native browser tooltip (title) shows a multi-line breakdown
                            (newlines via \n which Chromium / Firefox / Safari all render).
                            No custom CSS popover — those caused the modal body to gain a
                            vertical scroll when the popover overflowed the cell bounds.
                          -->
                          <template v-if="creditedAmountForOrderItem(orderItemRef.id) > 0">
                            <span
                              class="cursor-help"
                              :title="
                                'Credit summary\n'
                                + '  Gross:     ' + formatMoney(Math.max(0, Number(orderItemRef.price_info?.total_price) || 0))
                                + ' ' + (orderItemNetPriceCurrency?.code || '') + '\n'
                                + '− Credited: ' + formatMoney(creditedAmountForOrderItem(orderItemRef.id))
                                + ' ' + (orderItemNetPriceCurrency?.code || '') + '\n'
                                + '= Net:       ' + formatMoney(Math.max(
                                    0,
                                    (Number(orderItemRef.price_info?.total_price) || 0)
                                    - creditedAmountForOrderItem(orderItemRef.id),
                                  ))
                                + ' ' + (orderItemNetPriceCurrency?.code || '') + '\n\n'
                                + creditedCountForOrderItem(orderItemRef.id)
                                + ' credit note' + (creditedCountForOrderItem(orderItemRef.id) === 1 ? '' : 's')
                                + ' against this order item'
                              "
                            >
                              <span class="block text-slate-400 line-through">
                                {{
                                  formatMoney(Math.max(0, Number(orderItemRef.price_info?.total_price) || 0))
                                }}<span class="text-[10px] font-normal text-slate-400">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </span>
                              <span class="block text-emerald-700">
                                {{
                                  formatMoney(Math.max(
                                    0,
                                    (Number(orderItemRef.price_info?.total_price) || 0)
                                    - creditedAmountForOrderItem(orderItemRef.id),
                                  ))
                                }}<span class="text-[10px] font-normal text-emerald-700/80">{{
                                  orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                                }}</span>
                              </span>
                            </span>
                          </template>
                          <template v-else>
                            <span class="whitespace-nowrap"
                              >{{
                                formatMoney(Math.max(0, Number(orderItemRef.price_info?.total_price) || 0))
                              }}<span class="text-[10px] font-normal text-gray-500">{{
                                orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                              }}</span></span
                            >
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div
                v-if="showInvoiceProgressColumns"
                class="min-w-0 w-full min-[1280px]:flex-1 min-[1280px]:basis-0 min-[1280px]:max-w-[44%] rounded-md border border-gray-300 bg-white"
              >
                <div class="h-full min-h-full overflow-x-auto">
                  <table
                    class="invoice-beside-legacy invoice-beside-summary-table products-table w-full min-w-[340px] table-fixed border-collapse text-sm"
                    aria-label="Invoice totals for this order item"
                  >
                    <colgroup>
                      <col class="w-[22%]" />
                      <col class="w-[26%]" />
                      <col class="w-[26%]" />
                      <col class="w-[26%]" />
                    </colgroup>
                    <tbody>
                      <tr class="invoice-beside-summary-row border-t-2 border-gray-300 bg-gray-50 text-gray-900">
                        <td class="px-4 py-2 text-center align-middle text-sm font-semibold tabular-nums text-gray-900">
                          {{ sumInvoicedForOrderItem(orderItemRef) }}
                        </td>
                        <td class="px-4 py-2 text-center align-middle text-sm font-semibold tabular-nums text-gray-900">
                          {{ sumRemainingForOrderItem(orderItemRef) }}
                        </td>
                        <td class="px-4 py-2 text-center align-middle text-sm font-semibold tabular-nums text-gray-900">
                          {{ sumPartialQtyForOrderItem(orderItemRef) }}
                        </td>
                        <td
                          class="whitespace-nowrap px-2 py-2 text-right align-middle text-sm font-semibold tabular-nums text-gray-900"
                        >
                          <span class="whitespace-nowrap"
                            >{{
                              formatMoney(Math.max(0, sumPartialAmountForOrderItem(orderItemRef)))
                            }}<span class="text-[10px] font-normal text-gray-500">{{
                              orderItemNetPriceCurrency?.code ? ' ' + orderItemNetPriceCurrency.code : ''
                            }}</span></span
                          >
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        </template>

        <div class="mt-3 flex flex-wrap gap-2" v-if="can('update-price-breakdown')">
          <button
            class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200"
            @click="addProductModal = true"
          >
            <plus-icon class="size-5"></plus-icon> Add Item
          </button>
          <button
            type="button"
            class="flex gap-x-2 px-3 py-2 items-center text-[#219f84] border border-[#219f84] rounded-md cursor-pointer hover:text-white hover:bg-[#219F84] duration-200"
            @click="openAddOrderLineModal"
          >
            <plus-icon class="size-5"></plus-icon>
            Add manual line
          </button>
          <template v-if="can('update-techpack')">
            <a
              :href="`${appUrl}/edit-techpack/${order_ref.id}?source=new_dashboard`"
              class="border p-2 rounded-md border-[#219F84] text-[#219F84]"
            >
              Edit Tech Pack
            </a>
          </template>
        </div>
      </template>
      <template #footer>
        <div class="flex w-full flex-col gap-4">
          <div
            v-if="can('update-price-breakdown')"
            class="flex w-full flex-col gap-4 border-b border-gray-100 pb-4"
          >
            <div
              class="flex w-full min-w-0 flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end lg:gap-x-6 lg:gap-y-3"
            >
              <div class="flex min-w-[12rem] max-w-md flex-1 flex-col gap-1.5">
                <label class="text-sm font-medium text-gray-700">Order type</label>
                <Select
                  append-to-body
                  v-model="selectedOrderType"
                  :options="orderTypes"
                  label="label"
                  placeholder="Select order type"
                  :clearable="false"
                  @option:selected="handleOrderTypeUpdate"
                  :class="{ 'border-red-500': errorMessages['order_type'] }"
                  class="w-full min-w-[220px] text-sm"
                />
              </div>
              <div
                v-if="can('change-order-billing-merchant')"
                class="flex min-w-[14rem] max-w-xl flex-1 flex-col gap-1.5"
              >
                <label class="text-sm font-medium text-gray-700">Billing merchant</label>
                <p
                  v-if="billingMerchantSelectDisabled"
                  class="w-full min-w-[280px] rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                  :title="billingMerchantSelectDisabled ? 'Cannot change billing merchant after an invoice exists' : undefined"
                >
                  {{ billingMerchantDisplayName || '—' }}
                </p>
                <Select
                  v-else
                  ref="billingSelect"
                  v-model="selectedBillingCompany"
                  :options="billingMerchant"
                  @update:modelValue="handleBillingMerchantUpdate"
                  label="company_name"
                  :reduce="merchant => merchant.id"
                  :clearable="false"
                  class="w-full min-w-[280px]"
                />
              </div>
            </div>
          </div>
          <div
            v-if="can('update-price-breakdown')"
            class="flex w-full flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between"
          >
            <div class="grid w-full min-w-0 flex-1 grid-cols-1 gap-4 md:grid-cols-2">
              <div class="flex min-w-0 flex-col gap-1.5">
                <label class="block text-sm font-medium leading-5 text-gray-700">Memo on invoice</label>
                <textarea
                  v-model="invoice_memo"
                  rows="3"
                  class="memo-internal-shared min-h-[4.5rem] max-h-32 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400"
                  placeholder="Shown on the Stripe invoice PDF"
                />
              </div>
              <div class="flex min-w-0 flex-col gap-1.5">
                <label class="block text-sm font-medium leading-5 text-gray-700">Internal note</label>
                <textarea
                  v-model="internal_note"
                  rows="3"
                  class="memo-internal-shared min-h-[4.5rem] max-h-32 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm outline-none placeholder:text-gray-400"
                  placeholder="Internal note (not shown on customer invoice)"
                />
              </div>
            </div>
            <div class="flex shrink-0 items-start gap-x-5">
              <button
                class="h-max rounded-md border bg-[#219F84] p-2 text-white"
                type="button"
                @click="updateOrderPrices(false)"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </template>
    </Dialog>
  </Transition>
  <!-- US 5253 / Task 16169: split-button → invoice picker (multi-invoice) → wizard (stub for Task 16178). -->
  <SelectStripeInvoiceForCreditNoteModal
    v-model="invoicePickerOpen"
    :order-id="Number(order_ref.id ?? 0)"
    :order-label="dialogOrderNumberDisplay"
    :partial-invoices="creditableInvoicesList"
    @picked="onInvoicePickedForCreditNote"
  />
  <CreateStripeCreditNoteModal
    v-model="creditNoteWizardOpen"
    :order-id="Number(order_ref.id ?? 0)"
    :order-item-invoice-id="Number(selectedInvoiceForCn?.id ?? 0)"
    :order-label="dialogOrderNumberDisplay"
    :invoice-label="selectedInvoiceForCn?.stripe_invoice_number ?? selectedInvoiceForCn?.stripe_invoice_id ?? null"
    :eligibility="selectedEligibilityForCn"
    @credit-note-created="onCreditNoteCreated"
  />
  <Transition name="fade">
    <Dialog
      v-if="addOrderLineModal"
      title="Add manual line"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :sm="true"
      @closeEvent="closeAddOrderLineModal"
    >
      <template #modal-content>
        <div class="flex flex-col gap-4">
          <div>
            <label class="block mb-1 text-sm font-medium text-gray-700">Name</label>
            <input
              v-model="addOrderLineForm.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              placeholder="Line item name"
            />
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-gray-700">Quantity</label>
            <input
              type="text"
              inputmode="numeric"
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              :value="String(addOrderLineForm.quantity)"
              @input="onAddOrderLineQtyInput"
            />
          </div>
          <div class="flex justify-between items-center gap-3">
            <label class="text-sm font-medium text-gray-700 shrink-0">{{
              addOrderLineForm.is_fixed ? 'Unit price' : 'Percentage'
            }}</label>
            <div class="flex items-center gap-x-2">
              <input
                id="add-order-line-fixed-only"
                v-model="addOrderLineForm.is_fixed"
                type="checkbox"
                name="Fixed unit price"
                class="w-4 h-4"
              />
              <label for="add-order-line-fixed-only" class="text-sm text-gray-700 whitespace-nowrap"
                >Fixed price</label
              >
            </div>
          </div>
          <div class="min-h-[2.75rem]">
            <template v-if="addOrderLineForm.is_fixed">
              <input
                v-model="addOrderLineForm.price"
                type="text"
                inputmode="decimal"
                class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
                placeholder="0"
                @input="onPriceInputSanitize"
              />
            </template>
            <template v-else>
              <div class="relative flex items-center">
                <input
                  v-model="addOrderLineForm.percentage"
                  type="text"
                  inputmode="decimal"
                  class="w-full px-3 py-2 pr-9 border border-gray-300 rounded-md outline-none"
                  placeholder="0"
                  @input="onPriceInputSanitize"
                />
                <span class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">%</span>
              </div>
            </template>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end w-full gap-2">
          <button
            type="button"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            @click="closeAddOrderLineModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-md border border-[#219F84] text-[#219f84] hover:text-white hover:bg-[#219F84]"
            @click="saveAddOrderLine"
          >
            Add line
          </button>
        </div>
      </template>
    </Dialog>
  </Transition>
  <Transition name="fade">
    <AddProductModal
      v-if="addProductModal"
      @on-close="() => (addProductModal = false)"
      :customerId="(order_item as DashboardItem).order.customer_id"
      :companyId="(order_item as DashboardItem).order.company_id"
      :orderItem="order_item"
      :lockedFactoryId="addModalLockedFactoryId"
      :lockerRoom="lockerRoom"
      :factories="factories"
      :company_products="company_products"
      :copiedRoster="copiedRoster"
      @copy-roster="copyRoster"
      @add-factory-product="addFactoryProduct"
    />
  </Transition>
  <Transition name="fade">
    <Dialog
      v-if="shippingAndDiscountModal"
      title="Add Shipping and Discount"
      :showCloseButton="true"
      :showFooter="true"
      @close-event="discount_shipping_info = { ...discount_shipping_info, ...JSON.parse(JSON.stringify(order_items?.[0]?.price_info ?? order_ref.price_info)) }"
      @cancel-event="discount_shipping_info = { ...discount_shipping_info, ...JSON.parse(JSON.stringify(order_items?.[0]?.price_info ?? order_ref.price_info)) }"
      :showSaveButton="false"
      :showCancelButton="false"
      :custom-footer="true"
      :sm="true"
      @closeEvent="closeShippingAndDiscountModal"
    >
      <template #modal-content>
        <div>
          <div class="flex justify-between">
            <label class="block mb-1 text-sm font-medium text-gray-700">Add Discount</label>
            <div class="flex items-center gap-x-2">
              <input
                type="checkbox"
                name="Fixed Discount"
                class="w-4 h-4"
                tr
                v-model="discount_shipping_info.is_fixed"
              />
              <label for="Fixed Discount">Fixed Price</label>
            </div>
          </div>
          <template v-if="discount_shipping_info.is_fixed">
            <input
              class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
              type="text"
              inputmode="decimal"
              v-model="discount_shipping_info.fixed_value"
              @input="onPriceInputSanitize"
              @change="
                () => {
                  discount_shipping_info.fixed_value = discount_shipping_info.fixed_value
                    ? parseFloat(formatMoney(discount_shipping_info.fixed_value))
                    : 0;
                }
              "
              placeholder="Enter Fixed Price"
            />
          </template>
          <template v-else>
            <div class="relative">
              <input
                class="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md outline-none"
                type="text"
                inputmode="decimal"
                v-model="discount_shipping_info.discount"
                @input="onPriceInputSanitize"
                @change="
                  () => {
                    discount_shipping_info.discount = discount_shipping_info.discount
                      ? parseFloat(formatMoney(discount_shipping_info.discount))
                      : 0;
                  }
                "
                placeholder="Enter Discount Percentage"
              />
              <span class="absolute transform -translate-y-1/2 right-3 top-1/2">%</span>
            </div>
          </template>
          <label class="block mt-4 mb-1 text-sm font-medium text-gray-700">Add Shipping</label>
          <input
            class="w-full px-3 py-2 border border-gray-300 rounded-md outline-none"
            type="text"
            inputmode="decimal"
            v-model="discount_shipping_info.shipping"
            @input="onPriceInputSanitize"
            @change="
              () => {
                discount_shipping_info.shipping = discount_shipping_info.shipping
                  ? parseFloat(formatMoney(discount_shipping_info.shipping))
                  : 0;
              }
            "
            placeholder="Enter Shipping Cost"
          />
          <span class="absolute transform -translate-y-1/2 right-6" style="top: 65%">{{
            orderItemNetPriceCurrency?.code ?? ''
          }}</span>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end w-full">
          <button
            @click="calculateShippingAndDiscount"
            class="border px-4 py-2 rounded-md border-[#219F84] text-[#219F84]"
          >
            Save
          </button>
        </div>
      </template>
    </Dialog>
  </Transition>
  </div>
</template>
<script setup lang="ts">
import Dialog from '@/components/Shared/Dialog.vue';
import Select from '@/components/Shared/Select.vue';
import { can } from '@/composables';
import { showConfirm, showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { ApiResponse2, DashboardItem, OrderType, Product, Result } from '@/models';
import {
  ensureOrderLineName,
  normalizeOrderLinesFromApi,
  orderLineUsesFixedPricing,
} from '@/models/OrderItemOrderLine';
import { useMainStore } from '@/store';
import { orderTypes } from '@/utils/constants/orderTypes';
import { invoiceCompositeStatusPillClass } from '@/utils/invoiceCompositeStatusDisplay';
import { getOrderItemNetPriceCurrency } from '@/utils/orderItemPriceDisplay';
import { ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { onClickOutside } from '@vueuse/core';
import { AxiosError } from 'axios';
import { chunk } from 'lodash';
import { BadgeCheck, CircleX, Clock3, FileText, RefreshCw, Trash2, TriangleAlert, SquareM } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import AddProductModal from './AddProductModal.vue';
import BillingDocumentsLedger from './BillingDocumentsLedger.vue';
import CreateStripeCreditNoteModal from './CreateStripeCreditNoteModal.vue';
import SelectStripeInvoiceForCreditNoteModal from './SelectStripeInvoiceForCreditNoteModal.vue';
import {
  type CreditNoteEligibility,
  disableReasonLabel,
  fetchInvoiceCreditEligibility,
  isCreditableInvoiceRow,
} from '@/utils/stripeCreditNote';
import { resolveInvoiceDocumentUrl } from '@/utils/invoicePdf';
import { appendCreditNoteToInvoice, liveCreditTotalsForOrderItem } from '@/utils/billingDocuments';
import { openCreditNotePdf } from '@/utils/documentPdfUrl';
import { CopiedRoster } from '@/models/CopiedRoster';
import Swal from "sweetalert2";
const { loading } = storeToRefs(useMainStore());
const stripeLoading = ref(false);

/** Always clear modal + global save loaders (e.g. after API error or early return). */
function clearPriceModalLoaders(): void {
  loading.value = false;
  stripeLoading.value = false;
}
/* ─────────── Credit Note v2 (US 5253 / Task 16169) — split button + invoice picker + wizard entry ─────────── */
/** Split-button dropdown open state. */
const splitButtonOpen = ref(false);
/** Container ref for click-outside-to-close on the split button menu. */
const splitButtonContainerRef = ref<HTMLElement | null>(null);
onClickOutside(splitButtonContainerRef, () => {
  splitButtonOpen.value = false;
});
/** True while the Create Credit Note menu action is fetching eligibility (single-invoice fast path). */
const creditFlowLoading = ref(false);
/** Invoice-picker modal (shown only when the order has 2+ Stripe invoices). */
const invoicePickerOpen = ref(false);
/** Wizard modal (Phase 3 / Task 16178). Stub for now — receives invoice + eligibility from picker / fast path. */
const creditNoteWizardOpen = ref(false);
const selectedInvoiceForCn = ref<Record<string, any> | null>(null);
const selectedEligibilityForCn = ref<CreditNoteEligibility | null>(null);
/** Template ref to `BillingDocumentsLedger` — used to invalidate its per-invoice eligibility
 *  cache right after a credit note is created (live refresh, AB#20717 round 2 fix #4), without
 *  a `:key` remount (which would collapse the ledger if the user had it open). */
const billingLedgerRef = ref<{ invalidateInvoiceEligibility: (invoiceId: number | string) => void } | null>(null);

/** User story: hide Shipping Cost / Discount / Final Price rows and the Shipping & Discount control (data kept in model). */
/** Show shipping/discount/final summary rows (values still come from the same model). */
const showLegacyShippingDiscountFooterRows = false;

/** Stripe invoice badge in modal header + invoice number / edit / manual entry — off until we ship that UX again. */
const showHeaderInvoiceUi = false;

const stripeDashboardUrl = import.meta.env.VITE_APP_STRIPE_DASHBOARD_URL;

const props = defineProps<{
  order_item: any;
  order: any;
  appUrl: string;
}>();

const copiedRoster = ref<CopiedRoster>({
  isAnyRosterCopied: false,
  copiedFromProductIndex: null,
  rosterData: []
});

const order_ref = ref<Record<string, any>>({});
/** Mutable alias of `order_ref.value.items` — reassigned when `props.order` hydrates so `item_invoices` stays in sync with the API. */
let order_items: any[] = [];

/** Reactive view of line items (use in template/computed — plain `order_items` is not tracked). */
const orderItemsForView = computed(() => (order_ref.value?.items ?? []) as Record<string, any>[]);

const primaryOrderItem = computed(
  () => orderItemsForView.value[0] as Record<string, any> | undefined,
);

/** Ensure `item_invoices` exists when API only sends camelCase `itemInvoices`. */
function normalizeOrderItemsInvoiceFields(items: Record<string, any>[] | undefined): void {
  for (const it of items ?? []) {
    const camel = it.itemInvoices;
    if (!Array.isArray(it.item_invoices) && Array.isArray(camel)) {
      it.item_invoices = [...camel];
    }
  }
}

function invoiceCompositeStatusLocksBillingMerchant(status: unknown): boolean {
  const st = String(status ?? '').trim().toLowerCase();
  return st !== '' && st !== 'not_invoiced';
}

/**
 * Bug 20222 (Stripe sync saga) — raw `order_item_invoices.status === 'pending_stripe_sync'`:
 * sync in flight or awaiting reconciliation (double-fire guard / post-finalize ambiguous failure).
 */
function isPendingStripeSyncStatus(status: unknown): boolean {
  return String(status ?? '').trim().toLowerCase() === 'pending_stripe_sync';
}

/**
 * Bug 20222 — raw `order_item_invoices.status === 'failed'`: attempt failed pre-finalize and its
 * quantities were freed by the backend. Must never be shown as an invoice or counted as invoiced qty.
 */
function isFailedStripeSyncStatus(status: unknown): boolean {
  return String(status ?? '').trim().toLowerCase() === 'failed';
}

/** Drop `failed` ledger rows (Bug 20222) — mirrors backend exclusion; freed quantities/no real invoice. */
function excludeFailedStripeSyncRows(rows: Record<string, any>[]): Record<string, any>[] {
  return rows.filter((inv) => !isFailedStripeSyncStatus(inv?.status));
}

/**
 * AB#20223 follow-up — the backend now populates `manual_review_reason` on `order_item_invoice`
 * ledger rows for `requires_manual_review` rows AND `failed` rows (why the attempt failed).
 * Returns a trimmed reason string, or '' when the row has none.
 */
function manualReviewReasonFor(row: Record<string, any> | null | undefined): string {
  const reason = row?.manual_review_reason;
  return typeof reason === 'string' && reason.trim() !== '' ? reason.trim() : '';
}

/**
 * Append the ledger row's `manual_review_reason` to a base message — DOM `:title` tooltips only
 * (rendered as a plain attribute value; safe as-is). Never pass the result of this to `showToast`
 * — use `appendManualReviewReasonForToast` there instead (see `escapeHtmlForToast`).
 */
function appendManualReviewReason(message: string, row: Record<string, any> | null | undefined): string {
  const reason = manualReviewReasonFor(row);
  return reason ? `${message} Reason: ${reason}` : message;
}

/**
 * MAJOR 4 (AB#20223 follow-up) — HTML-escape text before it reaches a SweetAlert2 toast: `showToast`
 * passes its `message` as SweetAlert2's `title` option, which renders as HTML (not escaped text).
 * `manual_review_reason` and Stripe's `stripe_error` are server/Stripe-originated strings that can
 * echo customer-controlled values — never interpolate them into a toast unescaped.
 */
function escapeHtmlForToast(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Toast-safe variant of `appendManualReviewReason`: HTML-escapes the appended reason. `message`
 * itself must already be toast-safe — callers passing dynamic/external text (e.g. Stripe's
 * `stripe_error`) as `message` must run it through `escapeHtmlForToast` themselves first.
 */
function appendManualReviewReasonForToast(message: string, row: Record<string, any> | null | undefined): string {
  const reason = manualReviewReasonFor(row);
  return reason ? `${message} Reason: ${escapeHtmlForToast(reason)}` : message;
}

/**
 * Bug 20222 minor — a pre-finalize `pending_stripe_sync` row has neither `stripe_invoice_number`
 * nor `stripe_invoice_id`; give it a sensible label instead of an empty pill in list views.
 */
function invoiceListDisplayLabel(inv: Record<string, any> | null | undefined): string {
  const number = inv?.stripe_invoice_number;
  const id = inv?.stripe_invoice_id;
  if (number) return String(number);
  if (id) return String(id);
  if (isPendingStripeSyncStatus(inv?.status)) {
    return inv?.requires_manual_review ? 'Under review' : 'Pending sync';
  }
  return 'N/A';
}

/**
 * Bug 20222 minor — mirror the backend guard: a pending row can only be removed when it's the
 * post-finalize ambiguous case (`requires_manual_review`); otherwise the backend rejects removal,
 * so don't offer a button that always 422s.
 */
function canOfferRemoveInvoiceFor(inv: Record<string, any> | null | undefined): boolean {
  if (!can('remove-invoice')) return false;
  if (isPendingStripeSyncStatus(inv?.status)) {
    return !!inv?.requires_manual_review;
  }
  return true;
}

function mergeOrderItemInvoiceIntoPrimaryItem(
  invoiceRow: Record<string, any> | null | undefined,
  stripeInvoiceMeta?: { invoiceId?: string; invoice_pdf?: string; hosted_invoice_url?: string },
): void {
  if (!invoiceRow || typeof invoiceRow !== 'object') return;
  const item0 = order_ref.value?.items?.[0] as Record<string, any> | undefined;
  if (!item0) return;

  const row = { ...invoiceRow };
  if (!row.stripe_invoice_id && stripeInvoiceMeta?.invoiceId) {
    row.stripe_invoice_id = stripeInvoiceMeta.invoiceId;
  }
  if (!row.invoice_pdf && stripeInvoiceMeta?.invoice_pdf) {
    row.invoice_pdf = stripeInvoiceMeta.invoice_pdf;
  }
  if (!row.hosted_invoice_url && stripeInvoiceMeta?.hosted_invoice_url) {
    row.hosted_invoice_url = stripeInvoiceMeta.hosted_invoice_url;
  }

  const list = getItemInvoicesListForOrderItem(item0);
  const key = row.stripe_invoice_id ?? row.id;
  if (key == null || !list.some((inv) => (inv.stripe_invoice_id ?? inv.id) === key)) {
    item0.item_invoices = [...list, row];
  }

  if (!invoiceCompositeStatusLocksBillingMerchant(item0.invoice_composite_status)) {
    item0.invoice_composite_status = 'partially_invoiced';
  }
}

function hydrateLocalOrderFromProps(): void {
  const src =
    props.order && typeof props.order === 'object' ? props.order : {};
  order_ref.value = JSON.parse(JSON.stringify(src)) as Record<string, any>;
  order_items = order_ref.value.items ?? [];
  normalizeOrderItemsInvoiceFields(order_items);

  const api0 = order_items[0] as Record<string, any> | undefined;
  const propItem = props.order_item as Record<string, any> | undefined;
  const invFromProp =
    propItem?.item_invoices ?? propItem?.itemInvoices;
  if (
    api0 &&
    Array.isArray(invFromProp) &&
    invFromProp.length > 0 &&
    !api0.item_invoices?.length &&
    !api0.itemInvoices?.length
  ) {
    api0.item_invoices = [...invFromProp];
  }
}

hydrateLocalOrderFromProps();

/** `order_item_invoices` / API `item_invoices` (legacy line-item stripe_* on order_items removed). */
function getItemInvoicesListForOrderItem(item: Record<string, any> | null | undefined): any[] {
  if (!item) return [];
  const raw = item.item_invoices ?? item.itemInvoices;
  return Array.isArray(raw) ? raw : [];
}

/**
 * `order_item_invoices.memo_snapshot` is plain text for split-created invoices (copy of `invoice_memo`), but legacy
 * backfill stores audit JSON (`migrated_from: order_items.legacy`, …) — not customer-facing. Never show that JSON in
 * the “Memo on invoice” field.
 */
function invoiceMemoTextFromLedgerMemoSnapshot(memoSnapshot: unknown): string {
  if (memoSnapshot == null) return '';

  if (typeof memoSnapshot === 'object' && memoSnapshot !== null && !Array.isArray(memoSnapshot)) {
    const o = memoSnapshot as Record<string, unknown>;
    return o.migrated_from === 'order_items.legacy' ? '' : '';
  }

  const s = String(memoSnapshot);
  const t = s.trim();
  if (t === '') return '';
  if (!t.startsWith('{')) {
    return s;
  }
  try {
    const o = JSON.parse(t) as Record<string, unknown>;
    if (o?.migrated_from === 'order_items.legacy') {
      return '';
    }
  } catch {
    return s;
  }
  return '';
}

function lastMemoFromItemInvoicesOnItem(item: Record<string, any> | null | undefined): string {
  const list = excludeFailedStripeSyncRows(getItemInvoicesListForOrderItem(item));
  if (!list.length) return '';
  const last = list[list.length - 1];
  return invoiceMemoTextFromLedgerMemoSnapshot(last?.memo_snapshot);
}

/** Ledger rows for header / single-invoice affordances — excludes `failed` (Bug 20222; never a real invoice). */
const itemInvoicesOnPrimaryOrderItem = computed(() =>
  excludeFailedStripeSyncRows(getItemInvoicesListForOrderItem(primaryOrderItem.value)),
);
/** Most recent row in `item_invoices` for header / single-invoice affordances. */
const latestItemInvoiceOnPrimary = computed(() => {
  const list = itemInvoicesOnPrimaryOrderItem.value;
  return list.length ? list[list.length - 1] : null;
});
/** Expose latest `order_item_invoices` row for template (avoid `as` casts in the template). */
const headerLatestOrderItemInvoice = computed(
  () => (latestItemInvoiceOnPrimary.value ?? null) as Record<string, any> | null,
);
/** Dialog title: first line `item_no`, else `order_no`, else N/A. */
const dialogOrderNumberDisplay = computed(() => {
  const itemNo = (order_ref.value.items?.[0] as Record<string, any> | undefined)?.item_no;
  if (itemNo != null && String(itemNo).trim() !== '') {
    return String(itemNo).trim();
  }
  const orderNo = order_ref.value.order_no;
  if (orderNo != null && String(orderNo).trim() !== '') {
    return String(orderNo).trim();
  }
  return 'N/A';
});

/**
 * Bug 18510 — Stripe invoice links (hosted_invoice_url / invoice_pdf) expire.
 * A permission-gated "Refresh" button force-refreshes them from Stripe; the fresh
 * URLs are held here (keyed by order_item_invoice id) and preferred by the link
 * getters below, so links update in place without reloading the modal.
 */
const refreshedInvoiceLinks = ref<
  Record<string, { hosted_invoice_url?: string | null; invoice_pdf?: string | null; invoice_pdf_s3_url?: string | null }>
>({});
const refreshingInvoiceId = ref<string | number | null>(null);

async function refreshInvoiceLink(inv: Record<string, any>): Promise<void> {
  if (!inv?.id || refreshingInvoiceId.value != null) return;
  refreshingInvoiceId.value = inv.id;
  try {
    const { data } = await http.post(`order-item-invoices/${inv.id}/refresh-link`);
    const r = data?.result ?? {};
    if (r?.id != null) {
      refreshedInvoiceLinks.value = {
        ...refreshedInvoiceLinks.value,
        [String(r.id)]: {
          hosted_invoice_url: r.hosted_invoice_url,
          invoice_pdf: r.invoice_pdf,
          // AB#20717 — refresh-link response now also carries the S3-signed copy.
          invoice_pdf_s3_url: r.invoice_pdf_s3_url,
        },
      };
    }
    showToast('Invoice link refreshed', 'success');
  } catch (e) {
    handleResponseException(e);
  } finally {
    refreshingInvoiceId.value = null;
  }
}


/**
 * Resolve the best openable invoice URL, preferring a freshly-refreshed value
 * (from the Refresh button) over the stored one.
 *
 * AB#20717 — delegates to the shared `resolveInvoiceDocumentUrl` ladder
 * (S3-signed copy first, then `hosted_invoice_url`, then `invoice_pdf`; see Bug
 * 18510 for why `hosted_invoice_url` is preferred over `invoice_pdf` within Stripe's
 * own pair). Name/signature kept as-is — every existing caller is unchanged.
 */

function resolveInvoiceUrl(inv: Record<string, unknown> | null | undefined): string {
  if (!inv) return '';
  const override = (inv as Record<string, unknown>)?.id != null
    ? refreshedInvoiceLinks.value[String((inv as Record<string, unknown>).id)]
    : undefined;
  return resolveInvoiceDocumentUrl(inv, override);
}

/** Direct PDF first; Stripe hosted page if PDF URL not stored (from `order_item_invoices`, not legacy `stripe_invoice_info` on the line item). */
const primaryInvoicePdfOrHostedUrl = computed(() =>
  resolveInvoiceUrl(latestItemInvoiceOnPrimary.value as Record<string, unknown> | null | undefined),
);

function invoicePdfOrHostedUrl(inv: Record<string, unknown>): string {
  return resolveInvoiceUrl(inv);
}

/** `inv.credit_notes[]` — the embedded array from the invoice payload (AB#20717 round 2). Defensive: absent/non-array renders no pills. */
function creditNotesForInvoice(inv: Record<string, unknown> | null | undefined): Record<string, unknown>[] {
  const list = (inv as Record<string, unknown> | null | undefined)?.credit_notes;
  return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
}

/** Pill text: `{number ?? 'Credit note'} −{amount} {currency}` (amount is already a positive major-unit value on the row; the pill always renders it as a negative/applied amount). */
function creditNotePillLabel(cn: Record<string, unknown>): string {
  const number = typeof cn?.stripe_credit_note_number === 'string' && cn.stripe_credit_note_number.trim() !== ''
    ? cn.stripe_credit_note_number
    : 'Credit note';
  const amount = formatMoney(Math.abs(Number(cn?.amount ?? 0)));
  const currency = typeof cn?.currency === 'string' ? ` ${cn.currency.toUpperCase()}` : '';
  return `${number} −${amount}${currency}`;
}

/**
 * Matches backend `InvoiceLineKey::factoryProductFpKey`: when two factory rows share the same `id`
 * on one order item, disambiguate so line_keys (and UI totals) stay per-product, not merged.
 */
function factoryProductKeyForInvoice(
  factoryProduct: any,
  fpIndex: number | string,
  orderItemRef?: { factory_products?: any[] } | null,
): string {
  const idx = Number(fpIndex);
  const rawId = factoryProduct?.id;
  const fps = orderItemRef?.factory_products;
  if (rawId != null && rawId !== '' && Array.isArray(fps)) {
    const sid = String(rawId).trim();
    let sameIdCount = 0;
    for (const fp of fps) {
      if (fp != null && String(fp?.id ?? '').trim() === sid) {
        sameIdCount++;
      }
    }
    if (sameIdCount > 1) {
      return `${sid}__${idx}`;
    }
    return String(rawId);
  }
  if (rawId != null && rawId !== '') return String(rawId);
  return `idx${idx}`;
}

function buildFactorySizeLineKey(
  orderItemId: number | string,
  fpKey: string,
  szIdx: number | string,
): string {
  return `oi:${Number(orderItemId)}:fp:${fpKey}:sz:${Number(szIdx)}`;
}

function buildOrderLineLineKey(orderItemId: number | string, uuid: string): string {
  return `oi:${Number(orderItemId)}:ol:${uuid}`;
}

/** Collect partial Stripe invoices from every order line item (same keys as Laravel `itemInvoices`). */
function itemInvoicesForAllOrderItems(): Record<string, any>[] {
  const seen = new Set<string>();
  const out: Record<string, any>[] = [];
  for (const it of order_ref.value?.items ?? []) {
    const raw = excludeFailedStripeSyncRows(getItemInvoicesListForOrderItem(it as Record<string, any>));
    for (const inv of raw) {
      const id = inv?.stripe_invoice_id ?? inv?.id;
      const key = id != null ? String(id) : JSON.stringify(inv);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(inv);
    }
  }
  return out;
}

const partialInvoicesList = computed(() => itemInvoicesForAllOrderItems());

/**
 * US 5253 — credit notes need a real Stripe invoice. Saga rows still in `pending_stripe_sync`
 * or `failed` carry `stripe_invoice_id = null` and would 422 on every eligibility call, so they
 * are excluded from all credit-note entry points (menu item, fast path, picker).
 */
const creditableInvoicesList = computed(() =>
  partialInvoicesList.value.filter((inv: Record<string, any>) => isCreditableInvoiceRow(inv)),
);

/** Any persisted invoice row (including manual / non-Stripe) — addon lock, UC7 price warning. */
const hasAnyOrderItemInvoiceRecord = computed(() => partialInvoicesList.value.length > 0);

/**
 * MINOR 5 (AB#20223 follow-up) — same de-dup as `itemInvoicesForAllOrderItems`, but WITHOUT
 * excluding `failed` rows. Only ever used to surface the most recent failed attempt's reason below;
 * never for qty/pill logic (failed rows stay excluded from all of that, as designed).
 */
function allItemInvoicesIncludingFailedForAllOrderItems(): Record<string, any>[] {
  const seen = new Set<string>();
  const out: Record<string, any>[] = [];
  for (const it of order_ref.value?.items ?? []) {
    const raw = getItemInvoicesListForOrderItem(it as Record<string, any>);
    for (const inv of raw) {
      const id = inv?.stripe_invoice_id ?? inv?.id;
      const key = id !== undefined && id !== null ? String(id) : JSON.stringify(inv);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(inv);
    }
  }
  return out;
}

/**
 * MINOR 5 (AB#20223 follow-up) — the most recent `failed` ledger row's `manual_review_reason`
 * (excluded rows are otherwise invisible once the pill list is empty). Only non-empty when there are
 * NO real (non-failed) invoices, so it never distracts from an actual invoice pill.
 */
const mostRecentFailedInvoiceReason = computed((): string => {
  if (partialInvoicesList.value.length > 0) return '';
  const failedWithReason = allItemInvoicesIncludingFailedForAllOrderItems().filter(
    (inv) => isFailedStripeSyncStatus(inv?.status) && manualReviewReasonFor(inv) !== '',
  );
  if (!failedWithReason.length) return '';
  return manualReviewReasonFor(failedWithReason[failedWithReason.length - 1]);
});

/** Lock billing merchant when any invoice exists or composite status is past `not_invoiced`. */
const billingMerchantSelectDisabled = computed(
  () =>
    hasAnyOrderItemInvoiceRecord.value ||
    invoiceCompositeStatusLocksBillingMerchant(primaryOrderItem.value?.invoice_composite_status),
);

/** @deprecated Use `billingMerchantSelectDisabled` — kept for any legacy checks. */
const hasAnyStoredInvoiceOnOrder = billingMerchantSelectDisabled;

const invoiceAddonsLocked = computed(() => hasAnyOrderItemInvoiceRecord.value);

/**
 * Bug 20222 — most recent `pending_stripe_sync` ledger row on the primary order item, if any
 * (sync in flight / awaiting reconciliation / post-finalize ambiguous failure under review).
 */
const pendingStripeSyncRowForPrimaryItem = computed((): Record<string, any> | null => {
  const rows = itemInvoicesOnPrimaryOrderItem.value;
  for (let i = rows.length - 1; i >= 0; i--) {
    if (isPendingStripeSyncStatus(rows[i]?.status)) return rows[i] as Record<string, any>;
  }
  return null;
});

/** True when a sync is in-flight/awaiting reconciliation for the primary item (ledger row or composite status). */
const hasPendingStripeSyncForPrimaryItem = computed(
  () =>
    pendingStripeSyncRowForPrimaryItem.value != null ||
    String(primaryOrderItem.value?.invoice_composite_status ?? '').trim().toLowerCase() === 'pending_sync',
);

/** True when the pending row is the post-finalize "invoice may have been sent" ambiguous case. */
const pendingStripeSyncRequiresManualReview = computed(
  () => !!pendingStripeSyncRowForPrimaryItem.value?.requires_manual_review,
);

/**
 * AB#20223 follow-up — tooltip for the top "Invoice Status" pill: the primary item's pending-sync
 * ledger row `manual_review_reason`, only while the composite status is `pending_sync`.
 */
const topInvoiceStatusPillTitle = computed((): string | undefined => {
  const status = String(primaryOrderItem.value?.invoice_composite_status ?? '').trim().toLowerCase();
  if (status !== 'pending_sync') return undefined;
  return manualReviewReasonFor(pendingStripeSyncRowForPrimaryItem.value) || undefined;
});

async function confirmUnitPriceChangeIfNeeded(): Promise<boolean> {
  if (!hasAnyOrderItemInvoiceRecord.value) return true;
  return showConfirm(
    'Price change',
    "This price change won't update qty already invoiced. Existing invoices keep the original price; the new price applies only to remaining (un-invoiced) qty.",
  );
}

function stashSizePriceBeforeEdit(size: any) {
  if (!size) return;
  size.__prevFinalNet = size.final_net_price;
}

function stashAddonPriceBeforeEdit(addon: any) {
  if (!addon) return;
  addon.__prevFinalNet = addon.final_net_price;
}

/** Sum prior partial allocations from saved `item_invoices` across all order items (line_key includes order_item id). */
const invoicedQtyByLineKey = computed(() => {
  const invs = partialInvoicesList.value;
  const acc: Record<string, number> = {};
  for (const inv of invs) {
    const allocations = (inv as Record<string, unknown>)?.lineAllocations
      ?? (inv as Record<string, unknown>)?.line_allocations;
    if (!Array.isArray(allocations)) continue;
    for (const row of allocations) {
      const k = String((row as Record<string, unknown>)?.line_key ?? '');
      const q = Number((row as Record<string, unknown>)?.quantity ?? 0);
      if (!k) continue;
      acc[k] = (acc[k] ?? 0) + q;
    }
  }
  return acc;
});

const showInvoiceProgressColumns = computed(() => can('create-stripe-invoice'));

function invoicedQtyForLineKey(lineKey: string): number {
  return invoicedQtyByLineKey.value[lineKey] ?? 0;
}

function parseFactorySizeLineKey(k: string): { oi: number; fp: string; sz: number } | null {
  const m = /^oi:(\d+):fp:(.+):sz:(\d+)$/.exec(k);
  if (!m) return null;
  return { oi: Number(m[1]), fp: m[2], sz: Number(m[3]) };
}

/** Mirrors custimoo `InvoiceLineKey::factoryFpSegmentMatchesFactoryRow` for allocation snapshot lookups. */
function factoryFpSegmentMatchesFactoryRowVue(
  fpSegment: string,
  factoryProduct: any,
  fpIndex: number,
  orderItemRef: Record<string, any>,
): boolean {
  const canonical = factoryProductKeyForInvoice(factoryProduct, fpIndex, orderItemRef);
  const a = String(fpSegment).trim();
  const b = String(canonical).trim();
  if (a === b) return true;
  if (a !== '' && b !== '' && /^-?\d+$/.test(a) && /^-?\d+$/.test(b) && parseInt(a, 10) === parseInt(b, 10)) {
    return true;
  }
  const m = /^idx(\d+)$/.exec(a);
  if (m) return Number(m[1]) === fpIndex;
  return false;
}

/**
 * Prior invoiced qty for one factory size row. Sums `lineAllocations` rows that refer to the same
 * row (e.g. `fp:idx1` vs `fp:` + persisted id) so the grid matches after save.
 */
function invoicedQtyForFactorySizeRow(
  orderItemRef: Record<string, any>,
  factoryProduct: any,
  fpIndex: number,
  szIdx: number,
): number {
  const oid = Number(orderItemRef?.id);
  if (!Number.isFinite(oid)) return 0;
  const acc = invoicedQtyByLineKey.value;
  let sum = 0;
  for (const [k, v] of Object.entries(acc)) {
    const p = parseFactorySizeLineKey(k);
    if (!p || p.oi !== oid || p.sz !== szIdx) continue;
    if (factoryFpSegmentMatchesFactoryRowVue(p.fp, factoryProduct, fpIndex, orderItemRef)) {
      sum += Number(v);
    }
  }
  return sum;
}

/** Draft qty to invoice per line_key (partial Stripe payload). */
const partialQtyToInvoiceByLineKey = ref<Record<string, number>>({});

function partialQtyBoundsForOrderLine(orderQty: unknown, lineKey: string): { min: number; max: number } {
  const oq = Math.max(1, Number(orderQty) || 1);
  const prior = invoicedQtyForLineKey(lineKey);
  return { min: -prior, max: oq - prior };
}

function factorySizePartialInvoiceBounds(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
  size_idx: number | string,
  size: any,
): { min: number; max: number } {
  const oq = Number(size.quantity) || 0;
  const prior = invoicedQtyForFactorySizeRow(
    orderItemRef,
    factory_product,
    Number(factoryProductIndex),
    Number(size_idx),
  );
  return { min: -prior, max: oq - prior };
}

function standaloneOrderLinePartialBounds(orderLineRow: Record<string, any>): { min: number; max: number } {
  const oid = order_items?.[0]?.id;
  if (oid == null || !orderLineRow?.id) return { min: 0, max: 0 };
  const lk = buildOrderLineLineKey(oid, String(orderLineRow.id));
  return partialQtyBoundsForOrderLine(orderLineRow.quantity, lk);
}

function setPartialQtyToInvoice(lineKey: string, raw: string, minQ: number, maxQ: number) {
  let n = parseSignedQty(raw);
  n = clampInt(n, minQ, maxQ);
  partialQtyToInvoiceByLineKey.value = {
    ...partialQtyToInvoiceByLineKey.value,
    [lineKey]: n,
  };
}

/** Draft qty to invoice for one backend line_key (partial Stripe payload row). */
function draftPartialQtyForLineKey(lineKey: string): number {
  const raw = partialQtyToInvoiceByLineKey.value[lineKey];
  return typeof raw === 'number' ? raw : parseSignedQty(String(raw ?? '0'));
}

/**
 * Remaining qty column: ledger remaining minus current "Qty to invoice" draft for this line.
 * Updates live when the draft changes (same signed rules as partial invoicing).
 */
function remainingQtyForFactorySizeRow(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
  size_idx: number | string,
  sizeQty: unknown,
): number {
  const fpKey = factoryProductKeyForInvoice(factory_product, factoryProductIndex, orderItemRef);
  const lk = buildFactorySizeLineKey(orderItemRef.id, fpKey, size_idx);
  const q = Number(sizeQty) || 0;
  const prior = invoicedQtyForFactorySizeRow(
    orderItemRef,
    factory_product,
    Number(factoryProductIndex),
    Number(size_idx),
  );
  return q - prior - draftPartialQtyForLineKey(lk);
}

function onFactorySizePartialInvoiceInput(
  e: Event,
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
  size_idx: number | string,
  size: any,
) {
  const el = e.target as HTMLInputElement;
  const fixed = sanitizeSignedQuantityTyping(el.value);
  if (fixed !== el.value) el.value = fixed;
  const fpKey = factoryProductKeyForInvoice(factory_product, factoryProductIndex, orderItemRef);
  const lk = buildFactorySizeLineKey(orderItemRef.id, fpKey, size_idx);
  const b = factorySizePartialInvoiceBounds(orderItemRef, factory_product, factoryProductIndex, size_idx, size);
  setPartialQtyToInvoice(lk, el.value, b.min, b.max);
}

/** Pro-rated line $ for a partial invoice qty on a size row. */
function partialDollarForSizeLine(size: Record<string, unknown> | null | undefined, partialQty: number) {
  const oq = Number(size?.quantity) || 0;
  const lineTotal = parseFloat(String(size?.final_net_price_with_quantity ?? 0)) || 0;
  if (oq === 0) {
    return 0;
  }
  return (lineTotal / oq) * partialQty;
}

function partialToInvoiceDollarForFactorySize(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
  size: Record<string, unknown>,
  size_idx: number | string,
) {
  const fpKey = factoryProductKeyForInvoice(factory_product, factoryProductIndex, orderItemRef);
  const lk = buildFactorySizeLineKey(orderItemRef.id, fpKey, size_idx);
  const raw = partialQtyToInvoiceByLineKey.value[lk];
  const pq = typeof raw === 'number' ? raw : parseSignedQty(String(raw ?? '0'));
  return partialDollarForSizeLine(size, pq);
}

function sumInvoicedForFactorySizeLines(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
) {
  const sizes = factory_product?.prices?.sizes;
  if (!Array.isArray(sizes)) {
    return 0;
  }
  return sizes.reduce(
    (acc, _s, i) => acc + invoicedQtyForFactorySizeRow(orderItemRef, factory_product, Number(factoryProductIndex), i),
    0,
  );
}

/** Sum per size row of (ledger remaining − draft qty to invoice). */
function sumRemainingForFactorySizeLines(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
) {
  const sizes = factory_product?.prices?.sizes;
  if (!Array.isArray(sizes)) {
    return 0;
  }
  return sizes.reduce(
    (acc, size, i) =>
      acc +
      remainingQtyForFactorySizeRow(orderItemRef, factory_product, factoryProductIndex, i, size.quantity),
    0,
  );
}

function sumPartialQtyForFactorySizeLines(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
) {
  const fpKey = factoryProductKeyForInvoice(factory_product, factoryProductIndex, orderItemRef);
  const sizes = factory_product?.prices?.sizes;
  if (!Array.isArray(sizes)) {
    return 0;
  }
  return sizes.reduce((acc, _s, i) => {
    const lk = buildFactorySizeLineKey(orderItemRef.id, fpKey, i);
    const raw = partialQtyToInvoiceByLineKey.value[lk];
    const pq = typeof raw === 'number' ? raw : parseSignedQty(String(raw ?? '0'));
    return acc + pq;
  }, 0);
}

function sumPartialAmountForFactorySizeLines(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
) {
  const fpKey = factoryProductKeyForInvoice(factory_product, factoryProductIndex, orderItemRef);
  const sizes = factory_product?.prices?.sizes;
  if (!Array.isArray(sizes)) {
    return 0;
  }
  return sizes.reduce((acc, size, i) => {
    const lk = buildFactorySizeLineKey(orderItemRef.id, fpKey, i);
    const raw = partialQtyToInvoiceByLineKey.value[lk];
    const pq = typeof raw === 'number' ? raw : parseSignedQty(String(raw ?? '0'));
    return acc + partialDollarForSizeLine(size, pq);
  }, 0);
}

function addonLineListTotalDollar(line: any): number {
  const v = line?.final_net_price_with_quantity;
  if (v == null || v === '') return 0;
  return Number(v) || 0;
}

function sumFactoryProductAddonListTotalsDollars(factory_product: any): number {
  const p = factory_product?.prices;
  if (!p) return 0;
  let t = 0;
  for (const x of p.addons || []) t += addonLineListTotalDollar(x);
  for (const x of p.ungrouped_addons || []) t += addonLineListTotalDollar(x);
  const g = p.grouped_addons;
  if (g && typeof g === 'object') {
    for (const k of Object.keys(g)) t += addonLineListTotalDollar((g as Record<string, any>)[k]);
  }
  for (const x of p.logo_technologies || []) t += addonLineListTotalDollar(x);
  return t;
}

/** Garment count for this factory product: sum of `sizes` qty first (roster), else `prices.total_quantity`. */
function factoryProductGarmentOrderTotalQty(factory_product: any): number {
  const sizes = factory_product?.prices?.sizes;
  if (Array.isArray(sizes) && sizes.length > 0) {
    const sum = sizes.reduce((acc, s) => acc + (Number(s?.quantity) || 0), 0);
    if (sum > 0) {
      return sum;
    }
  }
  return parseInt(String(factory_product?.prices?.total_quantity ?? 0), 10) || 0;
}

/**
 * (Sum of "Qty to invoice" on size lines) / (order garment qty) — prorates add-on $ like garment partial batch.
 * Capped so we never prorate past full order.
 */
function partialToInvoiceAddonProration(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
): number {
  const t = factoryProductGarmentOrderTotalQty(factory_product);
  if (t <= 0) {
    return 0;
  }
  const q = sumPartialQtyForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex);
  const sign = q < 0 ? -1 : 1;
  const absQ = Math.abs(q);
  return sign * Math.min(1, absQ / t);
}

/** This add-on row: list $ × (qty to invoice for sizes) / (garment order qty). */
function addonDollarByPartialQtyToInvoice(
  line: any,
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
): number {
  return addonLineListTotalDollar(line) * partialToInvoiceAddonProration(orderItemRef, factory_product, factoryProductIndex);
}

function sumFactoryProductAddonListTotalsProratedByPartialQty(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
): number {
  return (
    sumFactoryProductAddonListTotalsDollars(factory_product) *
    partialToInvoiceAddonProration(orderItemRef, factory_product, factoryProductIndex)
  );
}

/** Subtotal: garment partial (size) $ + all add-on $ at same "qty to invoice" proration. */
function factoryProductPartialPlusAddonListDollars(
  orderItemRef: Record<string, any>,
  factory_product: any,
  factoryProductIndex: number | string,
): number {
  return (
    sumPartialAmountForFactorySizeLines(orderItemRef, factory_product, factoryProductIndex) +
    sumFactoryProductAddonListTotalsProratedByPartialQty(orderItemRef, factory_product, factoryProductIndex)
  );
}

/** Invoiced qty from custom order_line rows (same keys as per-line invoice cells). */
function sumInvoicedFromOrderLines(orderItemRef: Record<string, any>): number {
  const oid = orderItemRef?.id;
  if (oid == null) return 0;
  const lines = normalizeOrderLinesFromApi(orderItemRef?.order_line);
  return lines.reduce((acc, line: Record<string, any>) => {
    if (!line?.id) return acc;
    return acc + invoicedQtyForLineKey(buildOrderLineLineKey(oid, String(line.id)));
  }, 0);
}

/** Draft qty to invoice summed across custom order_line rows. */
function sumPartialQtyFromOrderLines(orderItemRef: Record<string, any>): number {
  const oid = orderItemRef?.id;
  if (oid == null) return 0;
  const lines = normalizeOrderLinesFromApi(orderItemRef?.order_line);
  return lines.reduce((acc, line: Record<string, any>) => {
    if (!line?.id) return acc;
    const lk = buildOrderLineLineKey(oid, String(line.id));
    const raw = partialQtyToInvoiceByLineKey.value[lk];
    const pq = typeof raw === 'number' ? raw : parseSignedQty(String(raw ?? '0'));
    return acc + pq;
  }, 0);
}

function sumRemainingFromOrderLines(orderItemRef: Record<string, any>): number {
  const oid = orderItemRef?.id;
  if (oid == null) return 0;
  const lines = normalizeOrderLinesFromApi(orderItemRef?.order_line);
  return lines.reduce((acc, line: Record<string, any>) => {
    if (!line?.id) return acc;
    const lk = buildOrderLineLineKey(oid, String(line.id));
    const oq = Number(line.quantity) || 1;
    const ledger = oq - invoicedQtyForLineKey(lk);
    return acc + (ledger - draftPartialQtyForLineKey(lk));
  }, 0);
}

/** Pro‑rated $ for partial qty on each custom line (matches per-row order line invoice total cell). */
function partialToInvoiceDollarForOrderLineOnItem(orderItemRef: Record<string, any>, orderLineRow: Record<string, any>): number {
  const oid = orderItemRef?.id;
  if (oid == null || !orderLineRow?.id || orderLineRow.type !== 'add_order_item_line') return 0;
  const lk = buildOrderLineLineKey(oid, String(orderLineRow.id));
  const raw = partialQtyToInvoiceByLineKey.value[lk];
  const pq = typeof raw === 'number' ? raw : parseSignedQty(String(raw ?? '0'));
  const lineTotal = orderLineLineAmount(orderItemRef, orderLineRow);
  const oq = Math.max(1, Number(orderLineRow.quantity) || 1);
  if (oq <= 0) return 0;
  return (lineTotal / oq) * pq;
}

function sumPartialAmountFromOrderLines(orderItemRef: Record<string, any>): number {
  const lines = normalizeOrderLinesFromApi(orderItemRef?.order_line);
  return lines.reduce(
    (acc, line: Record<string, any>) => acc + partialToInvoiceDollarForOrderLineOnItem(orderItemRef, line),
    0,
  );
}

function sumInvoicedForOrderItem(orderItemRef: Record<string, any>): number {
  const fps = orderItemRef?.factory_products;
  let fromSkus = 0;
  if (Array.isArray(fps)) {
    fromSkus = fps.reduce(
      (acc, fp, i) => acc + sumInvoicedForFactorySizeLines(orderItemRef, fp, i),
      0,
    );
  }
  return fromSkus + sumInvoicedFromOrderLines(orderItemRef);
}

function sumRemainingForOrderItem(orderItemRef: Record<string, any>): number {
  const fps = orderItemRef?.factory_products;
  let fromSkus = 0;
  if (Array.isArray(fps)) {
    fromSkus = fps.reduce(
      (acc, fp, i) => acc + sumRemainingForFactorySizeLines(orderItemRef, fp, i),
      0,
    );
  }
  return fromSkus + sumRemainingFromOrderLines(orderItemRef);
}

// Draft "Qty to invoice" total: InvoiceLineKey size rows + order_line only (see custimoo InvoiceLineKey);
// Qty: size + order_line only (add-on rows have no line_key in the partial model; add-on $ is in sumPartialAmountForOrderItem via proration).
function sumPartialQtyForOrderItem(orderItemRef: Record<string, any>): number {
  const fps = orderItemRef?.factory_products;
  let fromSkus = 0;
  if (Array.isArray(fps)) {
    fromSkus = fps.reduce(
      (acc, fp, i) => acc + sumPartialQtyForFactorySizeLines(orderItemRef, fp, i),
      0,
    );
  }
  return fromSkus + sumPartialQtyFromOrderLines(orderItemRef);
}

// Draft partial $ total: size lines + add-on $ prorated by "qty to invoice" per factory product + custom order_line.
function sumPartialAmountForOrderItem(orderItemRef: Record<string, any>): number {
  const fps = orderItemRef?.factory_products;
  let fromSkus = 0;
  let fromAddonsProrated = 0;
  if (Array.isArray(fps)) {
    fromSkus = fps.reduce(
      (acc, fp, i) => acc + sumPartialAmountForFactorySizeLines(orderItemRef, fp, i),
      0,
    );
    fromAddonsProrated = fps.reduce(
      (acc, fp, i) => acc + sumFactoryProductAddonListTotalsProratedByPartialQty(orderItemRef, fp, i),
      0,
    );
  }
  return fromSkus + fromAddonsProrated + sumPartialAmountFromOrderLines(orderItemRef);
}

function onOrderLinePartialInvoiceInput(e: Event, orderLineRow: Record<string, any>) {
  const el = e.target as HTMLInputElement;
  const fixed = sanitizeSignedQuantityTyping(el.value);
  if (fixed !== el.value) el.value = fixed;
  const item0id = order_items?.[0]?.id;
  if (item0id == null || !orderLineRow?.id) return;
  const lk = buildOrderLineLineKey(item0id, String(orderLineRow.id));
  const b = partialQtyBoundsForOrderLine(orderLineRow.quantity, lk);
  setPartialQtyToInvoice(lk, el.value, b.min, b.max);
}

/** Remaining qty for manual order line: ledger (qty − invoiced) minus draft qty to invoice. */
function remainingQtyForStandaloneOrderLine(
  orderLineRow: Record<string, any>,
  orderItemId: string | number | null | undefined,
): string | number {
  if (orderLineRow?.id == null || orderItemId == null) return '—';
  const lk = buildOrderLineLineKey(orderItemId, String(orderLineRow.id));
  const oq = Number(orderLineRow.quantity) || 1;
  const ledger = oq - invoicedQtyForLineKey(lk);
  return ledger - draftPartialQtyForLineKey(lk);
}

/** Custom line invoice table — remaining column as integer or em dash. */
function formatRemainingStandaloneQty(orderLineRow: Record<string, any>): string {
  const v = remainingQtyForStandaloneOrderLine(orderLineRow, order_items?.[0]?.id);
  return v === '—' ? '—' : formatIntegerQty(v as number);
}

function buildPartialLineQuantitiesPayload(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(partialQtyToInvoiceByLineKey.value)) {
    const q = typeof v === 'number' ? v : parseSignedQty(String(v ?? ''));
    if (q !== 0) out[k] = q;
  }
  return out;
}

/** Assign UUIDs on custom order lines so partial keys `oi:{id}:ol:{uuid}` match the API. */
function ensureOrderLineIdsForInvoicing(): void {
  const item0 = order_items?.[0] as Record<string, any> | undefined;
  if (!item0?.order_line) return;
  const r = item0.order_line;
  const lines: any[] = Array.isArray(r)
    ? r
    : r?.type === 'add_order_item_line'
      ? [r]
      : [];
  for (const line of lines) {
    if (line?.type === 'add_order_item_line' && !line.id) {
      line.id = crypto.randomUUID();
    }
  }
}

const orderCustomerCreditsList = computed(() => {
  const raw = order_ref.value?.order_customer_credits;
  return Array.isArray(raw) ? raw : [];
});

/**
 * Task 16180 — credit visualization on Price Breakdown. Sums `order_customer_credits.amount` for
 * one order_item. Used by the grand-totals row to render a strikethrough gross + net amount when
 * any credit notes exist against the item.
 *
 * v1 limitation: per-line strikethrough on Qty / Invoiced is not yet possible because the
 * `il_xxx` → `line_key` mapping is not persisted (would need the Stripe invoice-create response's
 * line ids stored on `order_item_invoice_line_allocations`). Item-level Total strikethrough uses
 * available data only; the full per-row pattern lands once the backend mapping ships.
 */
/** Live invoice rows scoped to ONE order item (the jsonb rows carry no order_item_id field). */
function liveInvoicesForOrderItemId(orderItemId: number | string | null | undefined): Record<string, any>[] {
  const id = Number(orderItemId ?? 0);
  if (!id) return [];
  const item = (order_ref.value?.items ?? []).find((it: Record<string, any>) => Number(it?.id ?? 0) === id);
  if (!item) return [];
  return excludeFailedStripeSyncRows(getItemInvoicesListForOrderItem(item as Record<string, any>));
}

function creditedAmountForOrderItem(orderItemId: number | string | null | undefined): number {
  // Credits nested under LIVE invoice rows only — the flat order_customer_credits table keeps
  // orphan rows from removed invoices forever (audit trail), which over-counted "credited" and
  // clamped the net to 0.00 while the Billing documents ledger disagreed (AB#20717).
  const live = liveCreditTotalsForOrderItem(liveInvoicesForOrderItemId(orderItemId), orderItemId);
  if (live) return Math.max(0, live.amount);

  // Payload predates nested credit_notes (deploy skew) — legacy flat-list fallback.
  const id = Number(orderItemId ?? 0);
  if (!id) return 0;
  const credits = orderCustomerCreditsList.value;
  let sum = 0;
  for (const c of credits) {
    if (Number((c as Record<string, any>)?.order_item_id ?? 0) === id) {
      const amt = Number((c as Record<string, any>)?.amount ?? 0);
      if (Number.isFinite(amt)) sum += amt;
    }
  }
  return Math.max(0, sum);
}

function creditedCountForOrderItem(orderItemId: number | string | null | undefined): number {
  const live = liveCreditTotalsForOrderItem(liveInvoicesForOrderItemId(orderItemId), orderItemId);
  if (live) return live.count;

  const id = Number(orderItemId ?? 0);
  if (!id) return 0;
  const credits = orderCustomerCreditsList.value;
  let n = 0;
  for (const c of credits) {
    if (Number((c as Record<string, any>)?.order_item_id ?? 0) === id) n++;
  }
  return n;
}

/**
 * For manual-only lines: show stored invoice number beside the status pill (Stripe flows unchanged).
 * Reads from the order_item_invoices ledger; the legacy stripe_invoice_* columns on order_items
 * are gone post-cleanup.
 */
const manualInvoiceNumberForStatusStrip = computed((): string => {
  const oi = primaryOrderItem.value;
  const comp = String(oi?.invoice_composite_status ?? '').toLowerCase();
  if (comp !== 'manual') {
    return '';
  }
  for (const inv of partialInvoicesList.value) {
    if (String(inv?.status ?? '').toLowerCase() === 'manual') {
      const n = String(inv?.stripe_invoice_number ?? '').trim();
      if (n !== '') return n;
    }
  }
  return '';
});

/** Whether any factory/manual line on the primary order item (sync endpoint scope) still has qty left to bill. */
const hasBillableRemainingForStripePrimaryItem = computed((): boolean => {
  const item0 = primaryOrderItem.value;
  if (!item0?.id) return false;
  const oid = Number(item0.id);
  const fps = Array.isArray(item0.factory_products) ? item0.factory_products : [];
  for (let fpIndex = 0; fpIndex < fps.length; fpIndex++) {
    const fp = fps[fpIndex];
    const sizes = fp?.prices?.sizes;
    if (!Array.isArray(sizes)) continue;
    for (let szIdx = 0; szIdx < sizes.length; szIdx++) {
      const orderQty = Number(sizes[szIdx]?.quantity) || 0;
      const prior = invoicedQtyForFactorySizeRow(item0, fp, fpIndex, szIdx);
      if (orderQty - prior !== 0) return true;
    }
  }
  const rawOl = item0.order_line;
  const lines: any[] = Array.isArray(rawOl)
    ? rawOl
    : rawOl?.type === 'add_order_item_line'
      ? [rawOl]
      : [];
  for (const line of lines) {
    if (line?.type !== 'add_order_item_line' || !line?.id) continue;
    const lk = buildOrderLineLineKey(oid, String(line.id));
    const q = Math.max(1, Number(line.quantity) || 1);
    const prior = invoicedQtyForLineKey(lk);
    if (q - prior !== 0) return true;
  }
  return false;
});

/** Show billing strip when composite status, partial invoices exist, or user can invoice (helper text). */
const invoiceBillingStripVisible = computed(
  () =>
    !!primaryOrderItem.value?.invoice_composite_status ||
    partialInvoicesList.value.length > 0 ||
    showInvoiceProgressColumns.value,
);

/** Surface missing API data so QA can spot payload issues quickly. */
const invoiceDataDebugBadge = computed(() => {
  const item0 = primaryOrderItem.value;
  if (!item0?.id || !showInvoiceProgressColumns.value) return '';
  const fromItem = item0.item_invoices ?? item0.itemInvoices;
  if (fromItem === undefined || fromItem === null) {
    return 'No item_invoices on order item — refetch order or check API.';
  }
  if (!Array.isArray(fromItem)) {
    return 'item_invoices has unexpected shape.';
  }
  return '';
});

/**
 * Remaining billable qty per line_key for the primary order item (Stripe partial sync shape).
 */
function buildFreshPartialDraftFromRemaining(): Record<string, number> {
  const item0 = order_items?.[0] as Record<string, any> | undefined;
  const next: Record<string, number> = {};
  if (!item0?.id) return next;

  const oid = Number(item0.id);

  const fps = Array.isArray(item0.factory_products) ? item0.factory_products : [];
  fps.forEach((fp: any, fpIndex: number) => {
    const fpKey = factoryProductKeyForInvoice(fp, fpIndex, item0);
    const sizes = fp?.prices?.sizes;
    if (!Array.isArray(sizes)) return;
    sizes.forEach((_sz: any, szIdx: number) => {
      const lk = buildFactorySizeLineKey(oid, fpKey, szIdx);
      const qty = Number(sizes[szIdx]?.quantity) || 0;
      const prior = invoicedQtyForFactorySizeRow(item0, fp, fpIndex, szIdx);
      next[lk] = qty - prior;
    });
  });

  const rawOl = item0.order_line;
  const lines: any[] = Array.isArray(rawOl)
    ? rawOl
    : rawOl?.type === 'add_order_item_line'
      ? [rawOl]
      : [];
  for (const line of lines) {
    if (line?.type !== 'add_order_item_line' || !line?.id) continue;
    const lk = buildOrderLineLineKey(oid, String(line.id));
    const q = Math.max(1, Number(line.quantity) || 1);
    const prior = invoicedQtyByLineKey.value[lk] ?? 0;
    next[lk] = q - prior;
  }

  return next;
}

/**
 * Default "Qty to invoice" = remaining billable qty per backend line_key (matches Stripe partial sync).
 */
function syncPartialDraftFromRemaining(): void {
  if (!showInvoiceProgressColumns.value) return;
  partialQtyToInvoiceByLineKey.value = buildFreshPartialDraftFromRemaining();
}

/**
 * After adding a factory product row or manual order line: fill qty-to-invoice for new line_keys only;
 * keep existing draft cells; drop keys for removed lines.
 */
function mergePartialDraftWithNewLinesFromRemaining(): void {
  if (!showInvoiceProgressColumns.value) return;
  const fresh = buildFreshPartialDraftFromRemaining();
  const prev = partialQtyToInvoiceByLineKey.value;
  const merged: Record<string, number> = {};
  for (const [lk, remaining] of Object.entries(fresh)) {
    merged[lk] = Object.prototype.hasOwnProperty.call(prev, lk) ? prev[lk] : remaining;
  }
  partialQtyToInvoiceByLineKey.value = merged;
}

/** When the first line has a factory, Add Product locks the factory picker to that id. */
const addModalLockedFactoryId = computed((): number | string | null => {
  const id = (order_ref.value.items?.[0] as any)?.factory_id;
  if (id === null || id === undefined) return null;
  if (typeof id === 'string' && id.trim() === '') return null;
  return id;
});

/** Item currency → billing company pricelist → order company pricelist (shared with action menu / order detail). */
const orderItemNetPriceCurrency = computed(() =>
  getOrderItemNetPriceCurrency(
    primaryOrderItem.value as Record<string, unknown> | undefined,
    order_ref.value as Record<string, unknown> | undefined,
  ),
);

/** Empty/missing: undefined, null, or blank string (0 and false are valid). */
const isMissingOrEmptyScalar = (v: unknown): boolean =>
  v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/** Prefer first order_item value, then order, then optional UI fallback. */
const pickOrderItemFirstScalar = <T>(
  itemVal: T | undefined | null,
  orderVal: T | undefined | null,
  uiFallback?: T,
): T | undefined => {
  if (!isMissingOrEmptyScalar(itemVal)) return itemVal as T;
  if (!isMissingOrEmptyScalar(orderVal)) return orderVal as T;
  return uiFallback;
};

const firstOrderItemSnapshot: any = order_items?.[0];
const orderBillingFallback =
  order_ref.value.billing_company_id || order_ref.value.company_id;
const propsBillingFallback =
  props.order?.billing_company_id || props.order?.company_id;

function factoryProductDesignLabel(factory_product: any): string {
  const isManual = order_ref.value.additional_fields?.is_manual_order === true;
  const designId =
    factory_product?.design_id != null && String(factory_product.design_id).trim() !== ''
      ? String(factory_product.design_id)
      : '';
  if (isManual) {
    const nick = factory_product?.design_nick_name;
    if (nick != null && String(nick).trim() !== '') {
      return String(nick).trim();
    }
    return designId;
  }
  const name = factory_product?.design_name;
  if (name != null && String(name).trim() !== '') {
    return String(name).trim();
  }
  return designId;
}

const billingMerchant=ref([]);
const selectedBillingCompany = ref(
  pickOrderItemFirstScalar(
    firstOrderItemSnapshot?.billing_company_id,
    orderBillingFallback,
    propsBillingFallback,
  ) ?? props.order?.company_id,
);
const previousBillingCompany = ref(null);
const billingSelect = ref(null);
/** Avoid clobbering `previousBillingCompany` when reverting selection for save-then-change flow. */
const skipBillingCompanyWatch = ref(false);

/** Resolved merchant label for the header (read-only text). Falls back to item/order API fields when the list is still loading. */
const billingMerchantDisplayName = computed(() => {
  const id = selectedBillingCompany.value;
  const list = (billingMerchant.value ?? []) as {
    id?: number | string;
    company_name?: string;
  }[];
  const found = list.find(
    (m) =>
      m != null &&
      id != null &&
      (m.id === id || String(m.id) === String(id)),
  );
  if (found && typeof found.company_name === 'string' && found.company_name.trim() !== '') {
    return found.company_name.trim();
  }
  const item0 =
    (order_ref.value.items?.[0] as Record<string, unknown> | undefined) ??
    (props.order_item as Record<string, unknown> | undefined);
  const byItem = item0?.billing_company_name;
  if (typeof byItem === 'string' && byItem.trim() !== '') {
    return byItem.trim();
  }
  return '';
});

const internal_note = ref(
  String(
    pickOrderItemFirstScalar(
      firstOrderItemSnapshot?.internal_note,
      order_ref.value.internal_note,
      '',
    ) ?? '',
  ),
);

const invoiceMemoItemSourceInit = pickOrderItemFirstScalar(
  (firstOrderItemSnapshot as any)?.invoice_memo,
  undefined,
  !isMissingOrEmptyScalar(lastMemoFromItemInvoicesOnItem(firstOrderItemSnapshot))
    ? lastMemoFromItemInvoicesOnItem(firstOrderItemSnapshot)
    : undefined,
);
const invoice_memo = ref(String(invoiceMemoItemSourceInit ?? ''));

watch(selectedBillingCompany, (newVal, oldVal) => {
  if (skipBillingCompanyWatch.value) return;
  previousBillingCompany.value = oldVal;
});

function revertBillingCompanySelection(toId: unknown): void {
  skipBillingCompanyWatch.value = true;
  selectedBillingCompany.value = toId;
  skipBillingCompanyWatch.value = false;
}

async function handleBillingMerchantUpdate() {
  if (billingMerchantSelectDisabled.value) {
    showToast('Cannot change billing merchant for orders with existing invoices.', 'error');
    revertBillingCompanySelection(previousBillingCompany.value);
    return;
  }

  const targetBillingCompanyId = selectedBillingCompany.value;
  const priorBillingCompanyId =
    previousBillingCompany.value ??
    primaryOrderItem.value?.billing_company_id ??
    order_ref.value.billing_company_id ??
    order_ref.value.company_id;
  if (
    targetBillingCompanyId == null ||
    String(targetBillingCompanyId) === String(priorBillingCompanyId)
  ) {
    return;
  }

  const result = await Swal.fire({
    title: 'Are you sure?',
    text: 'Unsaved price changes will be saved first, then the billing merchant will be updated (including currency). Continue?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, continue',
    cancelButtonText: 'No',
  });

  if (!result.isConfirmed) {
    revertBillingCompanySelection(priorBillingCompanyId);
    billingSelect.value?.$el?.blur();
    return;
  }

  billingSelect.value?.$el?.blur();

  const orderItemId = primaryOrderItem.value?.id ?? order_items?.[0]?.id;
  if (orderItemId == null) {
    showToast('Order item id is missing; cannot update billing merchant.', 'error');
    revertBillingCompanySelection(priorBillingCompanyId);
    return;
  }

  loading.value = true;
  try {
    // Revert dropdown so Save runs under the current billing merchant (same as footer Save button).
    revertBillingCompanySelection(priorBillingCompanyId);

    const saved = await updateOrderPrices(false, {
      showSuccessToast: false,
      refreshAfter: false,
      notifyParent: false,
      manageLoading: false,
    });
    if (!saved) {
      revertBillingCompanySelection(priorBillingCompanyId);
      return;
    }

    revertBillingCompanySelection(targetBillingCompanyId);

    const successResponse = await http.post(
      `update/order/${order_ref.value.id}/billing_merchant`,
      {
        order_item_id: orderItemId,
        billing_company_id: targetBillingCompanyId,
        billing_company_name:
          billingMerchant.value.find(
            (c) => c.id === targetBillingCompanyId || String(c.id) === String(targetBillingCompanyId),
          )?.company_name || null,
      },
    );
    showToast(successResponse.data?.message ?? 'Billing merchant updated');
    await refreshPriceBreakdownFromApi();
    notifyParentOrderDataUpdated();
  } catch (error) {
    revertBillingCompanySelection(priorBillingCompanyId);
    handleResponseException(error as AxiosError);
  } finally {
    clearPriceModalLoaders();
  }
}

const editingInvoiceNo = ref(false);
const invoiceNoInput = ref(''); // Used for both add and edit
const savingInvoice = ref(false);

const saveInvoiceNumber = async () => {
  if (!invoiceNoInput.value || !invoiceNoInput.value.toString().trim()) return;
  const lineItem: any = order_items?.[0];
  if (lineItem?.id == null) {
    showToast('Order item id is missing; cannot save invoice number.', 'error');
    return;
  }
  savingInvoice.value = true;
  try {
    const payload = { stripe_invoice_number: invoiceNoInput.value.toString().trim() };
    const response = await http.put(
      `order/stripe-invoice-no/${lineItem.id}`,
      payload,
    );
    const resData = response.data;
    const returnedInvoiceNo = resData?.result?.stripe_invoice_number ?? invoiceNoInput.value;
    if (lineItem) {
      // Bug 20222 minor: use the same failed-excluding view as the read side, so the number
      // never lands on a hidden `failed` row (which would leave the header showing stale data).
      const list = excludeFailedStripeSyncRows(getItemInvoicesListForOrderItem(lineItem));
      if (list.length) {
        const last = list[list.length - 1] as Record<string, unknown>;
        if (returnedInvoiceNo) last.stripe_invoice_number = returnedInvoiceNo;
      }
    }
    editingInvoiceNo.value = false;
    invoiceNoInput.value = '';
    showToast(resData?.message ?? 'Invoice number updated', 'success');
    notifyParentOrderDataUpdated();
  } catch (err) {
    const axiosErr = err as AxiosError<{ errors?: { message?: string; order_item_invoice?: Record<string, any> } }>;
    // Bug 20222: backend now rejects (422) when the latest ledger row is `pending_stripe_sync`
    // (previously overwrote silently) — surface the backend message instead of the generic handler.
    if (axiosErr.response?.status === 422 && axiosErr.response.data?.errors?.order_item_invoice) {
      showToast(
        appendManualReviewReasonForToast(
          escapeHtmlForToast(axiosErr.response.data.errors.message ?? 'An invoice sync is already in progress for this item.'),
          axiosErr.response.data.errors.order_item_invoice,
        ),
        'error',
      );
      // Contain a refetch failure (e.g. API fully down) — the toast above already fired.
      await refreshPriceBreakdownFromApi().catch(() => undefined);
    } else {
      handleResponseException(err);
    }
  } finally {
    savingInvoice.value = false;
  }
};

const startEditingInvoiceNo = () => {
  editingInvoiceNo.value = true;
  invoiceNoInput.value = headerLatestOrderItemInvoice.value?.stripe_invoice_number ?? '';
}




/** Empty factory id on an order item (null / undefined / blank). */
const isOrderItemFactoryIdMissing = (factoryId: unknown): boolean =>
  factoryId == null || (typeof factoryId === 'string' && factoryId.trim() === '');

const factoryIdsEqual = (a: unknown, b: unknown): boolean => {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return String(a) === String(b);
};

/**
 * Block Create Invoice: unsaved line items, or multi-item orders with any missing factory.
 * Toasts on every blocked attempt (caller must not proceed).
 */
const validateCreateInvoiceOrToast = (): boolean => {
  const items = order_items;
  if (!items.length) return true;

  // Bug 20222: double-fire guard — a sync is already in flight/awaiting reconciliation for this item.
  if (hasPendingStripeSyncForPrimaryItem.value) {
    showToast(
      appendManualReviewReasonForToast(
        pendingStripeSyncRequiresManualReview.value
          ? 'The invoice may have been sent but could not be confirmed — it is flagged for manual review. Do NOT re-send.'
          : 'An invoice sync is already in progress for this item.',
        pendingStripeSyncRowForPrimaryItem.value,
      ),
      'error',
    );
    return false;
  }

  const nullFactoryMessage =
    'Factory must be assigned to all products before invoice creation.';

  const first: any = items[0];
  const refFactoryId = first?.factory_id;

  const unsavedRows = items.filter((i: any) => i.id == null);
  for (const row of unsavedRows) {
    if (!factoryIdsEqual(row.factory_id, refFactoryId)) {
      showToast(
        'You selected a product with a different factory. Please save first, then create the invoice.',
        'error',
      );
      return false;
    }
  }
  if (items.some((i: any) => isOrderItemFactoryIdMissing(i.factory_id))) {
    showToast(nullFactoryMessage, 'error');
    return false;
  }

  return true;
};

const onCreateInvoiceClick = () => {
  if (can('update-price-breakdown')) {
    void updateOrderPrices(true);
  } else {
    void handleStripeSync();
  }
};

/**
 * Wrapper used by the dropdown menu's "Create Invoice" entry — closes the split-button menu
 * before delegating to the same handler as the primary button click.
 */
function onCreateInvoiceFromMenu(): void {
  splitButtonOpen.value = false;
  onCreateInvoiceClick();
}

/**
 * Credit-note flow entry point (US 5253 / Task 16169). Branches by how many Stripe invoices
 * the order has:
 *   - 0 → toast and exit (menu item is also disabled visually).
 *   - 1 → fetch eligibility for that invoice; on success open the wizard, otherwise surface the
 *         disable reason.
 *   - 2+ → open the picker modal which lazy-loads eligibility for each invoice in parallel.
 *
 * Wizard UI itself is the Phase 3 stub (`CreateStripeCreditNoteModal`) and ships properly under
 * Task 16178. This task only delivers the navigation surface.
 */
async function onCreateCreditNoteClick(): Promise<void> {
  splitButtonOpen.value = false;
  const invoices = creditableInvoicesList.value;
  if (invoices.length === 0) {
    showToast('No Stripe invoices on this order to credit.', 'info');
    return;
  }

  if (invoices.length === 1) {
    const inv = invoices[0];
    creditFlowLoading.value = true;
    try {
      const eligibility = await fetchInvoiceCreditEligibility(
        Number(order_ref.value?.id ?? 0),
        Number(inv.id),
      );
      if (!eligibility.can_credit) {
        const reason = disableReasonLabel(eligibility.disable_reason)
          || 'Invoice is not eligible for a credit note.';
        showToast(reason, 'error');
        return;
      }
      selectedInvoiceForCn.value = inv;
      selectedEligibilityForCn.value = eligibility;
      creditNoteWizardOpen.value = true;
    } catch {
      // handleResponseException already toasted; nothing more to do.
    } finally {
      creditFlowLoading.value = false;
    }
    return;
  }

  invoicePickerOpen.value = true;
}

function onInvoicePickedForCreditNote(
  payload: { invoice: Record<string, any>; eligibility: CreditNoteEligibility },
): void {
  selectedInvoiceForCn.value = payload.invoice;
  selectedEligibilityForCn.value = payload.eligibility;
  creditNoteWizardOpen.value = true;
}

/**
 * AB#20717 round 2 fix #4 — optimistically append the just-created credit note to the target
 * invoice's `credit_notes[]` on every order item that carries that invoice row (usually one),
 * writing the new array back onto the item so the reactive `partialInvoicesList` computed (and
 * everything it feeds — chip pills, `BillingDocumentsLedger`) picks it up instantly, with no
 * modal close/reopen and no wizard state-machine changes.
 */
function applyOptimisticCreditNoteAppend(
  invoiceId: number | string | null | undefined,
  creditRow: Record<string, unknown>,
): void {
  if (invoiceId === null || invoiceId === undefined) return;
  for (const it of order_ref.value?.items ?? []) {
    const item = it as Record<string, any>;
    const list = getItemInvoicesListForOrderItem(item);
    if (!list.length) continue;
    const updated = appendCreditNoteToInvoice(list, invoiceId, creditRow);
    if (updated === list) continue;
    item.item_invoices = updated;
    if (Array.isArray(item.itemInvoices)) item.itemInvoices = updated;
  }
}

function onCreditNoteCreated(payload: Record<string, unknown>): void {
  // Phase 3 (Task 16178) — wizard emits this on successful creation. Merge the new credit row
  // into the local order so the dashboard reflects it immediately, then trigger the parent
  // refresh (price_info / item_invoices / composite status follow through fetchOnSave).
  const creditRow = payload && typeof payload === 'object' && payload.credit && typeof payload.credit === 'object'
    ? (payload.credit as Record<string, unknown>)
    : payload;
  // Captured before the selection refs are cleared below — this is the invoice the wizard was
  // opened against, i.e. the one the new credit note applies to.
  const targetInvoiceId = selectedInvoiceForCn.value?.id ?? null;
  if (creditRow && typeof creditRow === 'object') {
    if (!Array.isArray(order_ref.value.order_customer_credits)) {
      order_ref.value.order_customer_credits = [];
    }
    order_ref.value.order_customer_credits.unshift(creditRow);
    applyOptimisticCreditNoteAppend(targetInvoiceId, creditRow);
  }
  creditNoteWizardOpen.value = false;
  selectedInvoiceForCn.value = null;
  selectedEligibilityForCn.value = null;
  if (targetInvoiceId !== null && targetInvoiceId !== undefined) {
    billingLedgerRef.value?.invalidateInvoiceEligibility(targetInvoiceId);
  }
  notifyParentOrderDataUpdated();
}

const buildNewOrderItemFromPayload = (payload: any) => {
  const factoryId = payload.factory_id;
  return {
    ...payload,
    id: null,
    status: factoryId ? 'order_approve' : 'submitted_for_factory_review',
    order_id: order_ref.value.id,
    order: {
      id: order_ref.value.id,
      order_no: order_ref.value.order_no,
      order_type_detail: order_ref.value.order_type_detail,
      order_type_symbol: order_ref.value.order_type_symbol,
      order_type_default: order_ref.value.order_type_default,
      company_id: order_ref.value.company_id,
      customer_id: order_ref.value.customer_id,
    },
  };
};

/** Add Item uses the first order line’s factory as the reference (not “any matching factory” row). */
const addFactoryProduct = (payload: any) => {
  const firstItem: any = order_items[0];
  if (!firstItem) return;

  const newProduct = payload.factory_products[0];
  const payloadFactoryId = payload.factory_id;
  const refFactoryId = firstItem.factory_id;

  const bothFactoriesMissing =
    isOrderItemFactoryIdMissing(payloadFactoryId) &&
    isOrderItemFactoryIdMissing(refFactoryId);
  const sameFactory = factoryIdsEqual(payloadFactoryId, refFactoryId);

  if (bothFactoriesMissing || sameFactory) {
    firstItem.factory_products.push(newProduct);
  } else {
    const existingRowSameFactory = order_items.find((item: any, index: number) => {
      if (index === 0) return false;
      return factoryIdsEqual(item?.factory_id, payloadFactoryId);
    });
    if (existingRowSameFactory) {
      existingRowSameFactory.factory_products.push(newProduct);
    } else {
      order_items.push(buildNewOrderItemFromPayload(payload));
    }
  }

  calculateTotalPrice();
  mergePartialDraftWithNewLinesFromRemaining();
};

const paymentStatuses = [
  {
    status: "draft",
    description: "Newly created, not finalized",
    icon: FileText,
    color: "bg-gray-100 text-gray-600"
  },
  {
    status: "open",
    description: "Finalized, awaits payment",
    icon: Clock3,
    color: "bg-blue-100 text-blue-600"
  },
  {
    status: "paid",
    description: "Fully paid",
    icon: BadgeCheck,
    color: "bg-green-100 text-green-600"
  },
  {
    status: "void",
    description: "Voided, no payment required",
    icon: Trash2,
    color: "bg-gray-100 text-gray-600"
  },
  {
    status: "uncollectable",
    description: "Marked as uncollectable",
    icon: CircleX,
    color: "bg-red-100 text-red-600"
  },
  {
    status: "overdue",
    description: "Past due date, unpaid",
    icon: TriangleAlert,
    color: "bg-orange-100 text-orange-500"
  },
  {
    status: "manual",
    description: "Manual invoice",
    icon: SquareM,
    color: "bg-yellow-100 text-orange-500"
  },
  {
    /** Bug 20222 — sync in flight or awaiting reconciliation; a follow-up attempt is blocked until resolved. */
    status: "pending_stripe_sync",
    description: "Invoice sync in progress — verify before re-sending",
    icon: Clock3,
    color: "bg-amber-100 text-amber-700"
  }
]

const statusObjects = computed(() =>
  paymentStatuses.filter(
    s => s.status === headerLatestOrderItemInvoice.value?.status,
  ),
);

const DEFAULT_ORDER_TYPE: OrderType =
  orderTypes.find((t) => t.label === 'Order (with invoice)') ??
  orderTypes.find((t) => t.default === true) ??
  orderTypes[0];

const selectedOrderType = ref<OrderType | null>(null);

/** Match API fields to a Select option; default to Order (with invoice) when unset. */
function resolveSelectedOrderTypeFromOrder(
  item0?: Record<string, any> | null,
  order?: Record<string, any> | null,
): OrderType {
  const label = String(
    pickOrderItemFirstScalar(item0?.order_type_detail, order?.order_type_detail, '') ?? '',
  ).trim();
  const symbol = String(
    pickOrderItemFirstScalar(item0?.order_type_symbol, order?.order_type_symbol, '') ?? '',
  ).trim();

  if (!label && !symbol) {
    return DEFAULT_ORDER_TYPE;
  }

  const match =
    orderTypes.find((t) => t.label === label && (!symbol || t.symbol === symbol)) ??
    orderTypes.find((t) => t.symbol === symbol) ??
    orderTypes.find((t) => t.label === label);

  return match ?? DEFAULT_ORDER_TYPE;
}

function handleOrderTypeUpdate(order_type: OrderType) {
  selectedOrderType.value = order_type;
}
const errorMessages = reactive<Record<string, string>>({});

//order discount and shipping starts
const discount_shipping_info = ref<any>({
  is_fixed: false,
  fixed_value: 0.0,
  discount: 0.0,
  shipping: 0.0,
  total_price: 0,
  total_quantity: 0,
  order_final_price: 0,
});

const getFactoryProductTotal = (factoryProduct: any): number => {
  const prices = factoryProduct?.prices ?? {};
  let total = 0;

  (prices.sizes ?? []).forEach((size: any) => {
    total += parseFloat(size?.final_net_price_with_quantity ?? 0);
  });
  (prices.addons ?? []).forEach((addon: any) => {
    total += parseFloat(addon?.final_net_price_with_quantity ?? 0);
  });
  (prices.ungrouped_addons ?? []).forEach((addon: any) => {
    total += parseFloat(addon?.final_net_price_with_quantity ?? 0);
  });
  Object.values(prices.grouped_addons ?? {}).forEach((groupedAddon: any) => {
    total += parseFloat(groupedAddon?.final_net_price_with_quantity ?? 0);
  });
  (prices.logo_technologies ?? []).forEach((logoTechnology: any) => {
    total += parseFloat(logoTechnology?.final_net_price_with_quantity ?? 0);
  });
  // Custom `order_item.order_line` is its own row; not included in factory_product_total_price

  return total;
};

/** Sum of factory product roster/addon totals only (excludes `order_line`). */
const orderItemProductsSubtotalOnly = (orderItem: any): number => {
  let s = 0;
  (orderItem?.factory_products ?? []).forEach((fp: any) => {
    s += getFactoryProductTotal(fp);
  });
  return s;
};

/** Recompute each `factory_product_total_price` from sizes/addons (run before `order_line` %). */
const recalculateFactoryProductTotalsForOrderItem = (orderItem: any) => {
  (orderItem?.factory_products ?? []).forEach((factory_product: any) => {
    syncFactoryProductRosterTotalAndAddonDollars(factory_product);
    let total = 0;
    factory_product.prices.sizes.forEach((size: any) => {
      total += parseFloat(size.final_net_price_with_quantity);
    });
    factory_product.prices.addons.forEach((addon: any) => {
      total += parseFloat(addon.final_net_price_with_quantity);
    });
    factory_product.prices.ungrouped_addons.forEach((ungrouped_addon: any) => {
      total += parseFloat(ungrouped_addon.final_net_price_with_quantity);
    });
    Object.values(factory_product.prices.grouped_addons).forEach((grouped_addon: any) => {
      total += parseFloat(grouped_addon.final_net_price_with_quantity);
    });
    factory_product.prices.logo_technologies.forEach((logo_technology: any) => {
      total += parseFloat(logo_technology.final_net_price_with_quantity);
    });
    factory_product.prices.factory_product_total_price = total;
  });
};

/** Money: always two fractional digits for display and stored strings (e.g. 10.34). */
function formatMoney(value: string | number): string {
  if (value === null || value === undefined || value === '') return '0.00';
  const normalized = String(value).replace(',', '.');
  const num = parseFloat(normalized);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2);
}

/** Order line price / percentage: same 2 d.p. as roster money. */
const normalizeOrderLineDecimal = formatMoney;

const formatOrderLineNumber = formatMoney;

/** Quantity shown in tables (no decimals); supports negative corrections. */
function formatIntegerQty(value: unknown): string {
  const s = String(value ?? '').trim();
  const neg = s.startsWith('-');
  const digits = s.replace(/\D/g, '');
  const n = parseInt(digits, 10);
  if (Number.isNaN(n)) return '0';
  return (neg ? '-' : '') + String(n);
}

/** Price inputs: digits, optional leading minus, one decimal point, up to 2 decimal places. */
function sanitizePriceTyping(raw: string): string {
  let s = String(raw).replace(',', '.');
  let neg = false;
  if (s.startsWith('-')) {
    neg = true;
    s = s.slice(1);
  }
  let out = '';
  let dot = false;
  for (const ch of s) {
    if (ch >= '0' && ch <= '9') out += ch;
    else if (ch === '.' && !dot) {
      out += ch;
      dot = true;
    }
  }
  const di = out.indexOf('.');
  if (di === -1) return (neg ? '-' : '') + out;
  const intPart = out.slice(0, di);
  const frac = out
    .slice(di + 1)
    .replace(/\D/g, '')
    .slice(0, 2);
  const trailingDot = out.endsWith('.') && frac === '';
  return (neg ? '-' : '') + intPart + (trailingDot ? '.' : frac !== '' ? `.${frac}` : '');
}

function onPriceInputSanitize(e: Event) {
  const el = e.target as HTMLInputElement;
  const fixed = sanitizePriceTyping(el.value);
  if (fixed !== el.value) el.value = fixed;
}

/** Quantity inputs: digits only (integers). */
function sanitizeQuantityDigits(raw: string): string {
  return String(raw).replace(/\D/g, '');
}

/** Partial invoice qty: optional leading minus, then digits. */
function sanitizeSignedQuantityTyping(raw: string): string {
  let s = String(raw);
  const neg = s.startsWith('-');
  const digits = s.replace(/\D/g, '');
  if (digits === '' && !neg) return '';
  if (digits === '') return '-';
  return (neg ? '-' : '') + digits;
}

function parseSignedQty(raw: string): number {
  const s = String(raw).trim();
  if (s === '' || s === '-') return 0;
  const neg = s.startsWith('-');
  const digits = s.replace(/\D/g, '');
  if (digits === '') return 0;
  const v = parseInt(digits, 10) || 0;
  return neg ? -v : v;
}

function clampInt(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function onQuantityInputSanitize(e: Event) {
  const el = e.target as HTMLInputElement;
  const d = sanitizeQuantityDigits(el.value);
  if (d !== el.value) el.value = d;
}

/** Sum of fixed (`qty × unit price`) manual lines only — base input for the single % line (no % on %). */
const orderItemFixedOrderLinesSubtotal = (orderItem: any): number => {
  const lines = normalizeOrderLinesFromApi(orderItem?.order_line);
  let s = 0;
  for (const line of lines) {
    if (!line || line.type !== 'add_order_item_line') continue;
    if (!orderLineUsesFixedPricing(line)) continue;
    const q = Number(line.quantity) || 0;
    const p = parseFloat(String(line.price ?? 0));
    if (Number.isNaN(p)) continue;
    s += q * p;
  }
  return parseFloat(normalizeOrderLineDecimal(s));
};

/**
 * One custom row amount (2 d.p.).
 * Percentage line: `pct` of (products + addons subtotal + all fixed-amount manual lines) — matches QA “order subtotal × (p/100)” as the line amount, with order total = that subtotal + line when only % rows are variable.
 */
const orderLineLineAmount = (orderItem: any, line: any): number => {
  if (!line || line.type !== 'add_order_item_line') return 0;
  const q = Number(line.quantity) || 0;
  if (!orderLineUsesFixedPricing(line)) {
    const pct = parseFloat(String(line.percentage ?? 0));
    const base =
      orderItemProductsSubtotalOnly(orderItem) + orderItemFixedOrderLinesSubtotal(orderItem);
    const amount = (base * (Number.isNaN(pct) ? 0 : pct)) / 100;
    return parseFloat(normalizeOrderLineDecimal(amount));
  }
  const p = parseFloat(String(line.price ?? 0));
  const amount = q * (Number.isNaN(p) ? 0 : p);
  return parseFloat(normalizeOrderLineDecimal(amount));
};

/** Sum of all `add_order_item_line` rows on the order item; not part of SKU subtotals. */
const orderLineContribution = (orderItem: any): { amount: number; quantity: number } => {
  const lines = normalizeOrderLinesFromApi(orderItem?.order_line);
  let amount = 0;
  let quantity = 0;
  for (const line of lines) {
    amount += orderLineLineAmount(orderItem, line);
    quantity += Number(line.quantity) || 0;
  }
  return { amount: parseFloat(normalizeOrderLineDecimal(amount)), quantity };
};

/**
 * List total (merchandise + all manual lines, full qty). Must be ≥ 0.
 * `lineIndex` excludes that line from "other" manual amounts when computing the floor for the edited line.
 */
function minAllowedContributionForOrderLineAt(orderItem: any, lineIndex: number): number {
  recalculateFactoryProductTotalsForOrderItem(orderItem);
  const P = orderItemProductsSubtotalOnly(orderItem);
  const lines = normalizeOrderLinesFromApi(orderItem?.order_line);
  let others = 0;
  for (let i = 0; i < lines.length; i++) {
    if (i === lineIndex) continue;
    others += orderLineLineAmount(orderItem, lines[i]);
  }
  return parseFloat(normalizeOrderLineDecimal(-(P + others)));
}

function minAllowedFixedUnitPriceForList(orderItem: any, lineIndex: number, quantity: number): number {
  const q = Math.max(1, Number(quantity) || 1);
  const minContrib = minAllowedContributionForOrderLineAt(orderItem, lineIndex);
  return parseFloat(normalizeOrderLineDecimal(minContrib / q));
}

/** Partial-invoice $ from SKU/add-ons + other manual lines only (excludes one manual line). Used with qty-to-invoice draft. */
function partialMerchandiseAndOtherManualLines(orderItemRef: Record<string, any>, excludeOrderLineIndex: number): number {
  const fps = orderItemRef?.factory_products;
  let fromSkus = 0;
  let fromAddonsProrated = 0;
  if (Array.isArray(fps)) {
    fromSkus = fps.reduce(
      (acc, fp, i) => acc + sumPartialAmountForFactorySizeLines(orderItemRef, fp, i),
      0,
    );
    fromAddonsProrated = fps.reduce(
      (acc, fp, i) => acc + sumFactoryProductAddonListTotalsProratedByPartialQty(orderItemRef, fp, i),
      0,
    );
  }
  const lines = normalizeOrderLinesFromApi(orderItemRef?.order_line);
  let ol = 0;
  for (let j = 0; j < lines.length; j++) {
    if (j === excludeOrderLineIndex) continue;
    ol += partialToInvoiceDollarForOrderLineOnItem(orderItemRef, lines[j]);
  }
  return parseFloat(normalizeOrderLineDecimal(fromSkus + fromAddonsProrated + ol));
}

function draftPartialQtyForOrderLineRow(
  orderItemRef: Record<string, any>,
  orderLineRow: Record<string, any>,
): number {
  const oid = orderItemRef?.id;
  if (oid == null || !orderLineRow?.id) return 0;
  const lk = buildOrderLineLineKey(oid, String(orderLineRow.id));
  const raw = partialQtyToInvoiceByLineKey.value[lk];
  return typeof raw === 'number' ? raw : parseSignedQty(String(raw ?? '0'));
}

/**
 * Floor unit price (most negative allowed) so partial draft total (right-hand invoice column) stays ≥ 0
 * for fixed lines: contribution on invoice is unit × pq, need unit × pq ≥ −R.
 */
function minAllowedFixedUnitPriceForPartialDraft(
  orderItemRef: Record<string, any>,
  lineIndex: number,
  quantity: number,
): number {
  const lines = normalizeOrderLinesFromApi(orderItemRef?.order_line);
  const line = lines[lineIndex];
  if (!line || !orderLineUsesFixedPricing(line)) return Number.NEGATIVE_INFINITY;
  const pq = draftPartialQtyForOrderLineRow(orderItemRef, line);
  if (pq === 0) return Number.NEGATIVE_INFINITY;
  const R = partialMerchandiseAndOtherManualLines(orderItemRef, lineIndex);
  return parseFloat(normalizeOrderLineDecimal(-R / pq));
}

/** Clamp fixed unit price using both full-list cap and partial–qty-to-invoice cap (whichever is stricter). */
function clampFixedOrderLineUnitPriceToCap(
  orderItem: any,
  lineIndex: number,
  proposedUnit: number,
  quantity: number,
): number {
  const listFloor = minAllowedFixedUnitPriceForList(orderItem, lineIndex, quantity);
  const partialFloor = minAllowedFixedUnitPriceForPartialDraft(orderItem, lineIndex, quantity);
  const floor = parseFloat(normalizeOrderLineDecimal(Math.max(listFloor, partialFloor)));
  const n = Number.isFinite(proposedUnit) ? proposedUnit : 0;
  return parseFloat(normalizeOrderLineDecimal(Math.max(n, floor)));
}

function orderItemListTotalBeforeDiscount(orderItem: any): number {
  if (!orderItem) return 0;
  recalculateFactoryProductTotalsForOrderItem(orderItem);
  const p = orderItemProductsSubtotalOnly(orderItem);
  const ol = orderLineContribution(orderItem).amount;
  const raw = parseFloat(normalizeOrderLineDecimal(p + ol));
  return Number.isFinite(raw) ? raw : 0;
}

function orderItemListTotalNonNegative(orderItem: any): boolean {
  return orderItemListTotalBeforeDiscount(orderItem) >= -0.01;
}

function partialInvoiceDraftTotalNonNegative(orderItemRef: Record<string, any>): boolean {
  return sumPartialAmountForOrderItem(orderItemRef) >= -0.01;
}

/** Roster qty + custom order line qty (for price_info.total_quantity). */
const sumOrderItemTotalQuantity = (orderItem: any): number => {
  let q = orderLineContribution(orderItem).quantity;
  (orderItem?.factory_products ?? []).forEach((fp: any) => {
    q += parseInt(String(fp?.prices?.total_quantity ?? 0), 10) || 0;
  });
  return q;
};

/** Ensure first line has a computed price_info when API sends it as null. */
const ensureFirstOrderItemPriceInfo = () => {
  const firstOrderItem: any = order_items?.[0];
  if (!firstOrderItem || firstOrderItem.price_info) return;

  // Same pipeline as calculateTotalPrice: factory totals → products subtotal → order_line → price_info.
  recalculateFactoryProductTotalsForOrderItem(firstOrderItem);
  const productsSubtotal = orderItemProductsSubtotalOnly(firstOrderItem);
  syncOrderLineTotalPrice(firstOrderItem);
  const rawList = productsSubtotal + orderLineContribution(firstOrderItem).amount;
  const totalPrice = Math.max(0, parseFloat(normalizeOrderLineDecimal(rawList)) || 0);
  const totalQuantity = sumOrderItemTotalQuantity(firstOrderItem);

  firstOrderItem.price_info = {
    total_price: totalPrice,
    total_quantity: totalQuantity,
    discount: 0,
    is_fixed: false,
    shipping: 0,
    fixed_value: 0,
    order_item_final_price: totalPrice,
    order_final_price: totalPrice,
    total_final_price: totalPrice,
  };
};

const activeOrderItemPriceInfo = computed(() => {
  return order_items?.[0]?.price_info ?? {};
});

const isFixedForUI = computed(() => {
  const v = activeOrderItemPriceInfo.value?.is_fixed;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
  return false;
});

const discount_amount = computed(() => {
  // Order-level discount never reduces `order_line`: base = factory products only (matches backend).
  const discount = activeOrderItemPriceInfo.value?.discount
    ? parseFloat(activeOrderItemPriceInfo.value?.discount)
    : 0;
  const item0 = order_items?.[0];
  const productsSubtotal = orderItemProductsSubtotalOnly(item0);
  return (productsSubtotal * discount) / 100;
});

const finalPriceForUI = computed(() => {
  const pi = activeOrderItemPriceInfo.value ?? {};
  const item0 = order_items?.[0];
  const productsSubtotal = orderItemProductsSubtotalOnly(item0);
  const orderLineAmt = orderLineContribution(item0).amount;
  const shipping = pi.shipping ? parseFloat(pi.shipping) : 0;

  // Final = discounted products + full order_line (line not discounted) + shipping.
  let afterDiscountProducts = productsSubtotal;
  if (isFixedForUI.value) {
    const fixedValue = pi.fixed_value ? parseFloat(pi.fixed_value) : 0;
    afterDiscountProducts = Math.max(0, productsSubtotal - fixedValue);
  } else {
    const discount = pi.discount ? parseFloat(pi.discount) : 0;
    const discountAmount = (productsSubtotal * discount) / 100;
    afterDiscountProducts = Math.max(0, productsSubtotal - discountAmount);
  }

  const raw = afterDiscountProducts + orderLineAmt + shipping;
  return Math.max(0, parseFloat(normalizeOrderLineDecimal(raw)) || 0);
});
//order discount and shipping ends

const emit = defineEmits(['close', 'openOrderPriceModal', 'fetchOnSave', 'passiveOrderDataRefreshed']);

/** Refresh dashboard list + order detail pane (parent `fetchOnSave` / `onFetch`) — user-action triggered. */
function notifyParentOrderDataUpdated(): void {
  emit('fetchOnSave', true);
}

/**
 * AB#20223 follow-up (BLOCKER 1) — passive, on-open background-refresh notify. Must NEVER route
 * through `fetchOnSave`: `DashboardActions.onFetch` (the context-menu host's `@fetchOnSave` handler)
 * synchronously emits `closeMenu`, which unmounts the context-menu host and this modal with it —
 * self-closing the modal ~1s after a right-click open. This distinct event lets each host update its
 * own cached data without unmounting anything.
 */
function notifyParentOrderDataRefreshedPassively(): void {
  emit('passiveOrderDataRefreshed');
}
const priceBreakdownModal = ref<boolean>(false);
const shippingAndDiscountModal = ref<boolean>(false);
const addProductModal = ref<boolean>(false);
/** Add-only modal (existing lines are edited inline in the listing). */
const addOrderLineModal = ref(false);
const addOrderLineForm = reactive({
  name: '',
  quantity: 1 as number,
  price: '' as string | number,
  is_fixed: true,
  percentage: '' as string | number,
});

/** Live list of custom lines on the first order item (array or legacy single object). */
const standaloneOrderLines = computed(() => {
  const item0 = primaryOrderItem.value;
  if (!item0?.order_line) return [];
  const r = item0.order_line;
  if (Array.isArray(r)) return r;
  if (r?.type === 'add_order_item_line') return [r];
  return [];
});

function applyOrderLineNameAliasesForFirstItem(): void {
  const item0 = order_items?.[0] as Record<string, any> | undefined;
  if (!item0?.order_line) return;
  const raw = item0.order_line;
  const lines: any[] = Array.isArray(raw)
    ? raw
    : raw?.type === 'add_order_item_line'
      ? [raw]
      : [];
  for (const line of lines) {
    ensureOrderLineName(line);
  }
}

const standaloneOrderLineAmountAt = (index: number) => {
  const item0 = order_items?.[0];
  const line = standaloneOrderLines.value[index];
  if (!item0 || !line) return 0;
  return orderLineLineAmount(item0, line);
};

/** Pro‑rated partial invoice $ for a custom order line row (aligned with roster size rows). */
function partialToInvoiceDollarForOrderLine(orderLineRow: Record<string, any>, orderLineIndex: number) {
  const item0id = order_items?.[0]?.id;
  if (item0id == null || !orderLineRow?.id) return 0;
  const lk = buildOrderLineLineKey(item0id, String(orderLineRow.id));
  const raw = partialQtyToInvoiceByLineKey.value[lk];
  const pq = typeof raw === 'number' ? raw : parseSignedQty(String(raw ?? '0'));
  const lineTotal = Number(standaloneOrderLineAmountAt(orderLineIndex)) || 0;
  const oq = Math.max(1, Number(orderLineRow.quantity) || 1);
  if (oq <= 0) return 0;
  return (lineTotal / oq) * pq;
}

const openAddOrderLineModal = () => {
  if (!order_items[0]) {
    showToast('No order item available.', 'error');
    return;
  }
  addOrderLineForm.name = '';
  addOrderLineForm.quantity = 1;
  addOrderLineForm.price = '';
  addOrderLineForm.is_fixed = true;
  addOrderLineForm.percentage = '';
  addOrderLineModal.value = true;
};

const closeAddOrderLineModal = () => {
  addOrderLineModal.value = false;
};

const appendOrderLineToFirstItem = (newLine: Record<string, any>) => {
  const item0 = order_items[0];
  if (!item0) return;
  if (item0.order_line == null) {
    item0.order_line = [newLine];
  } else if (Array.isArray(item0.order_line)) {
    item0.order_line.push(newLine);
  } else if (typeof item0.order_line === 'object' && (item0.order_line as any).type === 'add_order_item_line') {
    item0.order_line = [item0.order_line, newLine];
  } else {
    item0.order_line = [newLine];
  }
};

const saveAddOrderLine = () => {
  const item0 = order_items[0];
  if (!item0) return;
  const name = String(addOrderLineForm.name ?? '').trim();
  if (!name) {
    showToast('Please enter a name for the manual line.', 'error');
    return;
  }
  const qty = Math.max(1, parseInt(String(addOrderLineForm.quantity ?? 1), 10) || 1);
  if (!addOrderLineForm.is_fixed) {
    const existingLines = normalizeOrderLinesFromApi(item0.order_line);
    if (existingLines.some((l) => !orderLineUsesFixedPricing(l))) {
      showToast('Only one percentage line is allowed per order.', 'error');
      return;
    }
  }
  let newLine: Record<string, any>;
  if (addOrderLineForm.is_fixed) {
    let priceNum = parseFloat(normalizeOrderLineDecimal(addOrderLineForm.price ?? 0));
    if (Number.isNaN(priceNum)) {
      showToast('Please enter a valid unit price.', 'error');
      return;
    }
    const nextIdx = normalizeOrderLinesFromApi(item0.order_line).length;
    priceNum = clampFixedOrderLineUnitPriceToCap(item0, nextIdx, priceNum, qty);
    const rawAsk = parseFloat(normalizeOrderLineDecimal(addOrderLineForm.price ?? 0));
    if (Number.isFinite(rawAsk) && parseFloat(normalizeOrderLineDecimal(priceNum)) !== rawAsk) {
      showToast(
        `Credit cannot exceed product subtotal and the amount implied by “Qty to invoice” (and other lines). Unit price set to ${normalizeOrderLineDecimal(priceNum)}.`,
        'error',
      );
    }
    newLine = {
      type: 'add_order_item_line',
      id: crypto.randomUUID(),
      name,
      quantity: qty,
      is_fixed: true,
      price: priceNum,
      percentage: undefined,
    };
  } else {
    const pct = parseFloat(normalizeOrderLineDecimal(addOrderLineForm.percentage ?? 0));
    if (Number.isNaN(pct)) {
      showToast('Please enter a valid percentage.', 'error');
      return;
    }
    newLine = {
      type: 'add_order_item_line',
      id: crypto.randomUUID(),
      name,
      quantity: qty,
      is_fixed: false,
      price: 0,
      percentage: pct,
    };
  }
  appendOrderLineToFirstItem(newLine);
  mergePartialDraftWithNewLinesFromRemaining();
  calculateTotalPrice();
  if (
    !orderItemListTotalNonNegative(item0) ||
    !partialInvoiceDraftTotalNonNegative(item0)
  ) {
    const ol = item0.order_line;
    if (Array.isArray(ol) && ol.length > 0) {
      ol.pop();
      if (ol.length === 0) item0.order_line = null;
    } else if (ol && typeof ol === 'object' && (ol as any).type === 'add_order_item_line') {
      item0.order_line = null;
    }
    calculateTotalPrice();
    mergePartialDraftWithNewLinesFromRemaining();
    showToast(
      'This line would make the list total or invoice preview total negative. Reduce credit or percentage.',
      'error',
    );
    return;
  }
  addOrderLineModal.value = false;
};

const removeOrderLineAt = (index: number) => {
  const item0 = order_items[0];
  if (!item0?.order_line) return;
  const line = standaloneOrderLines.value[index];
  if (line?.id && item0.id != null) {
    const lk = buildOrderLineLineKey(item0.id, String(line.id));
    if (invoicedQtyForLineKey(lk) > 0) {
      showToast(
        'This line has invoiced quantities. Set Qty to invoice to 0 instead of deleting the line.',
        'error',
      );
      return;
    }
  }
  if (Array.isArray(item0.order_line)) {
    item0.order_line.splice(index, 1);
    if (item0.order_line.length === 0) item0.order_line = null;
  } else if (index === 0 && (item0.order_line as any).type === 'add_order_item_line') {
    item0.order_line = null;
  }
  calculateTotalPrice();
  mergePartialDraftWithNewLinesFromRemaining();
};

const setOrderLinePricingMode = (index: number, mode: 'fixed' | 'percent') => {
  const line = standaloneOrderLines.value[index];
  if (!line) return;
  if (mode === 'percent') {
    const otherPct = standaloneOrderLines.value.filter(
      (l, i) => i !== index && !orderLineUsesFixedPricing(l),
    );
    if (otherPct.length > 0) {
      showToast('Only one percentage line is allowed per order.', 'error');
      return;
    }
  }
  const prevFixed = line.is_fixed;
  const prevPrice = line.price;
  const prevPct = line.percentage;
  if (mode === 'fixed') {
    line.is_fixed = true;
    line.percentage = undefined;
    if (line.price == null || line.price === '') line.price = 0;
  } else {
    line.is_fixed = false;
    line.price = 0;
    if (line.percentage == null || line.percentage === '') line.percentage = 0;
  }
  calculateTotalPrice();
  const item0 = order_items[0];
  if (
    item0 &&
    (!orderItemListTotalNonNegative(item0) || !partialInvoiceDraftTotalNonNegative(item0))
  ) {
    line.is_fixed = prevFixed;
    line.price = prevPrice;
    line.percentage = prevPct;
    calculateTotalPrice();
    showToast(
      'That pricing mode would make the list or invoice preview total negative. Reverted.',
      'error',
    );
  }
};

const onOrderLineQuantityChange = (index: number) => {
  const line = standaloneOrderLines.value[index];
  if (!line) return;
  line.quantity = Math.max(1, parseInt(String(line.quantity ?? 1), 10) || 1);
  calculateTotalPrice();
};

function onStandaloneOrderLineQtyInput(e: Event, index: number) {
  const el = e.target as HTMLInputElement;
  const digits = sanitizeQuantityDigits(el.value);
  if (digits !== el.value) el.value = digits;
  const line = standaloneOrderLines.value[index];
  if (!line) return;
  const n = digits === '' ? 1 : Math.max(1, parseInt(digits, 10) || 1);
  line.quantity = n;
  el.value = String(n);
  const item0 = order_items[0];
  if (item0 && orderLineUsesFixedPricing(line)) {
    const raw = parseFloat(String(line.price ?? 0));
    const capped = clampFixedOrderLineUnitPriceToCap(item0, index, raw, n);
    if (capped !== raw) {
      line.price = capped;
      showToast(
        `Credit cannot exceed product subtotal and “Qty to invoice” limits. Unit price set to ${normalizeOrderLineDecimal(capped)}.`,
        'error',
      );
    }
  }
  calculateTotalPrice();
}

function onAddOrderLineQtyInput(e: Event) {
  const el = e.target as HTMLInputElement;
  const digits = sanitizeQuantityDigits(el.value);
  if (digits !== el.value) el.value = digits;
  const n = digits === '' ? 1 : Math.max(1, parseInt(digits, 10) || 1);
  addOrderLineForm.quantity = n;
  el.value = String(n);
}

function stashStandaloneOrderLinePrice(line: Record<string, any>) {
  line.__prevOrderLinePrice = line.price;
}

function stashStandaloneOrderLinePercentage(line: Record<string, any>) {
  line.__prevOrderLinePct = line.percentage;
}

const onStandaloneAddOrderLinePriceChange = async (e: Event, index: number) => {
  const el = e.target as HTMLInputElement;
  const line = standaloneOrderLines.value[index];
  if (!line || line.type !== 'add_order_item_line' || !orderLineUsesFixedPricing(line)) return;
  const num = formatMoney(el.value);
  const prev = formatMoney(String(line.__prevOrderLinePrice ?? line.price ?? ''));
  if (prev === num) {
    el.value = num;
    return;
  }
  if (!(await confirmUnitPriceChangeIfNeeded())) {
    const revert = line.__prevOrderLinePrice;
    line.price = typeof revert === 'number' ? revert : parseFloat(String(revert)) || 0;
    el.value = formatMoney(line.price);
    return;
  }
  const item0 = order_items[0];
  const parsed = parseFloat(num);
  const q = Math.max(1, Number(line.quantity) || 1);
  const clamped =
    item0 != null ? clampFixedOrderLineUnitPriceToCap(item0, index, parsed, q) : parsed;
  line.price = clamped;
  el.value = formatMoney(clamped);
  calculateTotalPrice();
  if (item0 && clamped !== parsed) {
    showToast(
      `Credit cannot exceed product subtotal and the amount implied by “Qty to invoice” (and other lines). Maximum unit price is ${normalizeOrderLineDecimal(clamped)}.`,
      'error',
    );
  }
};

const onStandaloneAddOrderLinePercentageChange = (e: Event, index: number) => {
  const el = e.target as HTMLInputElement;
  const line = standaloneOrderLines.value[index];
  if (!line || line.type !== 'add_order_item_line' || orderLineUsesFixedPricing(line)) return;
  const num = formatMoney(el.value);
  const prevRaw = line.__prevOrderLinePct;
  const prevStr =
    prevRaw != null && prevRaw !== '' ? formatMoney(String(prevRaw)) : formatMoney('0');
  if (prevStr === num) {
    el.value = num;
    return;
  }
  el.value = num;
  line.percentage = parseFloat(num);
  calculateTotalPrice();
  const item0 = order_items[0];
  if (
    item0 &&
    (!orderItemListTotalNonNegative(item0) || !partialInvoiceDraftTotalNonNegative(item0))
  ) {
    line.percentage = typeof prevRaw === 'number' ? prevRaw : parseFloat(String(prevRaw ?? 0)) || 0;
    el.value = formatMoney(line.percentage);
    calculateTotalPrice();
    showToast(
      'That percentage would make the list or invoice preview total negative. Reverted.',
      'error',
    );
  }
};

/**
 * Prefer `order_items.order_line` (DB) as JSON array. Normalize legacy single object → array.
 * Move embedded `factory_product.prices.add_order_item_line` / `add_order_item_lines[]` onto the item.
 */
const migrateLegacyOrderLineFromPrices = () => {
  (order_ref.value.items ?? []).forEach((oi: any) => {
    const rawCol = oi?.order_line;
    if (rawCol && !Array.isArray(rawCol) && rawCol.type === 'add_order_item_line') {
      oi.order_line = [rawCol];
    }
    const columnLines = normalizeOrderLinesFromApi(oi?.order_line);
    if (columnLines.length > 0) {
      oi.order_line = columnLines;
      (oi.factory_products ?? []).forEach((fp: any) => {
        const p = fp?.prices;
        if (!p) return;
        delete p.add_order_item_line;
        delete p.add_order_item_lines;
      });
      return;
    }
    for (const fp of oi.factory_products ?? []) {
      const p = fp?.prices;
      if (!p) continue;
      if (Array.isArray(p.add_order_item_lines) && p.add_order_item_lines.length) {
        const valid = p.add_order_item_lines.filter((x: any) => x?.type === 'add_order_item_line');
        delete p.add_order_item_lines;
        if (valid.length) {
          oi.order_line = valid.map((em: any) => ({
            type: 'add_order_item_line',
            name: String(em.name ?? em.line_name ?? em.title ?? '').trim() || 'Order line',
            quantity: Math.max(1, Number(em.quantity) || 1),
            is_fixed: orderLineUsesFixedPricing(em),
            price: orderLineUsesFixedPricing(em)
              ? parseFloat(normalizeOrderLineDecimal(em.price ?? 0))
              : 0,
            percentage: !orderLineUsesFixedPricing(em)
              ? parseFloat(normalizeOrderLineDecimal(em.percentage ?? 0))
              : undefined,
          }));
          delete p.add_order_item_line;
          break;
        }
      }
      const embedded = p.add_order_item_line;
      if (embedded?.type === 'add_order_item_line') {
        oi.order_line = [
          {
            type: 'add_order_item_line',
            name: String(embedded.name ?? embedded.line_name ?? embedded.title ?? '').trim() || 'Order line',
            quantity: embedded.quantity,
            price: parseFloat(normalizeOrderLineDecimal(embedded.price ?? 0)),
            is_fixed: orderLineUsesFixedPricing(embedded),
            percentage: !orderLineUsesFixedPricing(embedded)
              ? parseFloat(normalizeOrderLineDecimal(embedded.percentage ?? 0))
              : undefined,
          },
        ];
        delete p.add_order_item_line;
        break;
      }
    }
  });
};

let company_products = ref<Product[]>([]);
const getMerchantProducts = async (company_id: number, factory_id: number) => {
  http
    .get<ApiResponse2<Result<Product[]>>>(`merchant_products?company_id=${company_id}&factory_id=${factory_id}`)
    .then((successResponse: any) => {
      company_products.value = successResponse.data.result.products;
    });
};

// Treats "true"/1/true as truthy; everything else (false, "false", null, undefined, 0, "") as false
const normalizeBooleanish = (val: any): boolean => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val !== 0;
  if (typeof val === 'string') {
    const lower = val.trim().toLowerCase();
    return lower === 'true' || lower === '1' || lower === 'yes';
  }
  return Boolean(val);
};

// Find the product in merchant_products that matches factory_product.product_id
const getMatchedCatalogProduct = (factory_product: any): Product | null => {
  if (!factory_product?.product_id) return null;
  return company_products.value.find(p => p.product_id === factory_product.product_id) ?? null;
};

// Returns the catalog MSRP price for a factory product:
//   is_custom_price=true  → company_product.prices[0].price
//   is_custom_price=false → product.sku.skucurrency[0].pivot.msrp_price
const getCatalogPrice = (factory_product: any): number | null => {
  const product = getMatchedCatalogProduct(factory_product);
  if (!product) return null;
  const cp = product.company_product as any;
  if (!cp) return null;
  if (normalizeBooleanish(cp.is_custom_price)) {
    if (Array.isArray(cp.prices) && cp.prices.length > 0) {
      const p = parseFloat(cp.prices[0].price);
      return isNaN(p) ? null : p;
    }
    return null;
  } else {
    const sku = product.sku as any;
    const msrp = parseFloat(sku?.skucurrency?.[0]?.pivot?.msrp_price ?? sku?.skucurrency?.[0]?.msrp_price);
    return isNaN(msrp) ? null : msrp;
  }
};

// Returns the catalog MSRP price for a specific addon of a factory product:
//   is_custom_addons=true  → company_addons (matched by addon_id).active_addons.currencies[0].price
//   is_custom_addons=false → product.active_addons (matched by addon_id).price_info.msrp_price
const getCatalogAddonPrice = (factory_product: any, addon: any): number | null => {
  const product = getMatchedCatalogProduct(factory_product);
  if (!product) return null;
  const cp = product.company_product as any;
  if (!cp) return null;
  const addonId = addon?.addon_id ?? addon?.id;
  if (normalizeBooleanish(cp.is_custom_addons)) {
    const companyAddons = (product.company_addons as any[]) ?? [];
    const matched = addonId
      ? companyAddons.find(ca => ca.addon_id === addonId || ca.active_addons?.addon_id === addonId || ca.active_addons?.id === addonId)
      : companyAddons[0];
    const p = parseFloat(matched?.active_addons?.currencies?.[0]?.price);
    return isNaN(p) ? null : p;
  } else {
    const activeAddons = product.active_addons ?? [];
    const matched = addonId
      ? activeAddons.find(a => a.id === addonId || a.addon_id === addonId)
      : activeAddons[0];
    const msrp = parseFloat(matched?.price_info?.msrp_price as any);
    return isNaN(msrp) ? null : msrp;
  }
};
const mainStore = useMainStore();
const { getCustomerLockerRoom, getFactories, merchantUsers, getOrderDetail } = mainStore;
const { lockerRoom, factories } = storeToRefs(mainStore);

function syncFormStateFromOrder(): void {
  const item0: any = order_items?.[0];
  selectedOrderType.value = resolveSelectedOrderTypeFromOrder(item0, order_ref.value);
  selectedBillingCompany.value =
    pickOrderItemFirstScalar(
      item0?.billing_company_id,
      order_ref.value.billing_company_id || order_ref.value.company_id,
      props.order?.company_id,
    ) ?? props.order?.company_id;

  const activePriceInfo = JSON.parse(
    JSON.stringify(item0?.price_info ?? order_ref.value?.price_info ?? {}),
  );
  discount_shipping_info.value = {
    ...discount_shipping_info.value,
    ...activePriceInfo,
  };
  if (item0?.price_info?.is_fixed !== undefined) {
    item0.price_info.is_fixed = !!item0.price_info.is_fixed;
  }

  internal_note.value = String(
    pickOrderItemFirstScalar(item0?.internal_note, order_ref.value.internal_note, '') ?? '',
  );
  const memoSrc = pickOrderItemFirstScalar(
    item0?.invoice_memo,
    undefined,
    !isMissingOrEmptyScalar(lastMemoFromItemInvoicesOnItem(item0))
      ? lastMemoFromItemInvoicesOnItem(item0)
      : undefined,
  );
  invoice_memo.value = String(memoSrc ?? '');

  if (order_items?.length) {
    calculateTotalPrice();
  }
  syncPartialDraftFromRemaining();
}

/** Re-fetch order + item from API and rebind modal state (modal stays open). */
async function refreshPriceBreakdownFromApi(): Promise<void> {
  const orderId = order_ref.value?.id;
  const itemId = primaryOrderItem.value?.id ?? order_items?.[0]?.id;
  if (orderId == null) return;
  loading.value = true;
  try {
    const orderData = await getOrderDetail(Number(orderId), itemId != null ? Number(itemId) : undefined);
    if (!orderData) return;
    order_ref.value = JSON.parse(JSON.stringify(orderData)) as Record<string, any>;
    order_items = order_ref.value.items ?? [];
    normalizeOrderItemsInvoiceFields(order_items);
    migrateLegacyOrderLineFromPrices();
    applyOrderLineNameAliasesForFirstItem();
    ensureOrderLineIdsForInvoicing();
    ensureFirstOrderItemPriceInfo();
    syncFormStateFromOrder();
    // AB#20223 follow-up (BLOCKER 3) — this is a user-action-triggered full re-hydrate (after
    // save/sync); refresh the dirty-check baseline so it reflects what was just persisted.
    markFormSnapshotSynced();
  } finally {
    loading.value = false;
  }
}

/**
 * AB#20223 follow-up (BLOCKER 2) — cheap, order-independent signature of the invoice/ledger state
 * that drives every pending/failed pill, tooltip, and "Invoiced qty" cell: composite status per item
 * + its ledger rows (sorted by id) reduced to the fields that actually affect the UI. Used to gate
 * the passive on-open background refresh's downstream notify: most opens see zero change, so
 * comparing before/after keeps that the common case with zero extra requests.
 */
function buildInvoiceSyncSnapshot(items: Record<string, any>[] | null | undefined): string {
  const perItem = (items ?? []).map((item) => {
    const rows = getItemInvoicesListForOrderItem(item)
      .map((inv) => ({
        id: inv?.id ?? null,
        status: inv?.status ?? null,
        requires_manual_review: !!inv?.requires_manual_review,
        manual_review_reason: inv?.manual_review_reason ?? null,
        stripe_invoice_id: inv?.stripe_invoice_id ?? null,
        stripe_invoice_number: inv?.stripe_invoice_number ?? null,
        amount_due: inv?.amount_due ?? null,
        amount_paid: inv?.amount_paid ?? null,
      }))
      .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0));
    return {
      item_id: item?.id ?? null,
      invoice_composite_status: item?.invoice_composite_status ?? null,
      item_invoices: rows,
    };
  });
  return JSON.stringify(perItem);
}

/**
 * AB#20223 follow-up (BLOCKER 3) — snapshot of every field the OA can edit inline in this modal
 * (prices, memo, internal note, order type, billing company, partial-invoice draft qty). Compared
 * against itself after the passive background refetch resolves: if it changed, the OA edited
 * something during the fetch's latency, so the passive refetch must not overwrite it.
 */
function buildEditableFieldsSnapshot(): string {
  const pricesByLine: Record<string, unknown> = {};
  for (const item of order_items ?? []) {
    for (const fp of (item?.factory_products ?? []) as Record<string, any>[]) {
      for (const size of (fp?.prices?.sizes ?? []) as Record<string, any>[]) {
        pricesByLine[`${item?.id}:${fp?.id}:size:${size?.size}`] = size?.final_net_price ?? null;
      }
      const addonRows = [
        ...((fp?.prices?.addons ?? []) as Record<string, any>[]),
        ...((fp?.prices?.ungrouped_addons ?? []) as Record<string, any>[]),
        ...((fp?.prices?.logo_technologies ?? []) as Record<string, any>[]),
      ];
      addonRows.forEach((addon, idx) => {
        pricesByLine[`${item?.id}:${fp?.id}:addon:${idx}:${addon?.addon_name}`] = addon?.final_net_price ?? null;
      });
    }
  }
  return JSON.stringify({
    selectedOrderType: selectedOrderType.value,
    selectedBillingCompany: selectedBillingCompany.value,
    discount_shipping_info: discount_shipping_info.value,
    internal_note: internal_note.value,
    invoice_memo: invoice_memo.value,
    partialQtyToInvoiceByLineKey: partialQtyToInvoiceByLineKey.value,
    pricesByLine,
  });
}

/**
 * AB#20223 follow-up (BLOCKER 3) — baseline snapshot of the editable fields as of the last full
 * re-hydrate (initial mount, or any completed `syncFormStateFromOrder`). Compared against the
 * *current* values whenever fresh order data lands from anywhere other than a direct user action —
 * the passive background refresh (BLOCKER 3 window 1) AND the `props.order` deep watcher below
 * (BLOCKER 3 window 2, e.g. re-triggered by that same passive refresh's parent-side notify) — so an
 * in-progress edit is never silently overwritten.
 */
const lastSyncedFormSnapshot = ref<string>('');

/** Call after every full `syncFormStateFromOrder()` so the dirty-check baseline stays current. */
function markFormSnapshotSynced(): void {
  lastSyncedFormSnapshot.value = buildEditableFieldsSnapshot();
}

/** True when the OA has changed a tracked field since the last full re-hydrate. */
function isFormDirtySinceLastSync(): boolean {
  return lastSyncedFormSnapshot.value !== '' && buildEditableFieldsSnapshot() !== lastSyncedFormSnapshot.value;
}

/**
 * AB#20223 follow-up (BLOCKER 3) — patch ONLY invoice/ledger-derived fields on the live order items
 * in place (ledger rows, composite status + its manual-review fields, which feed the pending/failed
 * pills, tooltips, and "Invoiced qty" cells). Never touches price inputs, memo, internal note, order
 * type, billing company, or the partial-invoice draft — used when the OA has an in-progress edit the
 * passive background refresh must not stomp on.
 */
function patchLedgerStateOnly(freshItems: Record<string, any>[]): void {
  const liveItems = (order_ref.value?.items ?? []) as Record<string, any>[];
  for (const freshItem of freshItems) {
    const live = liveItems.find((it) => it?.id === freshItem?.id);
    if (!live) continue;
    if (Array.isArray(freshItem.item_invoices)) {
      live.item_invoices = [...freshItem.item_invoices];
    }
    if (Array.isArray(freshItem.itemInvoices)) {
      live.itemInvoices = [...freshItem.itemInvoices];
    }
    live.invoice_composite_status = freshItem.invoice_composite_status ?? live.invoice_composite_status;
    live.invoice_requires_manual_review =
      freshItem.invoice_requires_manual_review ?? live.invoice_requires_manual_review;
    live.invoice_manual_review_reason =
      freshItem.invoice_manual_review_reason ?? live.invoice_manual_review_reason;
  }
}

/**
 * AB#20223 follow-up — passive, one-shot background refresh fired on modal open (never polling).
 * Addresses BLOCKER 2 + BLOCKER 3 from the follow-up review:
 *  - BLOCKER 2: snapshots the invoice/ledger state before/after and only notifies the parent
 *    (dashboard list + right panel) when it actually changed — the common "nothing changed" open
 *    costs zero extra downstream requests.
 *  - BLOCKER 3: if the OA edited any form-bound field (price, memo, note, order type, billing
 *    company, partial-invoice draft) while this fetch was in flight, skips the full re-hydrate and
 *    patches only the ledger-derived fields instead, so their in-progress edit survives.
 * Uses a distinct passive-notify path (never `notifyParentOrderDataUpdated`) — see BLOCKER 1.
 */
async function refreshLedgerStateInBackgroundOnOpen(): Promise<void> {
  const orderId = order_ref.value?.id;
  const itemId = primaryOrderItem.value?.id ?? order_items?.[0]?.id;
  if (orderId === undefined || orderId === null) return;

  const preLedgerSnapshot = buildInvoiceSyncSnapshot(order_ref.value?.items);

  let orderData: Record<string, any> | null = null;
  try {
    orderData = await getOrderDetail(
      Number(orderId),
      itemId !== undefined && itemId !== null ? Number(itemId) : undefined,
    );
  } catch {
    return;
  }
  if (!orderData) return;

  const fresh = JSON.parse(JSON.stringify(orderData)) as Record<string, any>;
  const freshItems = (fresh.items ?? []) as Record<string, any>[];
  normalizeOrderItemsInvoiceFields(freshItems);

  const postLedgerSnapshot = buildInvoiceSyncSnapshot(freshItems);
  if (postLedgerSnapshot === preLedgerSnapshot) {
    // Common case (BLOCKER 2): nothing changed — no local state churn, no parent notify.
    return;
  }

  if (isFormDirtySinceLastSync()) {
    // BLOCKER 3 (window 1): the OA edited a form-bound field since the last full re-hydrate —
    // preserve it; only patch the ledger-derived fields.
    patchLedgerStateOnly(freshItems);
  } else {
    order_ref.value = fresh;
    order_items = order_ref.value.items ?? [];
    normalizeOrderItemsInvoiceFields(order_items);
    migrateLegacyOrderLineFromPrices();
    applyOrderLineNameAliasesForFirstItem();
    ensureOrderLineIdsForInvoicing();
    ensureFirstOrderItemPriceInfo();
    syncFormStateFromOrder();
    markFormSnapshotSynced();
  }

  notifyParentOrderDataRefreshedPassively();
}

/**
 * Remove a single invoice from the order (permission-gated: `remove-invoice`).
 * Deletes the clicked invoice's ledger rows + allocations on the backend so its
 * items become invoiceable again; does NOT credit/void the real Stripe invoice.
 * Identifiers mirror the modal's invoice dedup (`stripe_invoice_id ?? id`).
 */
async function onRemoveInvoice(inv: Record<string, any>): Promise<void> {
  const orderId = order_ref.value?.id;
  if (orderId == null) {
    showToast('Order id is missing; cannot remove invoice.', 'error');
    return;
  }

  const result = await Swal.fire({
    title: 'Clear invoice from order?',
    text: 'This will remove the invoice from the order but will not credit it. The items on this invoice will become invoiceable again so you can add them to a new invoice.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Clear invoice',
    cancelButtonText: 'Cancel',
  });
  if (!result.isConfirmed) return;

  loading.value = true;
  try {
    const response = await http.post(`order/${orderId}/remove-invoice`, {
      order_item_invoice_id: inv?.id ?? null,
      stripe_invoice_id: inv?.stripe_invoice_id ?? null,
      stripe_invoice_number: inv?.stripe_invoice_number ?? null,
    });
    showToast(response.data?.message ?? 'Invoice removed from order');
    await refreshPriceBreakdownFromApi();
    notifyParentOrderDataUpdated();
  } catch (error) {
    handleResponseException(error as AxiosError);
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  // Order payload is already loaded with `?order_item_id=` by the parent (`openOrderPriceModal` or
  // `priceListData` in DashboardActions) — render it instantly here, no blocking loader/blank state.

  priceBreakdownModal.value = true;
  hydrateLocalOrderFromProps();
  migrateLegacyOrderLineFromPrices();
  applyOrderLineNameAliasesForFirstItem();
  ensureOrderLineIdsForInvoicing();
  ensureFirstOrderItemPriceInfo();
  syncFormStateFromOrder();
  // AB#20223 follow-up (BLOCKER 3) — establish the dirty-check baseline right after the initial sync.
  markFormSnapshotSynced();
  getMerchantProducts(order_ref.value.company_id, firstOrderItemSnapshot.factory_id);
  getCustomerLockerRoom(order_ref.value.company_id, order_ref.value.customer_id);
  getFactories();
  billingMerchant.value = merchantUsers;

  // AB#20223 follow-up — the backend's self-healing sweep can resolve a pending/failed sync
  // out-of-band between the parent's pre-open fetch and this render. Fire a passive, one-shot
  // background refresh (no loading overlay, no polling/interval) so the modal self-heals instead
  // of staying stale until a manual refresh. See `refreshLedgerStateInBackgroundOnOpen` for the
  // change-gated notify (BLOCKER 2) and edit-preserving merge (BLOCKER 3).
  void refreshLedgerStateInBackgroundOnOpen();
});

const closeEvent = async () => {
  emit('close', false);
  priceBreakdownModal.value = false;
};

const closeShippingAndDiscountModal = () => {
  shippingAndDiscountModal.value = false;
};

const storage_url = import.meta.env.VITE_APP_FILE_URL;

/** Recompute a price row’s `final_net_price_with_quantity` from unit × garment qty. Does not run `calculateTotalPrice` (for batch sync). */
const applyItemPriceWithQuantity = (item: any, quantity: any) => {
  if (item == null) return;
  item.final_net_price = formatMoney(item.final_net_price);
  const q = Math.max(0, parseInt(String(quantity ?? 0), 10) || 0);
  const total = q * parseFloat(String(item.final_net_price));
  item.final_net_price_with_quantity = formatMoney(total);
};

const handlePriceUpdate = (item: any, quantity: any) => {
  applyItemPriceWithQuantity(item, quantity);
  calculateTotalPrice();
};

/**
 * Keep `prices.total_quantity` aligned with the roster and refresh add-on line totals so
 * they match the same multiplier as Stripe (roster count, not a stale `total_quantity`).
 */
function syncFactoryProductRosterTotalAndAddonDollars(factoryProduct: any) {
  const p = factoryProduct?.prices;
  if (!p || !Array.isArray(p.sizes) || p.sizes.length === 0) {
    return;
  }
  const t = factoryProductGarmentOrderTotalQty(factoryProduct);
  p.total_quantity = t;
  const run = (row: any) => {
    if (row == null) return;
    applyItemPriceWithQuantity(row, t);
  };
  (p.addons || []).forEach(run);
  (p.ungrouped_addons || []).forEach(run);
  const g = p.grouped_addons;
  if (g && typeof g === 'object') {
    for (const k of Object.keys(g as Record<string, any>)) {
      run((g as Record<string, any>)[k]);
    }
  }
  (p.logo_technologies || []).forEach(run);
}

const onFactorySizeNetPriceInput = async (e: Event, size: any) => {
  const input = e.target as HTMLInputElement;
  const number = formatMoney(input.value);
  const prevRaw = size.__prevFinalNet ?? size.final_net_price;
  const prev = formatMoney(prevRaw ?? '');
  if (prev === number) {
    input.value = number;
    return;
  }
  if (!(await confirmUnitPriceChangeIfNeeded())) {
    input.value = prev;
    size.final_net_price = prevRaw;
    return;
  }
  input.value = number;
  size.final_net_price = number;
  handlePriceUpdate(size, size.quantity);
};

async function onCatalogAddonNetPriceChange(e: Event, addon: any, factory_product: any) {
  const input = e.target as HTMLInputElement;
  const number = formatMoney(input.value);
  const prevRaw = addon.__prevFinalNet;
  const prev =
    prevRaw != null && prevRaw !== '' ? formatMoney(String(prevRaw)) : formatMoney('0');
  if (prev === number) return;
  if (!(await confirmUnitPriceChangeIfNeeded())) {
    input.value = prevRaw != null && prevRaw !== '' ? String(prevRaw) : '';
    addon.final_net_price = prevRaw;
    return;
  }
  addon.final_net_price = parseFloat(number) || 0;
  handlePriceUpdate(addon, factory_product.prices?.total_quantity);
}

const previewBlobUrls = new WeakMap<File, string>();

/** When a thumb URL 404s (or fails), hide `<img>` — do not use `v-img-fallback` `/img/placeholder.png`. */
const factorySkuThumbLoadFailed = reactive<Record<string, boolean>>({});
function factorySkuThumbKey(
  orderItemIdx: number | string,
  factoryProductIdx: number | string,
): string {
  return `${orderItemIdx}-${factoryProductIdx}`;
}

/** Preview URL for factory SKU thumb, or `null` when there is no image (UI shows empty space instead of a placeholder). */
const getFilePreview = (factory_product: any): string | null => {
  if (factory_product?.front_image_file instanceof File) {
    const file = factory_product.front_image_file;
    if (!file) return null;
    let cached = previewBlobUrls.get(file);
    if (!cached) {
      cached = URL.createObjectURL(file);
      previewBlobUrls.set(file, cached);
    }
    return cached;
  }
  const raw = factory_product?.front_image;
  if (raw == null || String(raw).trim() === '') {
    return null;
  }
  return `${storage_url}/${factory_product.front_image}`;
};

/** @deprecated — use formatMoney; kept for existing script refs. */
const normalizeDecimalInput = formatMoney;

const formatNumber = formatMoney;

/**
 * After factory product totals exist, refresh each row’s `total_price` (2 d.p.).
 * Percentage rows use the current products subtotal (add/remove SKUs changes this).
 */
const syncOrderLineTotalPrice = (orderItem: any) => {
  const raw = orderItem?.order_line;
  if (raw == null) return;
  let lines: any[];
  if (Array.isArray(raw)) {
    lines = raw.filter((x: any) => x?.type === 'add_order_item_line');
  } else if (typeof raw === 'object' && raw.type === 'add_order_item_line') {
    lines = [raw];
    orderItem.order_line = lines;
  } else {
    return;
  }
  for (const line of lines) {
    ensureOrderLineName(line);
    line.is_fixed = orderLineUsesFixedPricing(line);
    if (line.is_fixed && line.price != null && line.price !== '') {
      line.price = parseFloat(normalizeOrderLineDecimal(line.price));
    } else if (!line.is_fixed && line.percentage != null && line.percentage !== '') {
      line.percentage = parseFloat(normalizeOrderLineDecimal(line.percentage));
    }
    const amt = orderLineLineAmount(orderItem, line);
    line.total_price = parseFloat(normalizeOrderLineDecimal(amt));
  }
};

// const formatNumber = (value) => {
//   const num = parseFloat(value) || 0;

//   let str = num.toFixed(3); // always start with 3 decimals
//   const [intPart, decPart] = str.split('.');

//   // all decimals are zero → return integer
//   if (decPart === '000') {
//     return intPart;
//   }

//   // third decimal is zero → keep first two
//   if (decPart[2] === '0') {
//     return `${intPart}.${decPart.slice(0, 2)}`;
//   }

//   // all three decimals are meaningful → keep all
//   return `${intPart}.${decPart}`;
// };

const calculateTotalPrice = () => {
  syncOrderItemsAlias();
  let orderTotalPrice = 0;
  let orderTotalQuantity = 0;
  (order_ref.value?.items ?? []).forEach((orderItem: any) => {
    // 1) Refresh every factory row (add/remove product or roster change).
    recalculateFactoryProductTotalsForOrderItem(orderItem);
    // 2) Products + addons subtotal; percentage rows use this plus fixed manual lines (see orderLineLineAmount).
    const productsSubtotal = orderItemProductsSubtotalOnly(orderItem);
    // 3) order_line.total_price / is_fixed from current products (%, or fixed qty×price).
    syncOrderLineTotalPrice(orderItem);
    // 4) price_info.total_price = products + line (then shipping/discount recomputes finals).
    const orderLineAmt = orderLineContribution(orderItem).amount;
    const order_item_total_price = Math.max(
      0,
      parseFloat(normalizeOrderLineDecimal(productsSubtotal + orderLineAmt)) || 0,
    );

    if (!orderItem.price_info) {
      orderItem.price_info = {
        total_price: 0,
        total_quantity: 0,
        discount: 0,
        is_fixed: false,
        shipping: 0,
        fixed_value: 0,
        order_item_final_price: 0,
      };
    }

    orderItem.price_info.total_price = order_item_total_price;
    orderTotalPrice += order_item_total_price;
    const itemQtySum = sumOrderItemTotalQuantity(orderItem);
    orderItem.price_info.total_quantity = itemQtySum;
    orderTotalQuantity += itemQtySum;
    //order_item_final_price property is no more in use in future if we apply dicount on order item then it can be used. For now it is moved to order price info as order_final _price
    //orderItem.price_info.order_item_final_price = order_item_total_price;
  })
  if (!order_ref.value.price_info) {
    order_ref.value.price_info = {
      total_price: 0,
      total_final_price: 0,
      total_quantity: 0,
    };
  }

  const safeOrderTotal = Math.max(0, parseFloat(normalizeOrderLineDecimal(orderTotalPrice)) || 0);
  order_ref.value.price_info.total_price = safeOrderTotal;
  order_ref.value.price_info.total_final_price = safeOrderTotal;
  order_ref.value.price_info.total_quantity = orderTotalQuantity;
  calculateShippingAndDiscount();
};

watch(
  () => props.order,
  () => {
    // BLOCKER 3 (AB#20223 follow-up, window 2): a parent-side notify (including the passive
    // on-open background refresh's) can reassign this prop seconds after open. If the OA has an
    // in-progress edit since the last full re-hydrate, don't clobber it — patch ledger-derived
    // fields only. Genuine user-action refetches (save/sync) are unaffected: right after those,
    // the tracked fields match what was just persisted, so this still runs the full re-hydrate.
    if (isFormDirtySinceLastSync()) {
      const freshItems = JSON.parse(
        JSON.stringify((props.order as Record<string, any> | undefined)?.items ?? []),
      ) as Record<string, any>[];
      normalizeOrderItemsInvoiceFields(freshItems);
      patchLedgerStateOnly(freshItems);
      return;
    }
    hydrateLocalOrderFromProps();
    migrateLegacyOrderLineFromPrices();
    applyOrderLineNameAliasesForFirstItem();
    ensureOrderLineIdsForInvoicing();
    ensureFirstOrderItemPriceInfo();
    syncFormStateFromOrder();
    markFormSnapshotSynced();
  },
  { deep: true },
);

const calculateShippingAndDiscount = () => {
  syncOrderItemsAlias();
  const activeOrderItem = order_ref.value?.items?.[0];
  if (!activeOrderItem) {
    closeShippingAndDiscountModal();
    return;
  }

  if (!activeOrderItem.price_info) {
    activeOrderItem.price_info = {
      total_price: 0,
      total_quantity: 0,
      order_item_final_price: 0,
    };
  }

  const order_item_total_price = parseFloat(activeOrderItem.price_info?.total_price ?? 0);
  const productsSubtotal = orderItemProductsSubtotalOnly(activeOrderItem);
  const orderLineAmt = orderLineContribution(activeOrderItem).amount;

  // Normalize inputs coming from the UI.
  const isFixed = !!discount_shipping_info.value.is_fixed;
  discount_shipping_info.value.is_fixed = isFixed;
  discount_shipping_info.value.fixed_value = discount_shipping_info.value.fixed_value
    ? parseFloat(discount_shipping_info.value.fixed_value)
    : 0;
  discount_shipping_info.value.discount = discount_shipping_info.value.discount
    ? parseFloat(discount_shipping_info.value.discount)
    : 0;
  discount_shipping_info.value.shipping = discount_shipping_info.value.shipping
    ? parseFloat(discount_shipping_info.value.shipping)
    : 0;

  // Discount/fixed off applies only to factory products. `order_line` is added in full (never discounted). Shipping last.
  let afterDiscountProducts = productsSubtotal;
  if (isFixed) {
    const fv = discount_shipping_info.value.fixed_value ? discount_shipping_info.value.fixed_value : 0;
    afterDiscountProducts = Math.max(0, productsSubtotal - fv);
  } else {
    const discount = discount_shipping_info.value.discount ? discount_shipping_info.value.discount : 0;
    const discount_amount = (productsSubtotal * discount) / 100;
    afterDiscountProducts = Math.max(0, productsSubtotal - discount_amount);
  }

  const shippingVal = discount_shipping_info.value.shipping ? discount_shipping_info.value.shipping : 0;
  const itemFinalPrice = Math.max(
    0,
    parseFloat(normalizeOrderLineDecimal(afterDiscountProducts + orderLineAmt + shippingVal)) || 0,
  );

  // Round values so the payload matches the modal display (avoids float precision drift).
  const roundedFixedValue = parseFloat(formatNumber(discount_shipping_info.value.fixed_value));
  const roundedDiscount = parseFloat(formatNumber(discount_shipping_info.value.discount));
  const roundedShipping = parseFloat(formatNumber(discount_shipping_info.value.shipping));
  const roundedFinalPrice = parseFloat(formatNumber(itemFinalPrice));

  // Update order_items.price_info (these are saved with each order item).
  activeOrderItem.price_info.is_fixed = isFixed;
  activeOrderItem.price_info.fixed_value = roundedFixedValue;
  activeOrderItem.price_info.discount = roundedDiscount;
  activeOrderItem.price_info.shipping = roundedShipping;
  activeOrderItem.price_info.order_item_final_price = roundedFinalPrice;
  // Keep legacy keys in sync as well (some parts of the UI/backend expect them).
  // These are part of the save payload; keep them as numbers (avoid JSON "251.034" strings).
  activeOrderItem.price_info.order_final_price = roundedFinalPrice;
  activeOrderItem.price_info.total_final_price = roundedFinalPrice;

  // Keep the order-level price info in sync (UI + orders table payload).
  if (!order_ref.value.price_info) {
    order_ref.value.price_info = {
      total_price: 0,
      total_final_price: 0,
      total_quantity: 0,
    };
  }

  order_ref.value.price_info.total_price = order_item_total_price;
  const qtyWithOrderLine = sumOrderItemTotalQuantity(activeOrderItem);
  activeOrderItem.price_info.total_quantity = qtyWithOrderLine;
  order_ref.value.price_info.total_quantity = qtyWithOrderLine;
  order_ref.value.price_info.total_final_price = roundedFinalPrice;
  order_ref.value.price_info.is_fixed = isFixed;
  order_ref.value.price_info.fixed_value = roundedFixedValue;
  order_ref.value.price_info.discount = roundedDiscount;
  order_ref.value.price_info.shipping = roundedShipping;
  // Keep payload values numeric (avoid JSON string serialization).
  order_ref.value.price_info.order_final_price = roundedFinalPrice;

  closeShippingAndDiscountModal();
};
const uploadFactoryProductsFiles = async (order_items: any[]) => {
  const base_path = `comapny_${order_ref.value.company_id}/${order_ref.value.company_id}/order/${order_ref.value.customer_id}/${order_ref.value.id}`;
  const allPromises: Promise<any>[] = [];

  for (const [orderItemIndex, orderItem] of order_items.entries()) {
    const file_base_path = orderItem.factory_id ? `${base_path}/${orderItem.factory_id}` : `${base_path}/shareable`;

    for (const [factoryProductIndex, factoryProduct] of orderItem.factory_products.entries()) {
      const formData = new FormData();

      if (factoryProduct.front_image_file instanceof File) formData.append('front_image', factoryProduct.front_image_file);
      if (factoryProduct.back_image_file instanceof File) formData.append('back_image', factoryProduct.back_image_file);

      if (Array.isArray(factoryProduct.assets)) {
        factoryProduct.assets.forEach(asset => {
          if (asset instanceof File) formData.append('assets[]', asset);
        });
      }

      const hasFiles = formData.has('front_image') || formData.has('back_image') || formData.has('assets[]');
      if (!hasFiles) continue;

      formData.append('base_path', file_base_path);

      allPromises.push(
        http.post('upload/price/modal/files', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',}})
          .then(res => ({ orderItemIndex, factoryProductIndex, data: res.data.result }))

      );
    }
  }

  const factoryProductsFilesData = await Promise.allSettled(allPromises);
  const filesUploadfailedResponses = factoryProductsFilesData.filter((r): r is PromiseRejectedResult => r.status === "rejected");
  const filesUploadSuccessResponses = factoryProductsFilesData.filter((r): r is PromiseFulfilledResult<any> => r.status === "fulfilled");
    if(filesUploadfailedResponses.length > 0) {
      filesUploadfailedResponses.forEach(filesUploadfailedResponse => {
        handleResponseException((filesUploadfailedResponse.reason as AxiosError));
      })
    }
  filesUploadSuccessResponses.forEach((factoryProductsFilesDatum: any) => {
    const status = factoryProductsFilesDatum.status;
    if(status === "fulfilled") {
      const {
      value: {
        orderItemIndex,
        factoryProductIndex,
        data: {
          front_image,
          back_image,
          assets
        }
      }
    } = factoryProductsFilesDatum;
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].front_image = front_image
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].back_image = back_image
    order_items[orderItemIndex]['factory_products'][factoryProductIndex].assets = assets
    delete order_items[orderItemIndex]['factory_products'][factoryProductIndex].front_image_file;
    delete order_items[orderItemIndex]['factory_products'][factoryProductIndex].back_image_file;
    }
  });
};

const uploadFactoryProductsFiles1 = async (order_items: any[]) => {
  let base_path = `comapny_${order_ref.value.company_id}/${order_ref.value.company_id}/order/${order_ref.value.customer_id}/${order_ref.value.id}`;
  for (const order_item of order_items) {
    let file_path = '';
    if (order_item.factory_id) {
      file_path = `${base_path}/${order_item.factory_id}`;
    } else {
      file_path = `${base_path}/shareable`;
    }
    const factory_product_files = order_item.factory_products
      .map(async (factory_product: any, factory_product_index: number) => {
        const factory_product_form_data = new FormData();

        if (factory_product.front_image_file instanceof File) {
          factory_product_form_data.append('front_image', factory_product.front_image_file);
        }
        if (factory_product.back_image_file instanceof File) {
          factory_product_form_data.append('back_image', factory_product.back_image_file);
        }
        if (Array.isArray(factory_product.assets)) {
          factory_product.assets.forEach((asset, asset_index) => {
            if (asset instanceof File) {
              factory_product_form_data.append(`assets[${asset_index}]`, asset);
            }
          });
        }

        if (
          factory_product_form_data.has('front_image') ||
          factory_product_form_data.has('back_image') ||
          factory_product_form_data.has('assets[0]')
        ) {
          factory_product_form_data.append('base_path', base_path);

          const factory_product_response = await http.post(
            'upload/price/modal/files',
            factory_product_form_data,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );

          return {
            status: factory_product_response.status,
            factory_product_index,
            data: factory_product_response.data.result,
            order_item,
          };
        } else {
          return null;
        }
      })
      .filter(Boolean);

    const results = await Promise.allSettled(factory_product_files);

    results.forEach((result: any) => {
      if (result.status === 'fulfilled' && result.value) {
        const { factory_product_index, data } = result.value;

        order_item.factory_products[factory_product_index].front_image = data.front_image;
        order_item.factory_products[factory_product_index].back_image = data.back_image;
        order_item.factory_products[factory_product_index].assets = data.assets;

        delete order_item.factory_products[factory_product_index].front_image_file;
        delete order_item.factory_products[factory_product_index].back_image_file;
      }
    });
  }
};

/** Keep `order_items` alias aligned with reactive `order_ref.value.items` (modal edits live on the ref). */
function syncOrderItemsAlias(): void {
  order_items = order_ref.value?.items ?? [];
}

/** Keep first order item aligned with modal fields (source of truth for display & save). */
const syncFirstOrderItemFromForm = () => {
  syncOrderItemsAlias();
  const item: any = order_ref.value?.items?.[0];
  if (!item) return;
  const sel = selectedOrderType.value as OrderType | null;
  if (sel && typeof sel === 'object') {
    if (sel.label != null) item.order_type_detail = sel.label;
    if (sel.symbol != null) item.order_type_symbol = sel.symbol;
    if (sel.default != null) item.order_type_default = sel.default;
  }
  item.internal_note = internal_note.value;
  item.invoice_memo = invoice_memo.value;
  item.billing_company_id = selectedBillingCompany.value;
};

// Always reads net_price from product.active_addons[match].price_info.final_net_price,
// regardless of is_custom_addons.
const getCatalogAddonNetPrice = (factory_product: any, addon: any): number | null => {
  const product = getMatchedCatalogProduct(factory_product);
  if (!product) return null;
  const addonId = addon?.addon_id ?? addon?.id;
  const activeAddons = product.active_addons ?? [];
  const matched = addonId
    ? activeAddons.find(a => a.id === addonId || a.addon_id === addonId)
    : activeAddons[0];
  const val = parseFloat(matched?.price_info?.final_net_price as any);
  return isNaN(val) ? null : val;
};

const enrichAddonWithCurrencies = (factory_product: any, addon: any): any => {
  const catalogPrice = getCatalogAddonPrice(factory_product, addon);
  const catalogNetPrice = getCatalogAddonNetPrice(factory_product, addon);
  return {
    ...addon,
    currencies: [
      {
        price: catalogPrice !== null ? catalogPrice : (addon.final_net_price ?? 0),
        net_price: catalogNetPrice !== null ? catalogNetPrice : (addon.final_net_price ?? 0),
      },
    ],
  };
};

const enrichFactoryProductsForPayload = (factory_products: any[]): any[] => {
  return factory_products.map(fp => {
    const msrp_price = getCatalogPrice(fp);

    // Top-level addons (raw ActiveAddon objects from AddProductModal) already carry
    // currencies[].price but no net_price — inject it from addon.price_info.final_net_price.
    const addons = Array.isArray(fp.addons)
      ? fp.addons.map((a: any) => ({
          ...a,
          currencies: (a.currencies ?? []).map((c: any) => ({
            ...c,
            net_price: a.price_info?.final_net_price ?? 0,
          })),
        }))
      : fp.addons;

    const prices = fp.prices
      ? {
          ...fp.prices,
          addons: (fp.prices.addons ?? []).map((a: any) => enrichAddonWithCurrencies(fp, a)),
          ungrouped_addons: (fp.prices.ungrouped_addons ?? []).map((a: any) => enrichAddonWithCurrencies(fp, a)),
          grouped_addons: fp.prices.grouped_addons
            ? Object.fromEntries(
                Object.entries(fp.prices.grouped_addons).map(([k, v]) => [k, enrichAddonWithCurrencies(fp, v)])
              )
            : fp.prices.grouped_addons,
          logo_technologies: (fp.prices.logo_technologies ?? []).map((a: any) => enrichAddonWithCurrencies(fp, a)),
        }
      : fp.prices;

    return {
      ...fp,
      msrp_price: fp.shop_product_id ? (fp.msrp_price ?? 0) : (msrp_price ?? 0),
      addons,
      prices,
    };
  });
};

/** Persist price breakdown to API (`update/order/.../order_items/price`). */
async function persistOrderPricesToApi(options?: {
  showSuccessToast?: boolean;
  refreshAfter?: boolean;
  /** When false, parent list/pane are not refreshed (e.g. intermediate save before billing merchant change). */
  notifyParent?: boolean;
}): Promise<boolean> {
  const showSuccessToast = options?.showSuccessToast !== false;
  const refreshAfter = options?.refreshAfter !== false;
  const notifyParent = options?.notifyParent !== false;

  ensureFirstOrderItemPriceInfo();
  calculateTotalPrice();
  syncFirstOrderItemFromForm();

  syncOrderItemsAlias();
  const itemsForSave = order_ref.value?.items ?? [];
  const order_items_chunks = chunk(itemsForSave, 10);
  const allPromises: Promise<any>[] = [];
  for (const orderItemsChunk of order_items_chunks) {
    await uploadFactoryProductsFiles(orderItemsChunk);
    const orderItemsData = [];
    for (const orderItem of orderItemsChunk) {
      const typedOrderItem: any = orderItem;
      orderItemsData.push({
        id: typedOrderItem.id,
        factory_id: typedOrderItem.factory_id,
        factory_name: typedOrderItem.factory_name,
        price_info: typedOrderItem.price_info,
        factory_products: enrichFactoryProductsForPayload(typedOrderItem.factory_products ?? []),
        order_line: (() => {
          const lines = normalizeOrderLinesFromApi(typedOrderItem.order_line);
          return lines.length ? lines : null;
        })(),
        status: typedOrderItem.status,
        invoice_memo: typedOrderItem.invoice_memo,
        internal_note: typedOrderItem.internal_note,
        billing_company_id: typedOrderItem.billing_company_id,
        order_type_detail: typedOrderItem.order_type_detail,
        order_type_symbol: typedOrderItem.order_type_symbol,
        order_type_default: typedOrderItem.order_type_default,
      });
    }
    allPromises.push(
      http.post(`update/order/${order_ref.value.id}/order_items/price`, { order_items: orderItemsData }),
    );
  }

  const results = await Promise.allSettled(allPromises);
  const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  failed.forEach((failedItem) => {
    handleResponseException((failedItem.reason as AxiosError));
  });

  const fulfilled = results.find(
    (r): r is PromiseFulfilledResult<{ data?: { message?: string } }> => r.status === 'fulfilled',
  );
  if (!fulfilled) {
    return false;
  }

  if (showSuccessToast) {
    showToast(fulfilled.value?.data?.message ?? 'Saved successfully');
  }
  if (refreshAfter) {
    await refreshPriceBreakdownFromApi();
  }
  if (notifyParent) {
    notifyParentOrderDataUpdated();
  }
  return true;
}

type OrderPriceSaveOptions = {
  showSuccessToast?: boolean;
  refreshAfter?: boolean;
  notifyParent?: boolean;
  /** When false, caller manages loading (e.g. billing merchant change after save). */
  manageLoading?: boolean;
};

/** Footer Save / Create invoice — returns false when price save failed. */
const updateOrderPrices = async (
  createInvoice: boolean = false,
  saveOptions?: OrderPriceSaveOptions,
): Promise<boolean> => {
  if (createInvoice && !validateCreateInvoiceOrToast()) {
    return false;
  }
  const manageLoading = saveOptions?.manageLoading !== false;
  if (manageLoading) {
    loading.value = true;
  }
  try {
    if (createInvoice) {
      const saved = await persistOrderPricesToApi({
        showSuccessToast: false,
        refreshAfter: false,
        notifyParent: false,
      });
      if (!saved) return false;
      await handleStripeSync({ skipCreateInvoiceValidation: true });
      return true;
    }
    return await persistOrderPricesToApi({
      showSuccessToast: saveOptions?.showSuccessToast,
      refreshAfter: saveOptions?.refreshAfter,
      notifyParent: saveOptions?.notifyParent,
    });
  } catch (e) {
    handleResponseException(e as AxiosError);
    return false;
  } finally {
    if (manageLoading) {
      clearPriceModalLoaders();
    }
  }
};

const handleStripeSync = async (options?: { skipCreateInvoiceValidation?: boolean }) => {
  if (!options?.skipCreateInvoiceValidation && !validateCreateInvoiceOrToast()) {
    clearPriceModalLoaders();
    return;
  }
  ensureOrderLineIdsForInvoicing();
  const item0id = order_items?.[0]?.id;
  if (!item0id) {
    showToast('Order item id is missing; cannot sync invoice.', 'error');
    clearPriceModalLoaders();
    return;
  }

  const draftKeys = Object.keys(partialQtyToInvoiceByLineKey.value);
  if (draftKeys.length > 0 && !draftKeys.some((k) => Number(partialQtyToInvoiceByLineKey.value[k]) !== 0)) {
    showToast('At least one line must have a quantity not equal to zero.', 'error');
    clearPriceModalLoaders();
    return;
  }

  syncFirstOrderItemFromForm();

  const lineQty = buildPartialLineQuantitiesPayload();
  if (Object.keys(lineQty).length === 0) {
    const anyPriorInvoiced = Object.values(invoicedQtyByLineKey.value).some(v => Number(v) > 0);
    const hasPartialLedger = itemInvoicesForAllOrderItems().length > 0;
    const comp = (order_items?.[0] as Record<string, any> | undefined)?.invoice_composite_status;
    if (hasPartialLedger || anyPriorInvoiced || (comp && comp !== 'not_invoiced')) {
      showToast(
        'Nothing to invoice: set “Qty to invoice” above zero on at least one line, or this line item is already fully invoiced.',
        'error',
      );
      clearPriceModalLoaders();
      return;
    }
  }

  const payload: Record<string, unknown> = {
    order_item_id: item0id,
    invoice_memo: invoice_memo.value ?? '',
  };
  if (Object.keys(lineQty).length > 0) {
    payload.line_quantities = lineQty;
  }
  // Bug 20222: the backend now always generates/persists the idempotency key server-side;
  // no client-supplied key is required (or needed) anymore.

  stripeLoading.value = true;
  try {
    const response: any = await http.post(
      `customer-orders/${order_ref.value.id}/sync-to-stripe`,
      payload,
    );
    showToast(response.data?.message ?? 'Invoice created in Stripe');
    const resultPayload = response.data?.result ?? response.data?.data ?? {};
    const created = resultPayload?.order_item_invoice;
    const stripeInvoiceMeta = resultPayload?.invoice;
    mergeOrderItemInvoiceIntoPrimaryItem(
      created && typeof created === 'object' ? (created as Record<string, any>) : null,
      stripeInvoiceMeta && typeof stripeInvoiceMeta === 'object'
        ? (stripeInvoiceMeta as { invoiceId?: string; invoice_pdf?: string; hosted_invoice_url?: string })
        : undefined,
    );
    await refreshPriceBreakdownFromApi();
    notifyParentOrderDataUpdated();
  } catch (error) {
    // Bug 20222: sync-to-stripe saga — 400/409 errors now carry the ledger row in `errors.order_item_invoice`.
    // Re-fetch from the API (authoritative composite status + ledger) instead of guessing local state.
    const axiosErr = error as AxiosError<{
      errors?: {
        stripe_error?: string;
        order_item_invoice_id?: number | string;
        order_item_invoice?: Record<string, any>;
      };
    }>;
    const status = axiosErr.response?.status;
    const errorLedgerRow = axiosErr.response?.data?.errors?.order_item_invoice;

    if ((status === 400 || status === 409) && errorLedgerRow && typeof errorLedgerRow === 'object') {
      // Contain a refetch failure (e.g. API fully down) so the state-specific toast below still shows.
      await refreshPriceBreakdownFromApi().catch(() => undefined);
      notifyParentOrderDataUpdated();

      const rowStatus = String(errorLedgerRow.status ?? '').toLowerCase();
      const requiresReview = !!errorLedgerRow.requires_manual_review;

      if (status === 409 && requiresReview) {
        showToast(
          appendManualReviewReasonForToast(
            'The invoice may have been sent but could not be confirmed — it is flagged for manual review. Do NOT re-send.',
            errorLedgerRow,
          ),
          'error',
        );
      } else if (status === 409) {
        // Double-fire guard: a pending row already existed when this request landed.
        showToast(
          appendManualReviewReasonForToast('An invoice sync is already in progress for this item.', errorLedgerRow),
          'error',
        );
      } else if (rowStatus === 'failed') {
        showToast(
          appendManualReviewReasonForToast(
            escapeHtmlForToast(
              axiosErr.response?.data?.errors?.stripe_error || 'Stripe invoice creation failed. You can try again.',
            ),
            errorLedgerRow,
          ),
          'error',
        );
      } else {
        handleResponseException(axiosErr);
      }
    } else {
      handleResponseException(axiosErr);
    }
  } finally {
    clearPriceModalLoaders();
  }
};

function factoryProductHasInvoicedGarments(
  orderItemRef: Record<string, any> | undefined,
  factory_product: any,
  fpIndex: number,
): boolean {
  if (!orderItemRef?.id || !factory_product) return false;
  const sizes = factory_product?.prices?.sizes ?? [];
  for (let i = 0; i < sizes.length; i++) {
    if (invoicedQtyForFactorySizeRow(orderItemRef, factory_product, fpIndex, i) > 0) return true;
  }
  return false;
}

const removeFactoryProduct = (orderItemIndex: number, factoryProductIndex: number) => {
  const oi = order_items[orderItemIndex] as Record<string, any> | undefined;
  const fp = oi?.factory_products?.[factoryProductIndex];
  if (factoryProductHasInvoicedGarments(oi, fp, factoryProductIndex)) {
    showToast(
      'This product has invoiced quantities. Set Qty to invoice to 0 on a new invoice instead of removing the product.',
      'error',
    );
    return;
  }
  oi.factory_products.splice(factoryProductIndex, 1);
  calculateTotalPrice();
  mergePartialDraftWithNewLinesFromRemaining();
};

const copyRoster = (copiedRosterData: any) => {
  if(copiedRoster.value.isAnyRosterCopied) {
    showConfirm(
      'Overwrite existing copied roster?',
      'You already have a roster copied. Do you want to copy this product’s roster instead?',
    ).then((result) => {
      if (result) {
        setCopiedRoster(copiedRosterData.product_id, copiedRosterData.rosterData);
        showToast('Roster copied successfully', 'success');
      }
    });
  } else {
    setCopiedRoster(copiedRosterData.product_id, copiedRosterData.rosterData);
    showToast('Roster copied successfully', 'success');
  }
};

const setCopiedRoster = (factory_product_index: number, roster: any[]) => {
  copiedRoster.value.isAnyRosterCopied = true;
  copiedRoster.value.copiedFromProductIndex = factory_product_index;
  copiedRoster.value.rosterData = roster;
};
</script>

<style lang="scss">
.products-table {
  th,
  td {
    border: 1px solid #e5e7eb;
    padding: 5px;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;
  }

  th {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 10;
  }
}

.modal-body {
  padding-top: 0 !important;
}

/* Two-column layout: shared meta width (track 1) + full-width table block (track 2) for factory / order line / totals. */
.price-breakdown-meta-row {
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem 0.75rem;
  align-items: start;
  box-sizing: border-box;

  @media (min-width: 720px) {
    grid-template-columns: clamp(14.5rem, 28vw, 28rem) minmax(0, 1fr);
    column-gap: 1.5rem;
    row-gap: 0;
  }

  @media (min-width: 1024px) {
    column-gap: 2.5rem;
  }
}

.price-breakdown-meta-row__meta {
  min-width: 0;
  max-width: 100%;
}

.price-breakdown-meta-row__tables {
  min-width: 0;
  max-width: 100%;
}

/* Roster + invoice side-by-side: allow flex item to shrink on narrow modals */
.price-breakdown-roster-table-wrap {
  min-width: 0;
}

.products-table .price-breakdown-col-price,
.products-table th.price-breakdown-col-price {
  text-align: right;
  /* Tight horizontal padding so input + currency + tabular total don't merge on narrow modal widths. */
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  white-space: nowrap;
}

.products-table .price-breakdown-price-input {
  text-align: right;
}

/* Inline-flex wrappers around input + currency (size + order-line rows): switch to block-level flex
   so the input shrinks to its min-w bound inside narrow cells instead of overflowing into the Total
   column. Scoped to span.inline-flex so the bare currency span in addon rows (sibling of input) is
   not affected. */
.products-table .price-breakdown-col-price > span.inline-flex {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  max-width: 100%;
  gap: 0.25rem;
  white-space: nowrap;
}

/* Suffix slot (currency code or %) inside the wrapper: reserve a fixed min-width so the input's
   right edge stays aligned across rows whether the suffix is "%" (~8px) or "USD"/"EUR" (~24px).
   Left-aligned text within the reserved slot. */
.products-table .price-breakdown-col-price > span.inline-flex > span {
  display: inline-block;
  min-width: 1.75rem;
  text-align: left;
  flex: 0 0 auto;
}

/* Unwrapped (addon / ungrouped addon / grouped addon / logo technology) rows: the currency span
   is a direct child of the price td (no inline-flex wrapper). Same min-width reservation so the
   input's right edge matches the percentage / size / standalone-order-line rows above. */
.products-table .price-breakdown-col-price > span:not(.inline-flex) {
  display: inline-block;
  min-width: 1.75rem;
  text-align: left;
}

/* Total column: keep money number and currency code on a single baseline so the currency suffix
   doesn't visually merge into the next column's content. */
.products-table td.price-breakdown-col-total,
.products-table th.price-breakdown-col-total {
  white-space: nowrap;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

/* Align pricing + partial-billing tables; headers share baseline */
.factory-pricing-invoice-pair > div:first-child table thead tr th,
.factory-pricing-invoice-pair > div:last-child table thead tr th {
  vertical-align: middle;
}

.invoice-beside-summary-table {
  table-layout: fixed;
  width: 100%;

  thead th,
  tbody td,
  tfoot td {
    vertical-align: middle;
  }
}

.price-breakdown-order-totals {
  background-color: #fff;
}

/* Grand totals: do not re-set td padding. `.products-table` uses 5px; a previous 8px/16px rule here
   made the last row’s Total column look inset (extra space right of the currency) vs body rows. */

/* Memo on invoice + Internal note: shared metrics so both fields match */
.memo-internal-shared {
  box-sizing: border-box;
  font-size: 0.875rem;
  line-height: 1.25rem;
}
</style>
